/**
 * @fileoverview Types for Quickbooks integration
 *
 * Including:
 * - Types corresponding to rjsf schemas for action steps inputs and outputs
 * - Types for Quickbooks API responses
 * - Types for request data sent to Quickbooks API
 *
 * Roughly organized by entities:
 * - Customer
 * - Item (Inventory, Non-inventory, Service)
 * - Preferences (Custom Fields)
 * - Invoice (including Line items and Custom Fields)
 * - Account
 * - Vendor
 * - Purchase Order
 * - Payment
 *
 * Note: For request data, Quickbooks API doesn't accept null values, so all fields are optional but not nullable.
 */

import _ from 'lodash'
import * as yup from 'yup'

// AUTH ---------------------------------------------------------------------------------------------
export enum QuickbooksAuthScopes {
  Accounting = 'com.intuit.quickbooks.accounting',
  Payment = 'com.intuit.quickbooks.payment',
  CustomDimensions = 'app-foundations.custom-dimensions.read',
  ProjectManagement = 'project-management.project',
  CustomFieldDefinitions = 'app-foundations.custom-field-definitions',
  CustomFields = 'app-foundations.custom-field-definitions.read',
  /** Premium Sales Tax GraphQL (`indirectTaxCalculateSaleTransactionTax`). */
  IndirectTax = 'indirect-tax.tax-calculation.quickbooks',
  /**
   * Workforce payroll benefits, deductions, and pensions
   * (`payrollCreateBenefitPolicy`, `payrollCreatePensionPolicy`,
   * `payrollEmployerInfo.benefits` / `.deductions` / `.pensions`).
   */
  PayrollBenefits = 'qb.payroll.benefits',
  /** Workforce company / employer information (`payrollEmployerInfo.businessInfo`). */
  CompanyRead = 'qb.company.read',
  /**
   * Workforce employer compensations / pay types
   * (`payrollEmployerInfo.employerCompensations`).
   */
  CompensationRead = 'payroll.compensation.read',
}

export const QuickbooksAuthScopesToHumanReadableMap: { [key: string]: string } = {
  [QuickbooksAuthScopes.Accounting]: 'Accounting',
  [QuickbooksAuthScopes.Payment]: 'Payment',
  [QuickbooksAuthScopes.CustomDimensions]: 'Custom Dimensions',
  [QuickbooksAuthScopes.ProjectManagement]: 'Project Management',
  [QuickbooksAuthScopes.CustomFieldDefinitions]: 'Custom Field Definitions',
  [QuickbooksAuthScopes.CustomFields]: 'Custom Fields',
  [QuickbooksAuthScopes.IndirectTax]: 'Sales Tax Calculation',
  [QuickbooksAuthScopes.PayrollBenefits]: 'Payroll Benefits',
  [QuickbooksAuthScopes.CompanyRead]: 'Company Information',
  [QuickbooksAuthScopes.CompensationRead]: 'Payroll Compensation',
}

/** Intuit finance v3 ReferenceType when the entity id (`value`) is required. */
export const quickbooksReferenceTypeValueRequiredSchema = yup.object({
  value: yup.string().required(),
  name: yup.string().optional(),
})
export type QuickbooksReferenceTypeValueRequired = yup.InferType<
  typeof quickbooksReferenceTypeValueRequiredSchema
>

/** ModificationMetaData subset returned on IntuitEntity (e.g. Budget, Invoice). */
export const quickbooksIntuitEntityMetaDataSchema = yup.object({
  CreateTime: yup.string().optional(),
  LastUpdatedTime: yup.string().optional(),
})
export type QuickbooksIntuitEntityMetaData = yup.InferType<
  typeof quickbooksIntuitEntityMetaDataSchema
>

// CUSTOMER -----------------------------------------------------------------------------------------

/** Action step input schema for Quickbooks Create Customer */
export const quickbooksCreateCustomerActionStepInputSchema = yup.object({
  DisplayName: yup.string().notRequired(),
  CompanyName: yup.string().notRequired(),
  Title: yup.string().notRequired(),
  FirstName: yup.string().notRequired(),
  MiddleName: yup.string().notRequired(),
  LastName: yup.string().notRequired(),
  Suffix: yup.string().notRequired(),
  EmailAddress: yup.string().notRequired(),
  PhoneNumber: yup.string().notRequired(),
  Mobile: yup.string().notRequired(),
  AlternatePhone: yup.string().notRequired(),
  Fax: yup.string().notRequired(),
  WebAddress: yup.string().notRequired(),
  PrintOnCheckName: yup.string().notRequired(),
  Notes: yup.string().notRequired(),
  Taxable: yup.boolean().notRequired(),
  DefaultTaxCodeRef: yup
    .object({
      value: yup.string().notRequired(),
    })
    .notRequired(),
  PreferredDeliveryMethod: yup.string().notRequired(),
  CurrencyRef: yup
    .object({
      value: yup.string().notRequired(),
      name: yup.string().notRequired(),
    })
    .notRequired(),
  SalesTermRef: yup
    .object({
      value: yup.string().notRequired(),
    })
    .notRequired(),
  PaymentMethodRef: yup
    .object({
      value: yup.string().notRequired(),
    })
    .notRequired(),
  ResaleNum: yup.string().notRequired(),
  PrimaryTaxIdentifier: yup.string().notRequired(),
  IsProject: yup.boolean().notRequired(),
  ARAccountRef: yup
    .object({
      value: yup.string().notRequired(),
    })
    .notRequired(),
  ParentCustomerQuickbooksId: yup.string().notRequired(),
  BillWithParent: yup.boolean().notRequired(),
  BillingAddress: yup
    .object({
      Line1: yup.string().notRequired(),
      Line2: yup.string().notRequired(),
      City: yup.string().notRequired(),
      CountrySubDivisionCode: yup.string().notRequired(),
      Country: yup.string().notRequired(),
      PostalCode: yup.string().notRequired(),
    })
    .notRequired(),
  ShippingAddress: yup
    .object({
      SameAsBilling: yup.boolean().notRequired(),
      Line1: yup.string().notRequired(),
      Line2: yup.string().notRequired(),
      City: yup.string().notRequired(),
      CountrySubDivisionCode: yup.string().notRequired(),
      Country: yup.string().notRequired(),
      PostalCode: yup.string().notRequired(),
    })
    .notRequired(),
})
export type QuickbooksCreateCustomerActionStepInput = yup.InferType<
  typeof quickbooksCreateCustomerActionStepInputSchema
>

/** Action step input schema for Quickbooks Update Customer */
export const quickbooksUpdateCustomerActionStepInputSchema = yup.object({
  Id: yup.string().required(),
  SyncToken: yup.string().required(),
  DisplayName: yup.string().notRequired(),
  CompanyName: yup.string().notRequired(),
  Title: yup.string().notRequired(),
  FirstName: yup.string().notRequired(),
  MiddleName: yup.string().notRequired(),
  LastName: yup.string().notRequired(),
  Suffix: yup.string().notRequired(),
  EmailAddress: yup.string().notRequired(),
  PhoneNumber: yup.string().notRequired(),
  Mobile: yup.string().notRequired(),
  AlternatePhone: yup.string().notRequired(),
  Fax: yup.string().notRequired(),
  WebAddress: yup.string().notRequired(),
  PrintOnCheckName: yup.string().notRequired(),
  Notes: yup.string().notRequired(),
  Taxable: yup.boolean().notRequired(),
  DefaultTaxCodeRef: yup
    .object({
      value: yup.string().notRequired(),
    })
    .notRequired(),
  PreferredDeliveryMethod: yup.string().notRequired(),
  CurrencyRef: yup
    .object({
      value: yup.string().notRequired(),
      name: yup.string().notRequired(),
    })
    .notRequired(),
  SalesTermRef: yup
    .object({
      value: yup.string().notRequired(),
    })
    .notRequired(),
  PaymentMethodRef: yup
    .object({
      value: yup.string().notRequired(),
    })
    .notRequired(),
  ResaleNum: yup.string().notRequired(),
  PrimaryTaxIdentifier: yup.string().notRequired(),
  IsProject: yup.boolean().notRequired(),
  ARAccountRef: yup
    .object({
      value: yup.string().notRequired(),
    })
    .notRequired(),
  ParentCustomerQuickbooksId: yup.string().notRequired(),
  BillWithParent: yup.boolean().notRequired(),
  BillingAddress: yup
    .object({
      Line1: yup.string().notRequired(),
      Line2: yup.string().notRequired(),
      City: yup.string().notRequired(),
      CountrySubDivisionCode: yup.string().notRequired(),
      Country: yup.string().notRequired(),
      PostalCode: yup.string().notRequired(),
    })
    .notRequired(),
  ShippingAddress: yup
    .object({
      SameAsBilling: yup.boolean().notRequired(),
      Line1: yup.string().notRequired(),
      Line2: yup.string().notRequired(),
      City: yup.string().notRequired(),
      CountrySubDivisionCode: yup.string().notRequired(),
      Country: yup.string().notRequired(),
      PostalCode: yup.string().notRequired(),
    })
    .notRequired(),
  sparse: yup.boolean().notRequired(),
})
export type QuickbooksUpdateCustomerActionStepInput = yup.InferType<
  typeof quickbooksUpdateCustomerActionStepInputSchema
>

/** Quickbooks Customer Schema (incomplete, only including core fields) */
export const quickbooksCustomerSchema = yup.object({
  Id: yup.string().required(),
  DisplayName: yup.string().required(),
  SyncToken: yup.string().optional().nullable(),
  FullyQualifiedName: yup.string().optional().nullable(),
  CompanyName: yup.string().optional().nullable(),
  SalesTermRef: yup
    .object({
      value: yup.string().optional().nullable(),
      name: yup.string().optional().nullable(),
    })
    .optional()
    .nullable(),
})

/** Quickbooks Query Customer Response Schema */
export const quickbooksQueryCustomerResponseSchema = yup.object({
  QueryResponse: yup
    .object({
      Customer: yup.array().of(quickbooksCustomerSchema).optional(),
    })
    .required(),
})
export type QuickbooksQueryCustomerResponse = yup.InferType<
  typeof quickbooksQueryCustomerResponseSchema
>

/** Quickbooks Customer Request Base Schema (without validation test) */
const quickbooksCustomerRequestBaseSchema = yup.object({
  CompanyName: yup.string().optional(),
  DisplayName: yup.string().optional(),
  Title: yup.string().optional(),
  GivenName: yup.string().optional(),
  MiddleName: yup.string().optional(),
  FamilyName: yup.string().optional(),
  Suffix: yup.string().optional(),
  PrintOnCheckName: yup.string().optional(),
  PrimaryEmailAddr: yup
    .object({
      Address: yup.string().email().optional(),
    })
    .optional(),
  PrimaryPhone: yup
    .object({
      FreeFormNumber: yup.string().optional(),
    })
    .optional(),
  Mobile: yup
    .object({
      FreeFormNumber: yup.string().optional(),
    })
    .optional(),
  AlternatePhone: yup
    .object({
      FreeFormNumber: yup.string().optional(),
    })
    .optional(),
  Fax: yup
    .object({
      FreeFormNumber: yup.string().optional(),
    })
    .optional(),
  WebAddr: yup
    .object({
      URI: yup.string().optional(),
    })
    .optional(),
  Notes: yup.string().optional(),
  Taxable: yup.boolean().optional(),
  DefaultTaxCodeRef: yup
    .object({
      value: yup.string().optional(),
    })
    .optional(),
  PreferredDeliveryMethod: yup.string().optional(),
  CurrencyRef: yup
    .object({
      value: yup.string().optional(),
      name: yup.string().optional(),
    })
    .optional(),
  SalesTermRef: yup
    .object({
      value: yup.string().optional(),
    })
    .optional(),
  PaymentMethodRef: yup
    .object({
      value: yup.string().optional(),
    })
    .optional(),
  ResaleNum: yup.string().optional(),
  PrimaryTaxIdentifier: yup.string().optional(),
  IsProject: yup.boolean().optional(),
  ARAccountRef: yup
    .object({
      value: yup.string().optional(),
    })
    .optional(),
  ParentRef: yup
    .object({
      value: yup.string().optional(),
    })
    .optional(),
  Job: yup.boolean().optional(),
  BillWithParent: yup.boolean().optional(),
  BillAddr: yup
    .object({
      Line1: yup.string().optional(),
      Line2: yup.string().optional(),
      City: yup.string().optional(),
      CountrySubDivisionCode: yup.string().optional(),
      Country: yup.string().optional(),
      PostalCode: yup.string().optional(),
    })
    .optional(),
  ShipAddr: yup
    .object({
      Line1: yup.string().optional(),
      Line2: yup.string().optional(),
      City: yup.string().optional(),
      CountrySubDivisionCode: yup.string().optional(),
      Country: yup.string().optional(),
      PostalCode: yup.string().optional(),
    })
    .optional(),
})

/** Quickbooks Create Customer Request Schema */
export const quickbooksCreateCustomerRequestSchema = quickbooksCustomerRequestBaseSchema.test(
  'at-least-one-name',
  'At least one of the name fields must be specified when creating a customer',
  (value) =>
    value.DisplayName !== undefined ||
    value.Title !== undefined ||
    value.GivenName !== undefined ||
    value.MiddleName !== undefined ||
    value.FamilyName !== undefined ||
    value.Suffix !== undefined,
)
export type QuickbooksCreateCustomerRequest = yup.InferType<
  typeof quickbooksCreateCustomerRequestSchema
>

/** Quickbooks Create Customer Response Schema */
export const quickbooksCreateCustomerResponseSchema = yup.object({
  Customer: quickbooksCustomerSchema.required(),
})
export type QuickbooksCreateCustomerResponse = yup.InferType<
  typeof quickbooksCreateCustomerResponseSchema
>

/** Quickbooks Get Customer Response Schema */
export const quickbooksGetCustomerResponseSchema = yup.object({
  Customer: yup
    .object({
      Id: yup.string().required(),
      SyncToken: yup.string().optional(),
      domain: yup.string().optional(),
      GivenName: yup.string().optional().nullable(),
      MiddleName: yup.string().optional().nullable(),
      DisplayName: yup.string().required(),
      Title: yup.string().optional().nullable(),
      Suffix: yup.string().optional().nullable(),
      BillWithParent: yup.boolean().optional(),
      SalesTermRef: yup
        .object({
          value: yup.string().optional().nullable(),
          name: yup.string().optional().nullable(),
        })
        .optional()
        .nullable(),
      PaymentMethodRef: yup
        .object({
          value: yup.string().optional().nullable(),
          name: yup.string().optional().nullable(),
        })
        .optional()
        .nullable(),
      DefaultTaxCodeRef: yup
        .object({
          value: yup.string().optional().nullable(),
        })
        .optional()
        .nullable(),
      CurrencyRef: yup
        .object({
          value: yup.string().optional().nullable(),
          name: yup.string().optional().nullable(),
        })
        .optional()
        .nullable(),
      ARAccountRef: yup
        .object({
          value: yup.string().optional().nullable(),
          name: yup.string().optional().nullable(),
        })
        .optional()
        .nullable(),
      FullyQualifiedName: yup.string().optional().nullable(),
      CompanyName: yup.string().optional().nullable(),
      FamilyName: yup.string().optional().nullable(),
      sparse: yup.boolean().optional(),
      PrimaryEmailAddr: yup
        .object({
          Address: yup.string().email().optional().nullable(),
        })
        .optional()
        .nullable(),
      PrimaryPhone: yup
        .object({
          FreeFormNumber: yup.string().optional().nullable(),
        })
        .optional()
        .nullable(),
      Mobile: yup
        .object({
          FreeFormNumber: yup.string().optional().nullable(),
        })
        .optional()
        .nullable(),
      AlternatePhone: yup
        .object({
          FreeFormNumber: yup.string().optional().nullable(),
        })
        .optional()
        .nullable(),
      Fax: yup
        .object({
          FreeFormNumber: yup.string().optional().nullable(),
        })
        .optional()
        .nullable(),
      WebAddr: yup
        .object({
          URI: yup.string().optional().nullable(),
        })
        .optional()
        .nullable(),
      PrintOnCheckName: yup.string().optional().nullable(),
      Notes: yup.string().optional().nullable(),
      ResaleNum: yup.string().optional().nullable(),
      PrimaryTaxIdentifier: yup.string().optional().nullable(),
      IsProject: yup.boolean().optional(),
      Active: yup.boolean().optional(),
      Job: yup.boolean().optional(),
      BalanceWithJobs: yup.number().optional(),
      BillAddr: yup
        .object({
          City: yup.string().optional().nullable(),
          Line1: yup.string().optional().nullable(),
          Line2: yup.string().optional().nullable(),
          PostalCode: yup.string().optional().nullable(),
          Lat: yup.string().optional().nullable(),
          Long: yup.string().optional().nullable(),
          CountrySubDivisionCode: yup.string().optional().nullable(),
          Country: yup.string().optional().nullable(),
          Id: yup.string().optional().nullable(),
        })
        .optional()
        .nullable(),
      ShipAddr: yup
        .object({
          City: yup.string().optional().nullable(),
          Line1: yup.string().optional().nullable(),
          Line2: yup.string().optional().nullable(),
          PostalCode: yup.string().optional().nullable(),
          Lat: yup.string().optional().nullable(),
          Long: yup.string().optional().nullable(),
          CountrySubDivisionCode: yup.string().optional().nullable(),
          Country: yup.string().optional().nullable(),
          Id: yup.string().optional().nullable(),
        })
        .optional()
        .nullable(),
      Balance: yup.number().optional(),
      MetaData: yup
        .object({
          CreateTime: yup.string().optional().nullable(),
          LastUpdatedTime: yup.string().optional().nullable(),
        })
        .optional()
        .nullable(),
    })
    .required(),
  time: yup.string().optional().nullable(),
})
export type QuickbooksGetCustomerResponse = yup.InferType<
  typeof quickbooksGetCustomerResponseSchema
>

/** Quickbooks Update Customer Request Schema */
export const quickbooksUpdateCustomerRequestSchema = yup
  .object({
    Id: yup.string().required(),
    SyncToken: yup.string().required(),
    sparse: yup.boolean().optional().nullable(),
  })
  .concat(quickbooksCustomerRequestBaseSchema)
export type QuickbooksUpdateCustomerRequest = yup.InferType<
  typeof quickbooksUpdateCustomerRequestSchema
>

/** Quickbooks Update Customer Response Schema (same as Get Customer Response) */
export const quickbooksUpdateCustomerResponseSchema = quickbooksGetCustomerResponseSchema
export type QuickbooksUpdateCustomerResponse = QuickbooksGetCustomerResponse

// ITEM (used in invoice create/update) ------------------------------------------------------------------------------------------

/** Action step input schema for item metadata as a part of Quickbooks invoice create/update */
const quickbooksGetOrCreateItemMetadataActionStepInputSchema = yup
  .object({
    QueryMatchField: yup.string().oneOf(['Name', 'Sku']).notRequired(),
    Name: yup.string().notRequired(),
    Sku: yup.string().notRequired(),
    FullyQualifiedName: yup.string().notRequired(),
    Description: yup.string().notRequired(),
    Type: yup.string().oneOf(['Service', 'Inventory', 'NonInventory', 'Category']).notRequired(),
    QtyOnHand: yup.number().notRequired(),
    TrackQtyOnHand: yup.boolean().notRequired(),
    UnitPrice: yup.number().notRequired(),
    PurchaseCost: yup.number().notRequired(),
    ExpenseAccountId: yup.string().notRequired(),
    IncomeAccountId: yup.string().notRequired(),
    AssetAccountId: yup.string().notRequired(),
    InventoryStartDate: yup.string().notRequired(),
  })
  .test(
    'one-of-query-match-fields',
    'If QueryMatchField is defined, the selected field must be defined',
    (value) => {
      if (value?.QueryMatchField === 'Name') {
        return !_.isNil(value.Name)
      } else if (value?.QueryMatchField === 'Sku') {
        return !_.isNil(value.Sku)
      } else {
        return true
      }
    },
  )
export type QuickbooksGetOrCreateItemMetadataActionStepInput = yup.InferType<
  typeof quickbooksGetOrCreateItemMetadataActionStepInputSchema
>

/** Quickbooks Item Schema (incomplete, only including core fields) */
export const quickbooksItemSchema = yup.object({
  Id: yup.string().required(),
  Name: yup.string().required(),
  FullyQualifiedName: yup.string().optional().nullable(),
  Sku: yup.string().optional().nullable(),
  SyncToken: yup.string().optional().nullable(),
  Type: yup.string().optional().nullable(),
})

/** Quickbooks Query Item Response Schema */
export const quickbooksQueryItemResponseSchema = yup.object({
  QueryResponse: yup
    .object({
      Item: yup.array().of(quickbooksItemSchema).optional(),
    })
    .required(),
})
export type QuickbooksQueryItemResponse = yup.InferType<typeof quickbooksQueryItemResponseSchema>

/** Quickbooks Create Item Request Schema - used in invoice creation/update */
export const quickbooksCreateItemRequestSchema = yup.object({
  Name: yup.string().required(),
  Sku: yup.string().optional(),
  FullyQualifiedName: yup.string().optional(),
  Description: yup.string().optional(),
  Type: yup.string().oneOf(['Service', 'Inventory', 'NonInventory', 'Category']).required(),
  QtyOnHand: yup.number().when('Type', {
    is: 'Inventory',
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.optional(),
  }),
  TrackQtyOnHand: yup.boolean().when('Type', {
    is: 'Inventory',
    then: (schema) => schema.oneOf([true]),
    otherwise: (schema) => schema.optional(),
  }), // must be true if type is Inventory
  UnitPrice: yup.number().optional(),
  PurchaseCost: yup.number().optional(),
  IncomeAccountRef: yup
    .object({
      value: yup.string().required(),
    })
    .when('Type', {
      is: ['Inventory', 'Service'],
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.optional(),
    })
    .strict(), // Strict mode prevents casting undefined to empty object
  AssetAccountRef: yup
    .object({
      value: yup.string().required(),
    })
    .when('Type', {
      is: 'Inventory',
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.optional(),
    })
    .strict(), // Strict mode prevents casting undefined to empty object
  ExpenseAccountRef: yup
    .object({
      value: yup.string().required(),
    })
    .required(),
  InvStartDate: yup.date().when('Type', {
    is: 'Inventory',
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.optional(),
  }),
})
export type QuickbooksCreateItemRequest = yup.InferType<typeof quickbooksCreateItemRequestSchema>

/** Quickbooks Create Item Response Schema */
export const quickbooksCreateItemResponseSchema = yup.object({
  Item: quickbooksItemSchema.required(),
})
export type QuickbooksCreateItemResponse = yup.InferType<typeof quickbooksCreateItemResponseSchema>

/**
 * QuickBooks Item Types
 * - Inventory: Tracks quantity on hand, requires asset account
 * - NonInventory: Products/materials not tracked in inventory
 * - Service: Services sold or purchased
 * - Category: Used for grouping items in hierarchies
 */
export enum QuickbooksItemType {
  Inventory = 'Inventory',
  NonInventory = 'NonInventory',
  Service = 'Service',
  Category = 'Category',
}

/**
 * Action step input schema for Quickbooks Create/Update Item
 *
 * Required fields by type:
 * - All types: Name, Type, ExpenseAccountRef
 * - Inventory and Service: IncomeAccountRef
 * - Inventory only: TrackQtyOnHand=true, AssetAccountRef, QtyOnHand, InvStartDate
 *
 * Optional fields:
 * - Sku, UnitPrice, PurchaseCost, Description, PurchaseDesc, ParentRef, TaxCodeRef
 */
export const quickbooksCreateItemActionStepInputSchema = yup.object({
  // Core fields (required for all types)
  Name: yup.string().required().max(100, 'Name cannot exceed 100 characters'),
  Type: yup
    .string()
    .oneOf([
      QuickbooksItemType.Inventory,
      QuickbooksItemType.NonInventory,
      QuickbooksItemType.Service,
      QuickbooksItemType.Category,
    ])
    .required(),

  // Account references - conditionally required based on Type
  IncomeAccountId: yup.string().when('Type', {
    is: (type: string) =>
      type === QuickbooksItemType.Inventory || type === QuickbooksItemType.Service,
    then: (schema) =>
      schema.required('IncomeAccountId is required for Inventory and Service types'),
    otherwise: (schema) => schema.notRequired(),
  }),
  ExpenseAccountId: yup.string().required('ExpenseAccountId is required for all item types'),
  AssetAccountId: yup.string().when('Type', {
    is: QuickbooksItemType.Inventory,
    then: (schema) => schema.required('AssetAccountId is required for Inventory type'),
    otherwise: (schema) => schema.notRequired(),
  }),

  // Inventory-specific fields - conditionally required based on Type
  TrackQtyOnHand: yup.boolean().when('Type', {
    is: QuickbooksItemType.Inventory,
    then: (schema) => schema.oneOf([true], 'TrackQtyOnHand must be true for Inventory type'),
    otherwise: (schema) => schema.notRequired(),
  }),
  QtyOnHand: yup.number().when('Type', {
    is: QuickbooksItemType.Inventory,
    then: (schema) => schema.required('QtyOnHand is required for Inventory type'),
    otherwise: (schema) => schema.notRequired(),
  }),
  InvStartDate: yup.string().when('Type', {
    is: QuickbooksItemType.Inventory,
    then: (schema) => schema.required('InvStartDate is required for Inventory type'),
    otherwise: (schema) => schema.notRequired(),
  }),

  // Optional fields
  Sku: yup.string().notRequired().max(100, 'Sku cannot exceed 100 characters'),
  UnitPrice: yup.number().notRequired(),
  PurchaseCost: yup.number().notRequired(),
  Description: yup.string().notRequired().max(4000, 'Description cannot exceed 4000 characters'),
  PurchaseDesc: yup.string().notRequired().max(4000, 'PurchaseDesc cannot exceed 4000 characters'),
  ParentRef: yup.string().notRequired(),
  TaxCodeRef: yup.string().notRequired(),

  // For update operations - find existing item by SKU
  QueryBySku: yup.boolean().notRequired(),
})
export type QuickbooksCreateItemActionStepInput = yup.InferType<
  typeof quickbooksCreateItemActionStepInputSchema
>

/** Extended Quickbooks Item Schema with additional fields for response */
export const quickbooksItemResponseSchema = yup.object({
  Id: yup.string().required(),
  Name: yup.string().required(),
  FullyQualifiedName: yup.string().optional().nullable(),
  Sku: yup.string().optional().nullable(),
  Type: yup.string().optional().nullable(),
  SyncToken: yup.string().optional().nullable(),
  Active: yup.boolean().optional().nullable(),
  domain: yup.string().optional().nullable(),
  sparse: yup.boolean().optional().nullable(),
  Taxable: yup.boolean().optional().nullable(),
  UnitPrice: yup.number().optional().nullable(),
  PurchaseCost: yup.number().optional().nullable(),
  TrackQtyOnHand: yup.boolean().optional().nullable(),
  QtyOnHand: yup.number().optional().nullable(),
  Description: yup.string().optional().nullable(),
  PurchaseDesc: yup.string().optional().nullable(),
  InvStartDate: yup.string().optional().nullable(),
  SubItem: yup.boolean().optional().nullable(),
  Level: yup.number().optional().nullable(),
  ItemCategoryType: yup.string().optional().nullable(),
  SalesTaxIncluded: yup.boolean().optional().nullable(),
  PurchaseTaxIncluded: yup.boolean().optional().nullable(),
  AbatementRate: yup.number().optional().nullable(),
  ReverseChargeRate: yup.number().optional().nullable(),
  IncomeAccountRef: yup
    .object({
      value: yup.string().optional().nullable(),
      name: yup.string().optional().nullable(),
    })
    .optional()
    .nullable(),
  ExpenseAccountRef: yup
    .object({
      value: yup.string().optional().nullable(),
      name: yup.string().optional().nullable(),
    })
    .optional()
    .nullable(),
  AssetAccountRef: yup
    .object({
      value: yup.string().optional().nullable(),
      name: yup.string().optional().nullable(),
    })
    .optional()
    .nullable(),
  ParentRef: yup
    .object({
      value: yup.string().optional().nullable(),
      name: yup.string().optional().nullable(),
    })
    .optional()
    .nullable(),
  TaxClassificationRef: yup
    .object({
      value: yup.string().optional().nullable(),
      name: yup.string().optional().nullable(),
    })
    .optional()
    .nullable(),
  SalesTaxCodeRef: yup
    .object({
      value: yup.string().optional().nullable(),
      name: yup.string().optional().nullable(),
    })
    .optional()
    .nullable(),
  PurchaseTaxCodeRef: yup
    .object({
      value: yup.string().optional().nullable(),
      name: yup.string().optional().nullable(),
    })
    .optional()
    .nullable(),
  ClassRef: yup
    .object({
      value: yup.string().optional().nullable(),
      name: yup.string().optional().nullable(),
    })
    .optional()
    .nullable(),
  MetaData: yup
    .object({
      CreateTime: yup.string().optional().nullable(),
      LastUpdatedTime: yup.string().optional().nullable(),
    })
    .optional()
    .nullable(),
})
export type QuickbooksItemResponse = yup.InferType<typeof quickbooksItemResponseSchema>

/** Quickbooks Create/Update Item Response Schema */
export const quickbooksCreateUpdateItemResponseSchema = yup.object({
  Item: quickbooksItemResponseSchema.required(),
})
export type QuickbooksCreateUpdateItemResponse = yup.InferType<
  typeof quickbooksCreateUpdateItemResponseSchema
>

/** Quickbooks Get Item By ID Response Schema */
export const quickbooksGetItemByIdResponseSchema = yup.object({
  Item: quickbooksItemResponseSchema.required(),
})
export type QuickbooksGetItemByIdResponse = yup.InferType<
  typeof quickbooksGetItemByIdResponseSchema
>

/** Action step input schema for Quickbooks Update Item */
export const quickbooksUpdateItemActionStepInputSchema = yup.object({
  Id: yup.string().required(),
  SyncToken: yup.string().required(),
  sparse: yup.boolean().notRequired(),
  Name: yup.string().max(100).notRequired(),
  Type: yup
    .string()
    .oneOf([
      QuickbooksItemType.Inventory,
      QuickbooksItemType.NonInventory,
      QuickbooksItemType.Service,
      QuickbooksItemType.Category,
    ])
    .notRequired(),
  Sku: yup.string().max(100).notRequired(),
  Description: yup.string().max(4000).notRequired(),
  PurchaseDesc: yup.string().max(4000).notRequired(),
  UnitPrice: yup.number().notRequired(),
  PurchaseCost: yup.number().notRequired(),
  IncomeAccountId: yup.string().notRequired(),
  ExpenseAccountId: yup.string().notRequired(),
  AssetAccountId: yup.string().notRequired(),
  TrackQtyOnHand: yup.boolean().notRequired(),
  QtyOnHand: yup.number().notRequired(),
  InvStartDate: yup.string().notRequired(),
  TaxCodeRef: yup.string().notRequired(),
  ParentRef: yup.string().notRequired(),
  Active: yup.boolean().notRequired(),
  Taxable: yup.boolean().notRequired(),
})
export type QuickbooksUpdateItemActionStepInput = yup.InferType<
  typeof quickbooksUpdateItemActionStepInputSchema
>

/** Quickbooks Update Item Full Request Schema (maps to QB API POST body) */
export const quickbooksUpdateItemFullRequestSchema = yup.object({
  Id: yup.string().required(),
  SyncToken: yup.string().required(),
  sparse: yup.boolean().optional(),
  Name: yup.string().max(100).optional(),
  Type: yup.string().oneOf(['Inventory', 'NonInventory', 'Service', 'Category']).optional(),
  Sku: yup.string().max(100).optional(),
  Description: yup.string().max(4000).optional(),
  PurchaseDesc: yup.string().max(4000).optional(),
  UnitPrice: yup.number().optional(),
  PurchaseCost: yup.number().optional(),
  Active: yup.boolean().optional(),
  Taxable: yup.boolean().optional(),
  TrackQtyOnHand: yup.boolean().optional(),
  QtyOnHand: yup.number().optional(),
  InvStartDate: yup.string().optional(),
  IncomeAccountRef: yup
    .object({
      value: yup.string().optional().nullable(),
    })
    .optional(),
  ExpenseAccountRef: yup
    .object({
      value: yup.string().optional().nullable(),
    })
    .optional(),
  AssetAccountRef: yup
    .object({
      value: yup.string().optional().nullable(),
    })
    .optional(),
  TaxCodeRef: yup
    .object({
      value: yup.string().optional().nullable(),
    })
    .optional(),
  ParentRef: yup
    .object({
      value: yup.string().optional().nullable(),
    })
    .optional(),
})
export type QuickbooksUpdateItemFullRequest = yup.InferType<
  typeof quickbooksUpdateItemFullRequestSchema
>

// PREFERENCES (aka Custom Fields) (used in invoice and purchase order create/update) ------------------------------------------------------------------------------------------

/** Quickbooks Custom Field Sub-schema */
const quickbooksCustomFieldObjectSchema = yup.object({
  Name: yup.string().required(),
  Type: yup.string().oneOf(['BooleanType', 'StringType']).required(),
  BooleanValue: yup.boolean().when('Type', {
    is: 'BooleanType',
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.notRequired(),
  }),
  StringValue: yup.string().when('Type', {
    is: 'StringType',
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.notRequired(),
  }),
})
export type QuickbooksCustomFieldObject = yup.InferType<typeof quickbooksCustomFieldObjectSchema>

/** Quickbooks Custom Field Array Sub-schema */
const quickbooksCustomFieldArrayObjectSchema = yup.object({
  CustomField: yup.array().of(quickbooksCustomFieldObjectSchema).required(),
})

/** Quickbooks Get Preferences Response Schema */
export const quickbooksGetPreferencesResponseSchema = yup.object({
  Preferences: yup.object({
    SalesFormsPrefs: yup
      .object({
        CustomField: yup.array().of(quickbooksCustomFieldArrayObjectSchema).required(),
      })
      .required(),
    VendorAndPurchasesPrefs: yup
      .object({
        POCustomField: yup.array().of(quickbooksCustomFieldArrayObjectSchema).required(),
      })
      .required(),
  }),
})
export type QuickbooksGetPreferencesResponse = yup.InferType<
  typeof quickbooksGetPreferencesResponseSchema
>
/**
 * Type of form to determine the custom fields transformation preferences
 */
export type FormType = 'bill' | 'invoice' | 'purchaseOrder'
export type QuickbooksSalesFormsPrefs =
  QuickbooksGetPreferencesResponse['Preferences']['SalesFormsPrefs']
export type QuickbooksVendorAndPurchasesPrefs =
  QuickbooksGetPreferencesResponse['Preferences']['VendorAndPurchasesPrefs']

// SHARED AMONG CREATE/UPDATE -----------------------------------------------------------------------

/** Quickbooks Create Invoice or Puchase Order Request Schema - Custom Fields Input */
export const quickbooksSetCustomFieldInputSchema = yup.object({
  DefinitionId: yup.string().required(),
  Type: yup.string().oneOf(['StringType']).required(), // all custom field values are of type String
  StringValue: yup
    .string()
    .required()
    .transform((value): string | null => (value != null ? String(value) : value)), // the value you want to set on the custom field
  Name: yup.string().optional(), // the name of the custom field for the custom field definition
})
export type QuickbooksSetCustomFieldInput = yup.InferType<
  typeof quickbooksSetCustomFieldInputSchema
>

// INVOICE ------------------------------------------------------------------------------------------

/** Action step input schema of a Quickbooks sales item line detail */
const quickbooksSalesItemLineDetailActionStepInputSchema = yup.object({
  ItemId: yup.string().notRequired(),
  Qty: yup.number().notRequired(),
  UnitPrice: yup.number().notRequired(),
  ClassRef: yup
    .object({
      value: yup.string().notRequired(),
      name: yup.string().notRequired(),
    })
    .notRequired(),
  TaxCodeRef: yup
    .object({
      value: yup.string().notRequired(),
      name: yup.string().notRequired(),
    })
    .notRequired(),
  CreateIfNotExists: yup.boolean().notRequired(),
  ItemMetadata: quickbooksGetOrCreateItemMetadataActionStepInputSchema.notRequired(),
})

const quickbooksCustomExtensionAssociatedValueSchema = yup.object({
  Key: yup.string().notRequired(),
  Value: yup.string().notRequired(),
})

const quickbooksCustomExtensionSchema = yup.object({
  AssociatedValues: yup.array().of(quickbooksCustomExtensionAssociatedValueSchema).notRequired(),
  ExtensionType: yup.string().notRequired(),
})

const quickbooksReferenceObjectSchema = yup.object({
  value: yup.string().notRequired(),
  name: yup.string().notRequired(),
})

/** Nested discount detail on an invoice line item or top-level DiscountLineItem input */
const quickbooksDiscountLineDetailActionStepInputSchema = yup.object({
  PercentBased: yup.boolean().notRequired(),
  DiscountPercent: yup
    .number()
    .test(
      'discount-line-detail-percent-required',
      'DiscountPercent is required when PercentBased is true',
      function discountLineDetailPercentRequired(value) {
        return !this.parent.PercentBased || value != null
      },
    ),
  DiscountAccountRef: quickbooksReferenceObjectSchema.notRequired(),
  ClassRef: quickbooksReferenceObjectSchema.notRequired(),
  TaxCodeRef: quickbooksReferenceObjectSchema.notRequired(),
})

/** Top-level invoice discount input (convenience field, appended before shipping) */
export const quickbooksDiscountLineItemActionStepInputSchema = yup
  .object({
    Amount: yup
      .number()
      .moreThan(0)
      .test(
        'discount-line-item-amount-required',
        'Amount is required when PercentBased is false or omitted',
        function discountLineItemAmountRequired(value) {
          return this.parent.PercentBased || value != null
        },
      ),
    Description: yup.string().notRequired(),
  })
  .concat(quickbooksDiscountLineDetailActionStepInputSchema)
export type QuickbooksDiscountLineItemActionStepInput = yup.InferType<
  typeof quickbooksDiscountLineItemActionStepInputSchema
>

/** Action step input schema of a Quickbooks line item */
const quickbooksLineItemActionStepInputSchema = yup.object({
  DetailType: yup.string().oneOf(['SalesItemLineDetail', 'DiscountLineDetail']).required(),
  LineItemTotalAmount: yup
    .number()
    .test(
      'line-item-total-amount-required',
      'LineItemTotalAmount is required',
      function lineItemTotalAmountRequired(value) {
        const { DetailType: detailType, DiscountLineDetail: discountLineDetail } = this.parent
        switch (detailType) {
          case 'SalesItemLineDetail':
            return value != null
          case 'DiscountLineDetail':
            return discountLineDetail?.PercentBased || value != null
          default:
            return true
        }
      },
    ),
  LineItemDescription: yup.string().notRequired(),
  LineNumber: yup.number().notRequired(),
  CustomExtensions: yup.array().of(quickbooksCustomExtensionSchema).notRequired(),
  SalesItemLineDetail: quickbooksSalesItemLineDetailActionStepInputSchema.when('DetailType', {
    is: 'SalesItemLineDetail',
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.notRequired(),
  }),
  DiscountLineDetail: quickbooksDiscountLineDetailActionStepInputSchema.notRequired(),
})
export type QuickbooksLineItemActionStepInput = yup.InferType<
  typeof quickbooksLineItemActionStepInputSchema
>

/** Schema for Quickbooks PhysicalAddress object */
export const quickbooksPhysicalAddressSchema = yup.object({
  Id: yup.string().notRequired(),
  PostalCode: yup.string().max(30).notRequired(),
  City: yup.string().max(255).notRequired(),
  Country: yup.string().max(255).notRequired(),
  Line1: yup.string().max(500).notRequired(),
  Line2: yup.string().max(500).notRequired(),
  Line3: yup.string().max(500).notRequired(),
  Line4: yup.string().max(500).notRequired(),
  Line5: yup.string().max(500).notRequired(),
  CountrySubDivisionCode: yup.string().max(255).notRequired(),
})

/** Schema for Quickbooks TaxLine object */
const quickbooksTaxLineObject = yup.object({
  DetailType: yup.string().oneOf(['TaxLineDetail']).required(),
  TaxLineDetail: yup
    .object({
      TaxRateRef: yup
        .object({
          value: yup.string().notRequired(),
          name: yup.string().notRequired(),
        })
        .required(),
      NetAmountTaxable: yup.number().notRequired(),
      PercentBased: yup.boolean().notRequired(),
      TaxInclusiveAmount: yup.number().notRequired(),
      OverrideDeltaAmount: yup.number().notRequired(),
      TaxPercent: yup.number().notRequired(),
    })
    .required(),
  Amount: yup.number().notRequired(),
})

/** Base schema for Quickbooks create/update invoice action step input */
export const quickbooksInvoiceBaseActionStepInputSchema = yup.object({
  InvoiceNumber: yup.string().notRequired(),
  CustomFieldsInput: yup
    .array()
    .of(
      yup.object({
        Key: yup.string().required(),
        Value: yup
          .string()
          .nullable()
          .transform((value): string | null => (value != null ? String(value) : value)),
      }),
    )
    .notRequired(),
  QuickbooksCustomerId: yup.string().notRequired(),
  BillingEmail: yup.string().email().notRequired(),
  Customer: quickbooksCreateCustomerActionStepInputSchema.notRequired(),
  OverwriteExistingCustomerDetails: yup.boolean().default(true).notRequired(),
  LineItems: yup
    .array()
    .of(quickbooksLineItemActionStepInputSchema)
    .min(1, 'At least one line item is required')
    .required(),
  DiscountLineItem: yup
    .mixed<QuickbooksDiscountLineItemActionStepInput>()
    .optional()
    .test('validate-discount-line-item', 'Invalid DiscountLineItem', (value) => {
      if (value === undefined || value === null) {
        return true
      }
      return quickbooksDiscountLineItemActionStepInputSchema.isValidSync(value)
    }),
  ShippingLineItem: yup.number().notRequired(),
  CustomerMemo: yup.string().optional().nullable(),
  TxnDate: yup.string().optional().nullable(),
  DueDate: yup.string().optional().nullable(),
  ShipFromAddr: quickbooksPhysicalAddressSchema.notRequired(),
  CurrencyRef: yup
    .object({
      name: yup.string().notRequired(),
      value: yup.string().notRequired(),
    })
    .notRequired(),
  GlobalTaxCalculation: yup
    .string()
    .oneOf(['TaxExcluded', 'TaxInclusive', 'NotApplicable'])
    .notRequired(),
  ProjectRef: yup
    .object({
      name: yup.string().notRequired(),
      value: yup.string().notRequired(),
    })
    .notRequired(),
  ShipDate: yup.string().notRequired(),
  TrackingNum: yup.string().notRequired(),
  ClassRef: yup
    .object({
      name: yup.string().notRequired(),
      value: yup.string().notRequired(),
    })
    .notRequired(),
  PrintStatus: yup.string().oneOf(['NotSet', 'NeedToPrint', 'PrintComplete']).notRequired(),
  SalesTermRef: yup
    .object({
      name: yup.string().notRequired(),
      value: yup.string().notRequired(),
    })
    .notRequired(),
  TxnSource: yup.string().notRequired(),
  LinkedTxn: yup
    .array()
    .of(
      yup.object({
        TxnId: yup.string().required(),
        TxnType: yup.string().required(),
        TxnLineId: yup.string().notRequired(),
      }),
    )
    .notRequired(),
  DepositToAccountRef: yup
    .object({
      value: yup.string().notRequired(),
      name: yup.string().notRequired(),
    })
    .notRequired(),
  AllowOnlineACHPayment: yup.boolean().notRequired(),
  TransactionLocationType: yup.string().notRequired(),
  PrivateNote: yup.string().max(4000).notRequired(),
  BillEmailCc: yup.string().email().max(100).notRequired(),
  EmailStatus: yup.string().oneOf(['NotSet', 'NeedToSend', 'EmailSent']).notRequired(),
  ExchangeRate: yup.number().notRequired(),
  Deposit: yup.number().notRequired(),
  TxnTaxDetail: yup.object({
    TxnTaxCodeRef: yup
      .object({
        value: yup.string().notRequired(),
        name: yup.string().notRequired(),
      })
      .notRequired(),
    TotalTax: yup.number().notRequired(),
    TaxLine: yup.array().of(quickbooksTaxLineObject).notRequired(),
  }),
  AllowOnlineCreditCardPayment: yup.boolean().notRequired(),
  ShipAddr: quickbooksPhysicalAddressSchema.notRequired(),
  DepartmentRef: yup
    .object({
      value: yup.string().notRequired(),
      name: yup.string().notRequired(),
    })
    .notRequired(),
  BillEmailBcc: yup.string().email().max(100).notRequired(),
  ShipMethodRef: yup
    .object({
      value: yup.string().notRequired(),
      name: yup.string().notRequired(),
    })
    .notRequired(),
  BillAddr: quickbooksPhysicalAddressSchema.notRequired(),
  ApplyTaxAfterDiscount: yup.boolean().notRequired(),
})
export type QuickbooksInvoiceBaseActionStepInput = yup.InferType<
  typeof quickbooksInvoiceBaseActionStepInputSchema
>

/** Action step input schema for Quickbooks Create Invoice */
export const quickbooksCreateInvoiceActionStepInputSchema =
  quickbooksInvoiceBaseActionStepInputSchema

/** Action step input schema for Quickbooks Update Invoice */
export const quickbooksUpdateInvoiceActionStepInputSchema = yup
  .object({
    QuickbooksInvoiceId: yup.string().notRequired(),
  })
  .concat(quickbooksInvoiceBaseActionStepInputSchema)
export type QuickbooksUpdateInvoiceActionStepInput = yup.InferType<
  typeof quickbooksUpdateInvoiceActionStepInputSchema
>

/** Minimal fields used to look up an invoice by QuickBooks Id or DocNumber (query API). */
export type QuickbooksInvoiceLookupInput = Pick<
  QuickbooksUpdateInvoiceActionStepInput,
  'QuickbooksInvoiceId' | 'InvoiceNumber'
>

/** Action step input for deleting a QuickBooks invoice (POST invoice?operation=delete). */
export const quickbooksDeleteInvoiceActionStepInputSchema = yup
  .object({
    QuickbooksInvoiceId: yup.string().notRequired(),
    InvoiceNumber: yup.string().notRequired(),
    /** When set with QuickbooksInvoiceId, skips read-before-delete to resolve SyncToken. */
    SyncToken: yup.string().notRequired(),
  })
  .test(
    'qb-delete-invoice-needs-id-or-doc',
    'Provide QuickbooksInvoiceId (with optional SyncToken), or InvoiceNumber (DocNumber) to locate the invoice',
    (value) => {
      const hasId =
        value?.QuickbooksInvoiceId != null && String(value.QuickbooksInvoiceId).trim() !== ''
      const hasDoc = value?.InvoiceNumber != null && String(value.InvoiceNumber).trim() !== ''
      if (hasId) {
        return true
      }
      if (hasDoc) {
        return true
      }
      return false
    },
  )

export type QuickbooksDeleteInvoiceActionStepInput = yup.InferType<
  typeof quickbooksDeleteInvoiceActionStepInputSchema
>

/** Response body for QuickBooks POST invoice?operation=delete */
export const quickbooksDeleteInvoiceResponseSchema = yup.object({
  Invoice: yup
    .object({
      status: yup.string().oneOf(['Deleted']).required(),
      domain: yup.string().notRequired(),
      Id: yup.string().required(),
    })
    .required(),
})

export type QuickbooksDeleteInvoiceResponse = yup.InferType<
  typeof quickbooksDeleteInvoiceResponseSchema
>

/**
 * Quickbooks Create Invoice Request Schema - Individual Line Item Input
 *
 * This schema is a union of all possible line item types that can be included in a Quickbooks invoice,
 * including SalesItemLine, GroupLine, DescriptionOnlyLine, DiscountLine, and SubTotalLine.
 *
 * Used to construct each line item after getting the item ID from Quickbooks, also used for shipping line
 *
 * We support SalesItemLineDetail and DiscountLineDetail.
 */
const quickbooksInvoiceSalesLineItemSchema = yup.object({
  DetailType: yup.string().required().oneOf(['SalesItemLineDetail']),
  SalesItemLineDetail: yup
    .object({
      // Set ItemRef.value to SHIPPING_ITEM_ID when Line.amount represents transaction-wide shipping charges
      // When a line lacks an ItemRef it is treated as documentation and Line.Amount is ignored.
      ItemRef: yup
        .object({
          value: yup.string().required(),
        })
        .optional(),
      Qty: yup.number().optional(), // Number of items for the line
      // If supplied during create or update, overrides the unit price of the item as referenced by ItemRef
      UnitPrice: yup.number().optional(),
      ClassRef: yup
        .object({
          value: yup.string().optional(),
          name: yup.string().optional(),
        })
        .optional(),
      TaxCodeRef: yup
        .object({
          value: yup.string().optional(),
          name: yup.string().optional(),
        })
        .optional(),
    })
    .required()
    .strict(),
  Amount: yup
    .number()
    .test('invoice-sales-line-amount-required', 'Amount is required', (value) => value != null),
  Description: yup.string().optional(),
  LineNum: yup.number().optional(), // Specifies the position of the line
  CustomExtensions: yup.array().of(quickbooksCustomExtensionSchema).optional(),
})

const quickbooksInvoiceDiscountLineItemSchema = yup.object({
  DetailType: yup.string().required().oneOf(['DiscountLineDetail']),
  DiscountLineDetail: yup
    .object({
      PercentBased: yup.boolean().optional(),
      DiscountPercent: yup.number().optional(),
      DiscountAccountRef: yup
        .object({
          value: yup.string().optional(),
          name: yup.string().optional(),
        })
        .optional(),
      ClassRef: yup
        .object({
          value: yup.string().optional(),
          name: yup.string().optional(),
        })
        .optional(),
      TaxCodeRef: yup
        .object({
          value: yup.string().optional(),
          name: yup.string().optional(),
        })
        .optional(),
    })
    .required()
    .strict(),
  Amount: yup
    .number()
    .test(
      'invoice-discount-line-amount-required',
      'Amount is required',
      function invoiceDiscountLineAmountRequired(value) {
        return this.parent.DiscountLineDetail?.PercentBased || value != null
      },
    ),
  Description: yup.string().optional(),
  LineNum: yup.number().optional(), // Specifies the position of the line
  CustomExtensions: yup.array().of(quickbooksCustomExtensionSchema).optional(),
})

export type QuickbooksInvoiceSalesLineItem = yup.InferType<
  typeof quickbooksInvoiceSalesLineItemSchema
>
export type QuickbooksInvoiceDiscountLineItem = yup.InferType<
  typeof quickbooksInvoiceDiscountLineItemSchema
>
export type QuickbooksInvoiceLineItem =
  | QuickbooksInvoiceSalesLineItem
  | QuickbooksInvoiceDiscountLineItem

const quickbooksInvoiceLineItemSchema = yup.lazy((value: unknown) => {
  if (
    typeof value === 'object' &&
    value !== null &&
    'DetailType' in value &&
    value.DetailType === 'DiscountLineDetail'
  ) {
    return quickbooksInvoiceDiscountLineItemSchema
  }
  return quickbooksInvoiceSalesLineItemSchema
})

/** Quickbooks Create/Update Invoice Request Schema - Base */
const quickbooksInvoiceBaseObjectSchema = yup.object({
  CustomerRef: yup
    .object({
      value: yup.string().optional(),
    })
    .required(),
  BillEmail: yup.object({ Address: yup.string().email().optional() }).optional(),
  DocNumber: yup.string().optional(), // Custom invoice number
  Line: yup.array().of(quickbooksInvoiceLineItemSchema).required().min(1),
  CustomField: yup.array().of(quickbooksSetCustomFieldInputSchema).optional(),
  CustomerMemo: yup.object({ value: yup.string().optional() }).optional(),
  TxnDate: yup.string().optional().nullable(),
  DueDate: yup.string().optional().nullable(),
  ShipFromAddr: quickbooksPhysicalAddressSchema.notRequired(),
  CurrencyRef: yup
    .object({
      name: yup.string().notRequired(),
      value: yup.string().notRequired(),
    })
    .notRequired(),
  GlobalTaxCalculation: yup
    .string()
    .oneOf(['TaxExcluded', 'TaxInclusive', 'NotApplicable'])
    .notRequired(),
  ProjectRef: yup
    .object({
      name: yup.string().notRequired(),
      value: yup.string().notRequired(),
    })
    .notRequired(),
  ShipDate: yup.string().notRequired(),
  TrackingNum: yup.string().notRequired(),
  ClassRef: yup
    .object({
      name: yup.string().notRequired(),
      value: yup.string().notRequired(),
    })
    .notRequired(),
  PrintStatus: yup.string().oneOf(['NotSet', 'NeedToPrint', 'PrintComplete']).notRequired(),
  SalesTermRef: yup
    .object({
      name: yup.string().notRequired(),
      value: yup.string().notRequired(),
    })
    .notRequired(),
  TxnSource: yup.string().notRequired(),
  LinkedTxn: yup
    .array()
    .of(
      yup.object({
        TxnId: yup.string().notRequired(),
        TxnType: yup.string().notRequired(),
        TxnLineId: yup.string().notRequired(),
      }),
    )
    .notRequired(),
  DepositToAccountRef: yup
    .object({
      value: yup.string().notRequired(),
      name: yup.string().notRequired(),
    })
    .notRequired(),
  AllowOnlineACHPayment: yup.boolean().notRequired(),
  TransactionLocationType: yup.string().notRequired(),
  PrivateNote: yup.string().max(4000).notRequired(),
  BillEmailCc: yup
    .object({
      Address: yup.string().email().max(100).notRequired(),
    })
    .notRequired(),
  EmailStatus: yup.string().oneOf(['NotSet', 'NeedToSend', 'EmailSent']).notRequired(),
  ExchangeRate: yup.number().notRequired(),
  Deposit: yup.number().notRequired(),
  TxnTaxDetail: yup
    .object({
      TxnTaxCodeRef: yup
        .object({
          value: yup.string().notRequired(),
          name: yup.string().notRequired(),
        })
        .notRequired(),
      TotalTax: yup.number().notRequired(),
      TaxLine: yup.array().of(quickbooksTaxLineObject).notRequired(),
    })
    .notRequired(),
  AllowOnlineCreditCardPayment: yup.boolean().notRequired(),
  ShipAddr: quickbooksPhysicalAddressSchema.notRequired(),
  DepartmentRef: yup
    .object({
      value: yup.string().notRequired(),
      name: yup.string().notRequired(),
    })
    .notRequired(),
  BillEmailBcc: yup
    .object({
      Address: yup.string().email().max(100).notRequired(),
    })
    .notRequired(),
  ShipMethodRef: yup
    .object({
      value: yup.string().notRequired(),
      name: yup.string().notRequired(),
    })
    .notRequired(),
  BillAddr: quickbooksPhysicalAddressSchema.notRequired(),
  ApplyTaxAfterDiscount: yup.boolean().notRequired(),
})
export type QuickbooksInvoiceBaseObject = yup.InferType<typeof quickbooksInvoiceBaseObjectSchema>

/** Quickbooks Create Invoice Request Schema */
export const quickbooksCreateInvoiceRequestSchema = quickbooksInvoiceBaseObjectSchema
export type QuickbooksCreateInvoiceRequest = yup.InferType<
  typeof quickbooksCreateInvoiceRequestSchema
>

/** Quickbooks Update Invoice Request Schema */
export const quickbooksUpdateInvoiceRequestSchema = yup
  .object({
    Id: yup.string().required(),
    SyncToken: yup.string().required(),
    sparse: yup.boolean().optional().nullable(),
  })
  .concat(quickbooksInvoiceBaseObjectSchema)
export type QuickbooksUpdateInvoiceRequest = yup.InferType<
  typeof quickbooksUpdateInvoiceRequestSchema
>

/** Quickbooks Invoice Schema (incomplete, only including core fields) */
export const quickbooksInvoiceSchema = yup.object({
  Id: yup.string().required(),
  DocNumber: yup.string().optional().nullable(),
  SyncToken: yup.string().required(),
  Balance: yup.number().optional().nullable(),
})
export type QuickbooksInvoice = yup.InferType<typeof quickbooksInvoiceSchema>

/** Quickbooks Create or Update Invoice Response Schema */
export const quickbooksCreateUpdateInvoiceResponseSchema = yup.object({
  Invoice: quickbooksInvoiceSchema.required(),
})
export type QuickbooksCreateUpdateInvoiceResponse = yup.InferType<
  typeof quickbooksCreateUpdateInvoiceResponseSchema
>

/** Quickbooks Get Invoice by ID Response Schema */
export const quickbooksGetInvoiceByIdResponseSchema = yup.object({
  Invoice: quickbooksInvoiceSchema.required(),
})

/** Quickbooks Query Invoice Response Schema */
export const quickbooksQueryInvoiceResponseSchema = yup.object({
  QueryResponse: yup
    .object({
      maxResults: yup.number().optional(),
      startPosition: yup.number().optional(),
      totalCount: yup.number().optional(),
      Invoice: yup.array().of(quickbooksInvoiceSchema).optional(),
    })
    .required(),
  time: yup.string().optional(),
})
export type QuickbooksQueryInvoiceResponse = yup.InferType<
  typeof quickbooksQueryInvoiceResponseSchema
>

// CREDIT MEMO ---------------------------------------------------------------------------------------

/** Action step input schema for Quickbooks Create Credit Memo - SalesItemLineDetail line item */
const quickbooksCreditMemoLineItemActionStepInputSchema = yup.object({
  DetailType: yup.string().oneOf(['SalesItemLineDetail']).required(),
  Amount: yup.number().required(),
  CustomExtensions: yup.array().of(quickbooksCustomExtensionSchema).notRequired(),
  SalesItemLineDetail: yup
    .object({
      ItemRef: yup
        .object({
          name: yup.string().notRequired(),
          value: yup.string().notRequired(),
        })
        .required(),
    })
    .when('DetailType', {
      is: 'SalesItemLineDetail',
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.notRequired(),
    }),
})

/** Action step input schema for Quickbooks Create Credit Memo */
export const quickbooksCreateCreditMemoActionStepInputSchema = yup.object({
  CustomerRef: yup
    .object({
      name: yup.string().notRequired(),
      value: yup.string().notRequired(),
    })
    .required(),
  Line: yup
    .array()
    .of(quickbooksCreditMemoLineItemActionStepInputSchema)
    .min(1, 'At least one line item is required')
    .required(),
})
export type QuickbooksCreateCreditMemoActionStepInput = yup.InferType<
  typeof quickbooksCreateCreditMemoActionStepInputSchema
>

// SALES RECEIPT ---------------------------------------------------------------------------------------

/**
 * Create Sales Receipt — line with `SalesItemLineDetail` (same minimal shape as create credit memo line).
 * @see https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/salesreceipt#create-a-sales-receipt
 */
/** Shared sales-form line shape for create Sales Receipt and Refund Receipt (`SalesItemLineDetail`). */
export const quickbooksSalesReceiptCreateLineItemActionStepInputSchema = yup.object({
  Id: yup.string().notRequired(),
  DetailType: yup.string().oneOf(['SalesItemLineDetail']).required(),
  Amount: yup.number().required(),
  Description: yup.string().notRequired(),
  LineNum: yup.number().notRequired(),
  CustomExtensions: yup.array().of(quickbooksCustomExtensionSchema).notRequired(),
  SalesItemLineDetail: yup
    .object({
      ItemRef: yup
        .object({
          name: yup.string().notRequired(),
          value: yup.string().notRequired(),
        })
        .required(),
      TaxCodeRef: yup
        .object({
          name: yup.string().notRequired(),
          value: yup.string().notRequired(),
        })
        .notRequired(),
      Qty: yup.number().notRequired(),
      UnitPrice: yup.number().notRequired(),
    })
    .when('DetailType', {
      is: 'SalesItemLineDetail',
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.notRequired(),
    }),
})

/** Action step input for QuickBooks Create Sales Receipt (minimum: at least one sales line per Intuit). */
export const quickbooksCreateSalesReceiptActionStepInputSchema = yup.object({
  CustomerRef: yup
    .object({
      name: yup.string().notRequired(),
      value: yup.string().notRequired(),
    })
    .notRequired(),
  CurrencyRef: yup
    .object({
      name: yup.string().notRequired(),
      value: yup.string().required(),
    })
    .notRequired(),
  TxnDate: yup.string().notRequired(),
  Line: yup
    .array()
    .of(quickbooksSalesReceiptCreateLineItemActionStepInputSchema)
    .min(1, 'At least one line item is required')
    .required(),
})
export type QuickbooksCreateSalesReceiptActionStepInput = yup.InferType<
  typeof quickbooksCreateSalesReceiptActionStepInputSchema
>

/** Action step input for QuickBooks Create Refund Receipt (`DepositToAccountRef` + at least one line per Intuit). */
export const quickbooksCreateRefundReceiptActionStepInputSchema = yup.object({
  DepositToAccountRef: yup
    .object({
      name: yup.string().notRequired(),
      value: yup.string().required(),
    })
    .required(),
  CustomerRef: yup
    .object({
      name: yup.string().notRequired(),
      value: yup.string().notRequired(),
    })
    .notRequired(),
  CurrencyRef: yup
    .object({
      name: yup.string().notRequired(),
      value: yup.string().required(),
    })
    .notRequired(),
  TxnDate: yup.string().notRequired(),
  Line: yup
    .array()
    .of(quickbooksSalesReceiptCreateLineItemActionStepInputSchema)
    .min(1, 'At least one line item is required')
    .required(),
})
export type QuickbooksCreateRefundReceiptActionStepInput = yup.InferType<
  typeof quickbooksCreateRefundReceiptActionStepInputSchema
>

const quickbooksRefundReceiptFullUpdateMinimumFieldsTest = (
  value: unknown,
): value is Record<string, unknown> => {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return false
  }
  const v = value as Record<string, unknown>
  if (typeof v.Id !== 'string' || v.Id.trim().length === 0) {
    return false
  }
  if (typeof v.SyncToken !== 'string' || v.SyncToken.trim().length === 0) {
    return false
  }
  if (v.sparse === true) {
    return false
  }
  const depositRef = v.DepositToAccountRef
  if (depositRef === null || typeof depositRef !== 'object' || Array.isArray(depositRef)) {
    return false
  }
  const dr = depositRef as Record<string, unknown>
  if (typeof dr.value !== 'string' || dr.value.trim().length === 0) {
    return false
  }
  if (!Array.isArray(v.Line) || v.Line.length === 0) {
    return false
  }
  return true
}

const quickbooksRefundReceiptSparseUpdateMinimumFieldsTest = (
  value: unknown,
): value is Record<string, unknown> => {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return false
  }
  const v = value as Record<string, unknown>
  if (v.sparse !== true) {
    return false
  }
  if (typeof v.Id !== 'string' || v.Id.trim().length === 0) {
    return false
  }
  if (typeof v.SyncToken !== 'string' || v.SyncToken.trim().length === 0) {
    return false
  }
  return true
}

/**
 * Full update `POST /refundreceipt` — send writable fields from a read (GET) response; omitted fields become NULL.
 * Do not set `sparse: true` (use `quickbooksSparseUpdateRefundReceiptActionStepInputSchema` for sparse updates).
 *
 * @see https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/refundreceipt#full-update-a-refund-receipt
 */
export const quickbooksUpdateRefundReceiptActionStepInputSchema: yup.Schema<
  Record<string, unknown>
> = yup
  .mixed<Record<string, unknown>>()
  .defined()
  .required()
  .test(
    'qbo-refund-receipt-full-update-fields',
    'Full update requires Id, SyncToken, DepositToAccountRef with value, at least one Line, and sparse must not be true (map from GET refundreceipt response)',
    quickbooksRefundReceiptFullUpdateMinimumFieldsTest,
  )

export type QuickbooksUpdateRefundReceiptActionStepInput = Record<string, unknown>

/**
 * Sparse update `POST /refundreceipt` with `sparse: true` — only supplied fields are updated.
 * Intuit’s sample sends only `Id`, `SyncToken`, `sparse`, and fields to change (e.g. `PrivateNote`); `Line` and `DepositToAccountRef` are not required on every sparse request.
 *
 * @see https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/refundreceipt#sparse-update-a-refund-receipt
 */
export const quickbooksSparseUpdateRefundReceiptActionStepInputSchema: yup.Schema<
  Record<string, unknown>
> = yup
  .mixed<Record<string, unknown>>()
  .defined()
  .required()
  .test(
    'qbo-refund-receipt-sparse-update-fields',
    'Sparse update requires sparse: true, Id, and SyncToken',
    quickbooksRefundReceiptSparseUpdateMinimumFieldsTest,
  )

export type QuickbooksSparseUpdateRefundReceiptActionStepInput = Record<string, unknown>

const quickbooksSalesReceiptFullUpdateMinimumFieldsTest = (
  value: unknown,
): value is Record<string, unknown> => {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return false
  }
  const v = value as Record<string, unknown>
  if (typeof v.Id !== 'string' || v.Id.trim().length === 0) {
    return false
  }
  if (typeof v.SyncToken !== 'string' || v.SyncToken.trim().length === 0) {
    return false
  }
  if (v.sparse === true) {
    return false
  }
  const customerRef = v.CustomerRef
  if (customerRef === null || typeof customerRef !== 'object' || Array.isArray(customerRef)) {
    return false
  }
  const cr = customerRef as Record<string, unknown>
  if (typeof cr.value !== 'string' || cr.value.trim().length === 0) {
    return false
  }
  if (!Array.isArray(v.Line) || v.Line.length === 0) {
    return false
  }
  return true
}

const quickbooksSalesReceiptSparseUpdateMinimumFieldsTest = (
  value: unknown,
): value is Record<string, unknown> => {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return false
  }
  const v = value as Record<string, unknown>
  if (v.sparse !== true) {
    return false
  }
  if (typeof v.Id !== 'string' || v.Id.trim().length === 0) {
    return false
  }
  if (typeof v.SyncToken !== 'string' || v.SyncToken.trim().length === 0) {
    return false
  }
  if (!Array.isArray(v.Line) || v.Line.length === 0) {
    return false
  }
  return true
}

/**
 * Full update `POST /salesreceipt` — send writable fields from a read (GET) response; omitted fields become NULL.
 * Do not set `sparse: true` (use `quickbooksSparseUpdateSalesReceiptActionStepInputSchema` for sparse updates).
 * This schema only checks Intuit’s minimum identifiers; the full JSON is forwarded unchanged.
 *
 * @see https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/salesreceipt#full-update-a-salesreceipt
 */
export const quickbooksUpdateSalesReceiptActionStepInputSchema: yup.Schema<
  Record<string, unknown>
> = yup
  .mixed<Record<string, unknown>>()
  .defined()
  .required()
  .test(
    'qbo-sales-receipt-full-update-fields',
    'Full update requires Id, SyncToken, CustomerRef with value, at least one Line, and sparse must not be true (map from GET salesreceipt response)',
    quickbooksSalesReceiptFullUpdateMinimumFieldsTest,
  )

export type QuickbooksUpdateSalesReceiptActionStepInput = Record<string, unknown>

/**
 * Sparse update `POST /salesreceipt` with `sparse: true` — only supplied fields are updated.
 * Intuit’s sample omits `CustomerRef`; body is forwarded unchanged after minimum checks.
 *
 * @see https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/salesreceipt#sparse-update-a-salesreceipt
 */
export const quickbooksSparseUpdateSalesReceiptActionStepInputSchema: yup.Schema<
  Record<string, unknown>
> = yup
  .mixed<Record<string, unknown>>()
  .defined()
  .required()
  .test(
    'qbo-sales-receipt-sparse-update-fields',
    'Sparse update requires sparse: true, Id, SyncToken, and at least one Line',
    quickbooksSalesReceiptSparseUpdateMinimumFieldsTest,
  )

export type QuickbooksSparseUpdateSalesReceiptActionStepInput = Record<string, unknown>

/** Quickbooks Credit Memo Schema (incomplete, only including core fields) */
export const quickbooksCreditMemoSchema = yup.object({
  Id: yup.string().required(),
  DocNumber: yup.string().optional().nullable(),
  SyncToken: yup.string().optional().nullable(),
  TotalAmt: yup.number().optional().nullable(),
  Balance: yup.number().optional().nullable(),
  RemainingCredit: yup.number().optional().nullable(),
})
export type QuickbooksCreditMemo = yup.InferType<typeof quickbooksCreditMemoSchema>

/** Quickbooks Create Credit Memo Response Schema */
export const quickbooksCreateCreditMemoResponseSchema = yup.object({
  CreditMemo: quickbooksCreditMemoSchema.required(),
})
export type QuickbooksCreateCreditMemoResponse = yup.InferType<
  typeof quickbooksCreateCreditMemoResponseSchema
>

/** Delete credit memo — request body (flat Id + SyncToken per QBO samples). */
export const quickbooksDeleteCreditMemoActionStepInputSchema = yup.object({
  Id: yup.string().required(),
  SyncToken: yup.string().required(),
})
export type QuickbooksDeleteCreditMemoActionStepInput = yup.InferType<
  typeof quickbooksDeleteCreditMemoActionStepInputSchema
>

/** Response shape after POST .../creditmemo?operation=delete */
export const quickbooksDeletedCreditMemoSchema = yup.object({
  status: yup.string().oneOf(['Deleted']).required(),
  domain: yup.string().optional(),
  Id: yup.string().required(),
})

export const quickbooksDeleteCreditMemoResponseSchema = yup.object({
  CreditMemo: quickbooksDeletedCreditMemoSchema.required(),
})
export type QuickbooksDeleteCreditMemoResponse = yup.InferType<
  typeof quickbooksDeleteCreditMemoResponseSchema
>

// ESTIMATE ------------------------------------------------------------------------------------------

/** Action step input schema of a Quickbooks estimate sales item line detail */
const quickbooksEstimateSalesItemLineDetailActionStepInputSchema = yup.object({
  ItemRef: yup
    .object({
      value: yup.string().notRequired(),
      name: yup.string().notRequired(),
      CreateIfNotExists: yup.boolean().notRequired(),
      ItemMetadata: quickbooksGetOrCreateItemMetadataActionStepInputSchema.notRequired(),
    })
    .notRequired(),
  Qty: yup.number().notRequired(),
  UnitPrice: yup.number().notRequired(),
  TaxCodeRef: yup
    .object({
      value: yup.string().notRequired(),
      name: yup.string().notRequired(),
    })
    .notRequired(),
  DiscountRate: yup.number().notRequired(),
  DiscountAmt: yup.number().notRequired(),
  TaxInclusiveAmt: yup.number().notRequired(),
  ServiceDate: yup.string().notRequired(),
  ClassRef: yup
    .object({
      value: yup.string().notRequired(),
      name: yup.string().notRequired(),
    })
    .notRequired(),
})

/** Action step input schema of a Quickbooks estimate line item (SalesItemLineDetail only for create) */
const quickbooksEstimateLineItemActionStepInputSchema = yup.object({
  DetailType: yup.string().oneOf(['SalesItemLineDetail']).required(),
  Amount: yup.number().required(),
  Description: yup.string().max(4000).notRequired(),
  LineNum: yup.number().notRequired(),
  CustomExtensions: yup.array().of(quickbooksCustomExtensionSchema).notRequired(),
  SalesItemLineDetail: quickbooksEstimateSalesItemLineDetailActionStepInputSchema.when(
    'DetailType',
    {
      is: 'SalesItemLineDetail',
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.notRequired(),
    },
  ),
})
export type QuickbooksEstimateLineItemActionStepInput = yup.InferType<
  typeof quickbooksEstimateLineItemActionStepInputSchema
>

/** Base schema for Quickbooks create estimate action step input */
export const quickbooksEstimateBaseActionStepInputSchema = yup.object({
  CustomerRef: yup
    .object({
      value: yup.string().notRequired(),
      name: yup.string().notRequired(),
    })
    .required(),
  Customer: quickbooksCreateCustomerActionStepInputSchema.notRequired(),
  Line: yup
    .array()
    .of(quickbooksEstimateLineItemActionStepInputSchema)
    .min(1, 'At least one line item is required')
    .required(),
  CurrencyRef: yup
    .object({
      name: yup.string().notRequired(),
      value: yup.string().notRequired(),
    })
    .notRequired(),
  BillEmail: yup
    .object({
      Address: yup.string().email().notRequired(),
    })
    .notRequired(),
  CustomerMemo: yup
    .object({
      value: yup.string().notRequired(),
    })
    .notRequired(),
  PrintStatus: yup.string().oneOf(['NeedToPrint', 'NotSet', 'PrintComplete']).notRequired(),
  EmailStatus: yup.string().oneOf(['NeedToSend', 'NotSet']).notRequired(),
  ApplyTaxAfterDiscount: yup.boolean().notRequired(),
  TxnTaxDetail: yup
    .object({
      TotalTax: yup.number().notRequired(),
    })
    .notRequired(),
  BillAddr: quickbooksPhysicalAddressSchema.notRequired(),
  ShipAddr: quickbooksPhysicalAddressSchema.notRequired(),
  ProjectRef: yup
    .object({
      value: yup.string().notRequired(),
      name: yup.string().notRequired(),
    })
    .notRequired(),
})
export type QuickbooksEstimateBaseActionStepInput = yup.InferType<
  typeof quickbooksEstimateBaseActionStepInputSchema
>

/** Action step input schema for Quickbooks Create Estimate */
export const quickbooksCreateEstimateActionStepInputSchema =
  quickbooksEstimateBaseActionStepInputSchema

/** Quickbooks Create Estimate Request - Individual Line Item (API shape, SalesItemLineDetail only) */
const quickbooksEstimateLineItemSchema = yup.object({
  DetailType: yup.string().required().oneOf(['SalesItemLineDetail']),
  Amount: yup.number().required(),
  SalesItemLineDetail: yup
    .object({
      ItemRef: yup
        .object({
          value: yup.string().required(),
          name: yup.string().notRequired(),
        })
        .required(),
      Qty: yup.number().optional(),
      UnitPrice: yup.number().optional(),
      TaxCodeRef: yup
        .object({
          value: yup.string().notRequired(),
          name: yup.string().notRequired(),
        })
        .optional(),
      DiscountRate: yup.number().optional(),
      DiscountAmt: yup.number().optional(),
      TaxInclusiveAmt: yup.number().optional(),
      ServiceDate: yup.string().optional(),
      ClassRef: yup
        .object({
          value: yup.string().notRequired(),
          name: yup.string().notRequired(),
        })
        .optional(),
    })
    .required()
    .strict(),
  Description: yup.string().optional(),
  LineNum: yup.number().optional(),
  CustomExtensions: yup.array().of(quickbooksCustomExtensionSchema).optional(),
})
export type QuickbooksEstimateLineItem = yup.InferType<typeof quickbooksEstimateLineItemSchema>

/** Quickbooks Create Estimate Request Schema - Base */
export const quickbooksEstimateBaseObjectSchema = yup.object({
  CustomerRef: yup
    .object({
      value: yup.string().required(),
      name: yup.string().notRequired(),
    })
    .required(),
  Line: yup.array().of(quickbooksEstimateLineItemSchema).required().min(1),
  CurrencyRef: yup
    .object({
      name: yup.string().notRequired(),
      value: yup.string().notRequired(),
    })
    .notRequired(),
  BillEmail: yup.object({ Address: yup.string().email().optional() }).optional(),
  CustomerMemo: yup.object({ value: yup.string().optional() }).optional(),
  PrintStatus: yup.string().oneOf(['NeedToPrint', 'NotSet', 'PrintComplete']).optional(),
  EmailStatus: yup.string().oneOf(['NeedToSend', 'NotSet']).optional(),
  ApplyTaxAfterDiscount: yup.boolean().optional(),
  TxnTaxDetail: yup.object({ TotalTax: yup.number().optional() }).optional(),
  BillAddr: quickbooksPhysicalAddressSchema.notRequired(),
  ShipAddr: quickbooksPhysicalAddressSchema.notRequired(),
  ProjectRef: yup
    .object({
      value: yup.string().notRequired(),
      name: yup.string().notRequired(),
    })
    .notRequired(),
})
export type QuickbooksEstimateBaseObject = yup.InferType<typeof quickbooksEstimateBaseObjectSchema>

/** Quickbooks Estimate Schema for query and sparse update (Id, SyncToken, DocNumber, CustomerRef). */
export const quickbooksEstimateSchema = yup.object({
  Id: yup.string().required(),
  SyncToken: yup.string().required(),
  DocNumber: yup.string().optional().nullable(),
  CustomerRef: yup
    .object({
      value: yup.string().notRequired(),
      name: yup.string().notRequired(),
    })
    .notRequired(),
})
export type QuickbooksEstimate = yup.InferType<typeof quickbooksEstimateSchema>

/** Quickbooks Create Estimate Response Schema */
export const quickbooksCreateEstimateResponseSchema = yup.object({
  Estimate: quickbooksEstimateSchema.required(),
})
export type QuickbooksCreateEstimateResponse = yup.InferType<
  typeof quickbooksCreateEstimateResponseSchema
>

/** Quickbooks Query Estimate Response Schema */
export const quickbooksQueryEstimateResponseSchema = yup.object({
  QueryResponse: yup
    .object({
      Estimate: yup.array().of(quickbooksEstimateSchema).optional(),
    })
    .required(),
})
export type QuickbooksQueryEstimateResponse = yup.InferType<
  typeof quickbooksQueryEstimateResponseSchema
>

/** Base schema for Quickbooks update estimate action step input (CustomerRef and Line optional for sparse update). */
const quickbooksEstimateUpdateBaseActionStepInputSchema = yup.object({
  QuickbooksEstimateId: yup.string().notRequired(),
  DocNumber: yup.string().notRequired(),
  CustomerRef: yup
    .object({
      value: yup.string().notRequired(),
      name: yup.string().notRequired(),
    })
    .notRequired(),
  Customer: quickbooksCreateCustomerActionStepInputSchema.notRequired(),
  Line: yup.array().of(quickbooksEstimateLineItemActionStepInputSchema).notRequired(),
  CurrencyRef: yup
    .object({
      name: yup.string().notRequired(),
      value: yup.string().notRequired(),
    })
    .notRequired(),
  BillEmail: yup
    .object({
      Address: yup.string().email().notRequired(),
    })
    .notRequired(),
  CustomerMemo: yup
    .object({
      value: yup.string().notRequired(),
    })
    .notRequired(),
  PrintStatus: yup.string().oneOf(['NeedToPrint', 'NotSet', 'PrintComplete']).notRequired(),
  EmailStatus: yup.string().oneOf(['NeedToSend', 'NotSet']).notRequired(),
  ApplyTaxAfterDiscount: yup.boolean().notRequired(),
  TxnTaxDetail: yup
    .object({
      TotalTax: yup.number().notRequired(),
    })
    .notRequired(),
  BillAddr: quickbooksPhysicalAddressSchema.notRequired(),
  ShipAddr: quickbooksPhysicalAddressSchema.notRequired(),
  ProjectRef: yup
    .object({
      value: yup.string().notRequired(),
      name: yup.string().notRequired(),
    })
    .notRequired(),
  TxnDate: yup.string().notRequired(),
  DueDate: yup.string().notRequired(),
  ExpirationDate: yup.string().notRequired(),
  TxnStatus: yup
    .string()
    .oneOf(['Accepted', 'Closed', 'Pending', 'Rejected', 'Converted'])
    .notRequired(),
  PrivateNote: yup.string().max(4000).notRequired(),
  ShipDate: yup.string().notRequired(),
  SalesTermRef: yup
    .object({
      value: yup.string().notRequired(),
      name: yup.string().notRequired(),
    })
    .notRequired(),
  ShipMethodRef: yup
    .object({
      value: yup.string().notRequired(),
      name: yup.string().notRequired(),
    })
    .notRequired(),
  ClassRef: yup
    .object({
      value: yup.string().notRequired(),
      name: yup.string().notRequired(),
    })
    .notRequired(),
  DepartmentRef: yup
    .object({
      value: yup.string().notRequired(),
      name: yup.string().notRequired(),
    })
    .notRequired(),
})
export const quickbooksUpdateEstimateActionStepInputSchema =
  quickbooksEstimateUpdateBaseActionStepInputSchema
export type QuickbooksUpdateEstimateActionStepInput = yup.InferType<
  typeof quickbooksUpdateEstimateActionStepInputSchema
>

/** Quickbooks Update Estimate Request Schema (sparse update). Id, SyncToken, sparse, CustomerRef.value required. */
export const quickbooksUpdateEstimateRequestSchema = yup.object({
  Id: yup.string().required(),
  SyncToken: yup.string().required(),
  sparse: yup.boolean().oneOf([true]).required(),
  CustomerRef: yup
    .object({
      value: yup.string().required(),
      name: yup.string().notRequired(),
    })
    .required(),
  Line: yup.array().of(quickbooksEstimateLineItemSchema).optional(),
  CurrencyRef: yup
    .object({
      name: yup.string().notRequired(),
      value: yup.string().notRequired(),
    })
    .notRequired(),
  BillEmail: yup.object({ Address: yup.string().email().optional() }).optional(),
  CustomerMemo: yup.object({ value: yup.string().optional() }).optional(),
  PrintStatus: yup.string().oneOf(['NeedToPrint', 'NotSet', 'PrintComplete']).optional(),
  EmailStatus: yup.string().oneOf(['NeedToSend', 'NotSet']).optional(),
  ApplyTaxAfterDiscount: yup.boolean().optional(),
  TxnTaxDetail: yup.object({ TotalTax: yup.number().optional() }).optional(),
  BillAddr: quickbooksPhysicalAddressSchema.notRequired(),
  ShipAddr: quickbooksPhysicalAddressSchema.notRequired(),
  ProjectRef: yup
    .object({
      value: yup.string().notRequired(),
      name: yup.string().notRequired(),
    })
    .notRequired(),
  TxnDate: yup.string().notRequired(),
  DueDate: yup.string().notRequired(),
  ExpirationDate: yup.string().notRequired(),
  TxnStatus: yup
    .string()
    .oneOf(['Accepted', 'Closed', 'Pending', 'Rejected', 'Converted'])
    .notRequired(),
  DocNumber: yup.string().notRequired(),
  PrivateNote: yup.string().max(4000).notRequired(),
  ShipDate: yup.string().notRequired(),
  SalesTermRef: yup
    .object({
      value: yup.string().notRequired(),
      name: yup.string().notRequired(),
    })
    .notRequired(),
  ShipMethodRef: yup
    .object({
      value: yup.string().notRequired(),
      name: yup.string().notRequired(),
    })
    .notRequired(),
  ClassRef: yup
    .object({
      value: yup.string().notRequired(),
      name: yup.string().notRequired(),
    })
    .notRequired(),
  DepartmentRef: yup
    .object({
      value: yup.string().notRequired(),
      name: yup.string().notRequired(),
    })
    .notRequired(),
})
export type QuickbooksUpdateEstimateRequest = yup.InferType<
  typeof quickbooksUpdateEstimateRequestSchema
>

// ACCOUNT ------------------------------------------------------------------------------------------

/** Action step input schema for account metadata as a part of Quickbooks purchase order create/update */
export const quickbooksGetOrCreateAccountMetadataActionStepInputSchema = yup.object({
  Name: yup.string().notRequired(),
  AcctNum: yup.string().notRequired(),
})
export type QuickbooksGetOrCreateAccountMetadataActionStepInput = yup.InferType<
  typeof quickbooksGetOrCreateAccountMetadataActionStepInputSchema
>

/** Quickbooks Create Account Request Schema - used in purchase order creation/update and standalone account creation */
export const quickbooksCreateAccountRequestSchema = yup.object({
  Name: yup.string().required(),
  AccountType: yup.string().required(),
  AcctNum: yup.string().notRequired(),
  AccountSubType: yup.string().notRequired(),
  Description: yup.string().notRequired(),
  CurrencyRef: yup
    .object({
      value: yup.string().notRequired(),
      name: yup.string().notRequired(),
    })
    .notRequired(),
  ParentRef: yup
    .object({
      value: yup.string().notRequired(),
      name: yup.string().notRequired(),
    })
    .notRequired(),
})
export type QuickbooksCreateAccountRequest = yup.InferType<
  typeof quickbooksCreateAccountRequestSchema
>

/** Quickbooks Account Schema */
export const quickbooksAccountSchema = yup.object({
  Id: yup.string().required(),
  Name: yup.string().required(),
  FullyQualifiedName: yup.string().notRequired(),
  AcctNum: yup.string().notRequired(),
  Classification: yup.string().notRequired(),
  AccountType: yup.string().notRequired(),
  AccountSubType: yup.string().notRequired(),
  CurrentBalance: yup.number().notRequired(),
  CurrentBalanceWithSubAccounts: yup.number().notRequired(),
  Active: yup.boolean().notRequired(),
  SyncToken: yup.string().notRequired(),
  SubAccount: yup.boolean().notRequired(),
  CurrencyRef: yup
    .object({
      name: yup.string().notRequired(),
      value: yup.string().notRequired(),
    })
    .notRequired(),
  MetaData: yup
    .object({
      CreateTime: yup.string().notRequired(),
      LastUpdatedTime: yup.string().notRequired(),
    })
    .notRequired(),
})
export type QuickbooksAccount = yup.InferType<typeof quickbooksAccountSchema>

/** Quickbooks Create Account Response Schema */
export const quickbooksCreateAccountResponseSchema = yup.object({
  Account: quickbooksAccountSchema.required(),
})
export type QuickbooksCreateAccountResponse = yup.InferType<
  typeof quickbooksCreateAccountResponseSchema
>

/** Action step input schema for Quickbooks Create Account (standalone) */
export const quickbooksCreateAccountActionStepInputSchema = yup.object({
  Name: yup.string().required(),
  AccountType: yup.string().required(),
  AccountSubType: yup.string().notRequired(),
  AcctNum: yup.string().notRequired(),
  Description: yup.string().notRequired(),
  CurrencyRef: yup
    .object({
      value: yup.string().notRequired(),
      name: yup.string().notRequired(),
    })
    .notRequired(),
  createIfNotExists: yup.boolean().notRequired(),
})
export type QuickbooksCreateAccountActionStepInput = yup.InferType<
  typeof quickbooksCreateAccountActionStepInputSchema
>

/** Action step input schema for Quickbooks Update Account */
export const quickbooksUpdateAccountActionStepInputSchema = yup.object({
  Id: yup.string().required(),
  SyncToken: yup.string().required(),
  Name: yup.string().required(),
  AccountType: yup.string().required(),
  AccountSubType: yup.string().notRequired(),
  AcctNum: yup.string().notRequired(),
  Description: yup.string().notRequired(),
  CurrencyRef: yup
    .object({
      value: yup.string().notRequired(),
      name: yup.string().notRequired(),
    })
    .notRequired(),
  sparse: yup.boolean().notRequired(),
})
export type QuickbooksUpdateAccountActionStepInput = yup.InferType<
  typeof quickbooksUpdateAccountActionStepInputSchema
>

/** Quickbooks Query Account Response Schema */
export const quickbooksQueryAccountResponseSchema = yup.object({
  QueryResponse: yup
    .object({
      Account: yup.array().of(quickbooksAccountSchema).optional(),
    })
    .required(),
})
export type QuickbooksQueryAccountResponse = yup.InferType<
  typeof quickbooksQueryAccountResponseSchema
>

/** Quickbooks Get Single Account by ID Response Schema */
export const quickbooksGetAccountByIdResponseSchema = yup.object({
  Account: quickbooksAccountSchema.required(),
})

// BUDGET ------------------------------------------------------------------------------------------
// Entity shape matches Intuit finance v3 XSD (Budget, BudgetDetail, BudgetEntryTypeEnum, BudgetTypeEnum).
// @see https://static.developer.intuit.com/sdkdocs/qbv3doc/ipp-v3-java-devkit-javadoc/com/intuit/ipp/data/Budget.html

/** BudgetEntryTypeEnum — xsd string enumeration on Budget */
export const QUICKBOOKS_BUDGET_ENTRY_TYPE_VALUES = ['Yearly', 'Quarterly', 'Monthly'] as const

/** BudgetTypeEnum — xsd string enumeration on Budget */
export const QUICKBOOKS_BUDGET_TYPE_VALUES = ['ProfitAndLoss', 'BalanceSheet'] as const

export type QuickbooksBudgetEntryType = (typeof QUICKBOOKS_BUDGET_ENTRY_TYPE_VALUES)[number]
export type QuickbooksBudgetType = (typeof QUICKBOOKS_BUDGET_TYPE_VALUES)[number]

export const quickbooksBudgetDetailSchema = yup
  .object({
    BudgetDate: yup.string().optional(),
    Amount: yup.number().optional(),
    AccountRef: quickbooksReferenceTypeValueRequiredSchema.optional(),
    CustomerRef: quickbooksReferenceTypeValueRequiredSchema.optional(),
    ClassRef: quickbooksReferenceTypeValueRequiredSchema.optional(),
    DepartmentRef: quickbooksReferenceTypeValueRequiredSchema.optional(),
  })
  .test(
    'budget-detail-account-with-measures',
    'AccountRef.value is required when Amount or BudgetDate is set on a budget detail line',
    (detail) => {
      if (!detail) {
        return true
      }
      const hasMeasures =
        detail.Amount !== undefined ||
        (typeof detail.BudgetDate === 'string' && detail.BudgetDate.trim() !== '')
      if (!hasMeasures) {
        return true
      }
      const accountValue = detail.AccountRef?.value
      return typeof accountValue === 'string' && accountValue.trim() !== ''
    },
  )

export type QuickbooksBudgetDetailLineInput = yup.InferType<typeof quickbooksBudgetDetailSchema>

const budgetDetailLinesRequireAccountAndDateTest = (
  lines: QuickbooksBudgetDetailLineInput[] | undefined,
): boolean => {
  if (lines === undefined || lines.length === 0) {
    return true
  }
  return lines.every((line) => {
    const refVal = line.AccountRef?.value
    const dateVal = line.BudgetDate
    return (
      typeof refVal === 'string' &&
      refVal.trim() !== '' &&
      typeof dateVal === 'string' &&
      dateVal.trim() !== ''
    )
  })
}

export const quickbooksBudgetSchema = yup.object({
  Id: yup.string().required(),
  SyncToken: yup.string().optional(),
  MetaData: quickbooksIntuitEntityMetaDataSchema.optional(),
  Name: yup.string().optional(),
  StartDate: yup.string().required(),
  EndDate: yup.string().required(),
  BudgetType: yup
    .string()
    .oneOf([...QUICKBOOKS_BUDGET_TYPE_VALUES])
    .optional(),
  BudgetEntryType: yup
    .string()
    .oneOf([...QUICKBOOKS_BUDGET_ENTRY_TYPE_VALUES])
    .optional(),
  Active: yup.boolean().optional(),
  BudgetDetail: yup.array().of(quickbooksBudgetDetailSchema).optional(),
  domain: yup.string().optional(),
  sparse: yup.boolean().optional(),
})
export type QuickbooksBudget = yup.InferType<typeof quickbooksBudgetSchema>

/** Budget returned from POST /budget (create, update, delete); delete may omit dates */
export const quickbooksBudgetMutationResponseBudgetSchema = yup.object({
  Id: yup.string().required(),
  SyncToken: yup.string().optional(),
  MetaData: quickbooksIntuitEntityMetaDataSchema.optional(),
  Name: yup.string().optional(),
  StartDate: yup.string().optional(),
  EndDate: yup.string().optional(),
  BudgetType: yup
    .string()
    .oneOf([...QUICKBOOKS_BUDGET_TYPE_VALUES])
    .optional(),
  BudgetEntryType: yup
    .string()
    .oneOf([...QUICKBOOKS_BUDGET_ENTRY_TYPE_VALUES])
    .optional(),
  Active: yup.boolean().optional(),
  BudgetDetail: yup.array().of(quickbooksBudgetDetailSchema).optional(),
  domain: yup.string().optional(),
  sparse: yup.boolean().optional(),
  status: yup.string().optional(),
})
export type QuickbooksBudgetMutationResponseBudget = yup.InferType<
  typeof quickbooksBudgetMutationResponseBudgetSchema
>

export const quickbooksCreateBudgetActionStepInputSchema = yup.object({
  Name: yup.string().optional(),
  StartDate: yup.string().required(),
  EndDate: yup.string().required(),
  BudgetType: yup
    .string()
    .oneOf([...QUICKBOOKS_BUDGET_TYPE_VALUES])
    .optional(),
  BudgetEntryType: yup
    .string()
    .oneOf([...QUICKBOOKS_BUDGET_ENTRY_TYPE_VALUES])
    .optional(),
  Active: yup.boolean().optional(),
  BudgetDetail: yup
    .array()
    .of(quickbooksBudgetDetailSchema)
    .optional()
    .test(
      'budget-detail-lines-complete',
      'Each BudgetDetail line must include AccountRef.value and BudgetDate',
      budgetDetailLinesRequireAccountAndDateTest,
    ),
})
export type QuickbooksCreateBudgetActionStepInput = yup.InferType<
  typeof quickbooksCreateBudgetActionStepInputSchema
>

export const quickbooksUpdateBudgetActionStepInputSchema = yup
  .object({
    Id: yup.string().required(),
    SyncToken: yup.string().required(),
    sparse: yup.boolean().optional(),
  })
  .concat(quickbooksCreateBudgetActionStepInputSchema)
export type QuickbooksUpdateBudgetActionStepInput = yup.InferType<
  typeof quickbooksUpdateBudgetActionStepInputSchema
>

export const quickbooksDeleteBudgetActionStepInputSchema = yup.object({
  Id: yup.string().required(),
  SyncToken: yup.string().required(),
})
export type QuickbooksDeleteBudgetActionStepInput = yup.InferType<
  typeof quickbooksDeleteBudgetActionStepInputSchema
>

export const quickbooksCreateUpdateBudgetResponseSchema = yup.object({
  Budget: quickbooksBudgetMutationResponseBudgetSchema.required(),
})
export type QuickbooksCreateUpdateBudgetResponse = yup.InferType<
  typeof quickbooksCreateUpdateBudgetResponseSchema
>

export const quickbooksGetBudgetByIdResponseSchema = yup.object({
  Budget: quickbooksBudgetSchema.required(),
})

// VENDOR ------------------------------------------------------------------------------------------

/** Action step input schema for vendor as part of Quickbooks purchase order create/update */
export const quickbooksGetOrCreateVendorActionStepInputSchema = yup.object({
  name: yup.string().notRequired(),
  value: yup.string().notRequired(),
})
export type QuickbooksGetOrCreateVendorActionStepInput = yup.InferType<
  typeof quickbooksGetOrCreateVendorActionStepInputSchema
>

/** Quickbooks Create Vendor Request Schema - used in purchase order creation/update (minimal: DisplayName only) */
export const quickbooksCreateVendorRequestSchema = yup.object({
  DisplayName: yup.string().required(),
})
export type QuickbooksCreateVendorRequest = yup.InferType<
  typeof quickbooksCreateVendorRequestSchema
>

/** Bill address sub-schema for Quickbooks Create Vendor action step */
const quickbooksCreateVendorBillAddrSchema = yup.object({
  Line1: yup.string().notRequired(),
  Line2: yup.string().notRequired(),
  Line3: yup.string().notRequired(),
  City: yup.string().notRequired(),
  PostalCode: yup.string().notRequired(),
  CountrySubDivisionCode: yup.string().notRequired(),
  Country: yup.string().notRequired(),
})

/** Action step input schema for Quickbooks Create Vendor (full vendor create endpoint) */
export const quickbooksCreateVendorActionStepInputSchema = yup
  .object({
    DisplayName: yup.string().max(500).notRequired(),
    Title: yup.string().max(16).notRequired(),
    GivenName: yup.string().max(100).notRequired(),
    MiddleName: yup.string().max(100).notRequired(),
    FamilyName: yup.string().max(100).notRequired(),
    Suffix: yup.string().max(16).notRequired(),
    CompanyName: yup.string().notRequired(),
    PrimaryEmailAddress: yup.string().email().notRequired(),
    WebAddress: yup.string().notRequired(),
    PrimaryPhone: yup.string().notRequired(),
    Mobile: yup.string().notRequired(),
    TaxIdentifier: yup.string().notRequired(),
    AcctNum: yup.string().notRequired(),
    BillAddr: quickbooksCreateVendorBillAddrSchema.notRequired(),
    PrintOnCheckName: yup.string().notRequired(),
    Vendor1099: yup.boolean().notRequired(),
    Active: yup.boolean().notRequired(),
    TermRef: yup
      .object({ value: yup.string().required(), name: yup.string().optional() })
      .notRequired(),
  })
  .test(
    'at-least-one-name',
    'DisplayName or at least one of Title, GivenName, MiddleName, FamilyName, Suffix is required when creating a vendor',
    (value) =>
      value.DisplayName !== undefined ||
      value.Title !== undefined ||
      value.GivenName !== undefined ||
      value.MiddleName !== undefined ||
      value.FamilyName !== undefined ||
      value.Suffix !== undefined,
  )
export type QuickbooksCreateVendorActionStepInput = yup.InferType<
  typeof quickbooksCreateVendorActionStepInputSchema
>

/** Quickbooks Create Vendor full request schema (API request body for POST /vendor) */
export const quickbooksCreateVendorFullRequestSchema = yup
  .object({
    DisplayName: yup.string().max(500).optional(),
    Title: yup.string().max(16).optional(),
    GivenName: yup.string().max(100).optional(),
    MiddleName: yup.string().max(100).optional(),
    FamilyName: yup.string().max(100).optional(),
    Suffix: yup.string().max(16).optional(),
    CompanyName: yup.string().optional(),
    PrimaryEmailAddr: yup.object({ Address: yup.string().email().optional() }).optional(),
    WebAddr: yup.object({ URI: yup.string().optional() }).optional(),
    PrimaryPhone: yup.object({ FreeFormNumber: yup.string().optional() }).optional(),
    Mobile: yup.object({ FreeFormNumber: yup.string().optional() }).optional(),
    TaxIdentifier: yup.string().optional(),
    AcctNum: yup.string().optional(),
    BillAddr: quickbooksCreateVendorBillAddrSchema.optional(),
    PrintOnCheckName: yup.string().optional(),
    Vendor1099: yup.boolean().optional(),
    Active: yup.boolean().optional(),
    TermRef: yup
      .object({ value: yup.string().required(), name: yup.string().optional() })
      .optional(),
  })
  .test(
    'at-least-one-name',
    'DisplayName or at least one of Title, GivenName, MiddleName, FamilyName, Suffix is required when creating a vendor',
    (value) =>
      value.DisplayName !== undefined ||
      value.Title !== undefined ||
      value.GivenName !== undefined ||
      value.MiddleName !== undefined ||
      value.FamilyName !== undefined ||
      value.Suffix !== undefined,
  )
export type QuickbooksCreateVendorFullRequest = yup.InferType<
  typeof quickbooksCreateVendorFullRequestSchema
>

/** Quickbooks Vendor Schema (incomplete - only incuding core fields) */
export const quickbooksVendorSchema = yup.object({
  Id: yup.string().required(),
  DisplayName: yup.string().required(),
  SyncToken: yup.string().optional().nullable(),
  Title: yup.string().optional().nullable(),
  GivenName: yup.string().optional().nullable(),
  MiddleName: yup.string().optional().nullable(),
  FamilyName: yup.string().optional().nullable(),
  Suffix: yup.string().optional().nullable(),
  CompanyName: yup.string().optional().nullable(),
  PrintOnCheckName: yup.string().optional().nullable(),
  AcctNum: yup.string().optional().nullable(),
  Active: yup.boolean().optional(),
  Vendor1099: yup.boolean().optional(),
  T4AEligible: yup.boolean().optional(),
  T5018Eligible: yup.boolean().optional(),
  HasTPAR: yup.boolean().optional(),
  Balance: yup.number().optional().nullable(),
  CostRate: yup.number().optional().nullable(),
  BillRate: yup.number().optional().nullable(),
  TaxIdentifier: yup.string().optional().nullable(),
  GSTIN: yup.string().optional().nullable(),
  GSTRegistrationType: yup.string().optional().nullable(),
  BusinessNumber: yup.string().optional().nullable(),
  TaxReportingBasis: yup.string().optional().nullable(),
  Source: yup.string().optional().nullable(),
  PrimaryEmailAddr: yup
    .object({ Address: yup.string().optional().nullable() })
    .optional()
    .nullable(),
  PrimaryPhone: yup
    .object({ FreeFormNumber: yup.string().optional().nullable() })
    .optional()
    .nullable(),
  AlternatePhone: yup
    .object({ FreeFormNumber: yup.string().optional().nullable() })
    .optional()
    .nullable(),
  Mobile: yup.object({ FreeFormNumber: yup.string().optional().nullable() }).optional().nullable(),
  Fax: yup.object({ FreeFormNumber: yup.string().optional().nullable() }).optional().nullable(),
  WebAddr: yup.object({ URI: yup.string().optional().nullable() }).optional().nullable(),
  BillAddr: yup
    .object({
      Id: yup.string().optional().nullable(),
      Line1: yup.string().optional().nullable(),
      Line2: yup.string().optional().nullable(),
      Line3: yup.string().optional().nullable(),
      Line4: yup.string().optional().nullable(),
      Line5: yup.string().optional().nullable(),
      City: yup.string().optional().nullable(),
      Country: yup.string().optional().nullable(),
      CountrySubDivisionCode: yup.string().optional().nullable(),
      PostalCode: yup.string().optional().nullable(),
      Lat: yup.string().optional().nullable(),
      Long: yup.string().optional().nullable(),
    })
    .optional()
    .nullable(),
  OtherContactInfo: yup
    .object({
      Type: yup.string().optional().nullable(),
      Telephone: yup
        .object({ FreeFormNumber: yup.string().optional().nullable() })
        .optional()
        .nullable(),
    })
    .optional()
    .nullable(),
  VendorPaymentBankDetail: yup
    .object({
      BankAccountName: yup.string().optional().nullable(),
      BankBranchIdentifier: yup.string().optional().nullable(),
      BankAccountNumber: yup.string().optional().nullable(),
      StatementText: yup.string().optional().nullable(),
    })
    .optional()
    .nullable(),
  APAccountRef: yup
    .object({ value: yup.string().required(), name: yup.string().optional().nullable() })
    .optional()
    .nullable(),
  TermRef: yup
    .object({ value: yup.string().required(), name: yup.string().optional().nullable() })
    .optional()
    .nullable(),
  CurrencyRef: yup
    .object({ value: yup.string().optional(), name: yup.string().optional().nullable() })
    .optional()
    .nullable(),
  MetaData: yup
    .object({
      CreateTime: yup.string().optional().nullable(),
      LastUpdatedTime: yup.string().optional().nullable(),
    })
    .optional()
    .nullable(),
})
export type QuickbooksVendor = yup.InferType<typeof quickbooksVendorSchema>

/** Quickbooks Create Vendor Response Schema - (incomplete - only including core fields) */
export const quickbooksCreateVendorResponseSchema = yup.object({
  Vendor: quickbooksVendorSchema.required(),
})
export type QuickbooksCreateVendorResponse = yup.InferType<
  typeof quickbooksCreateVendorResponseSchema
>

/** Quickbooks Query Vendor Response Schema */
export const quickbooksQueryVendorResponseSchema = yup.object({
  QueryResponse: yup
    .object({
      Vendor: yup.array().of(quickbooksVendorSchema).optional(),
    })
    .required(),
})
export type QuickbooksQueryVendorResponse = yup.InferType<
  typeof quickbooksQueryVendorResponseSchema
>

/** Quickbooks Get Single Vendor by ID Response Schema */
export const quickbooksGetVendorByIdResponseSchema = yup.object({
  Vendor: quickbooksVendorSchema.required(),
})

/** Action step input schema for Quickbooks Update Vendor */
export const quickbooksUpdateVendorActionStepInputSchema = yup.object({
  Id: yup.string().required(),
  SyncToken: yup.string().required(),
  DisplayName: yup.string().max(500).notRequired(),
  Title: yup.string().max(16).notRequired(),
  GivenName: yup.string().max(100).notRequired(),
  MiddleName: yup.string().max(100).notRequired(),
  FamilyName: yup.string().max(100).notRequired(),
  Suffix: yup.string().max(16).notRequired(),
  CompanyName: yup.string().notRequired(),
  PrimaryEmailAddress: yup.string().email().notRequired(),
  WebAddress: yup.string().notRequired(),
  PrimaryPhone: yup.string().notRequired(),
  Mobile: yup.string().notRequired(),
  TaxIdentifier: yup.string().notRequired(),
  AcctNum: yup.string().notRequired(),
  BillAddr: quickbooksCreateVendorBillAddrSchema.notRequired(),
  PrintOnCheckName: yup.string().notRequired(),
  sparse: yup.boolean().notRequired(),
  Vendor1099: yup.boolean().notRequired(),
  Active: yup.boolean().notRequired(),
  TermRef: yup
    .object({ value: yup.string().required(), name: yup.string().optional() })
    .notRequired(),
})
export type QuickbooksUpdateVendorActionStepInput = yup.InferType<
  typeof quickbooksUpdateVendorActionStepInputSchema
>

/** Quickbooks Update Vendor full request schema (API request body for POST /vendor update) */
export const quickbooksUpdateVendorFullRequestSchema = yup.object({
  Id: yup.string().required(),
  SyncToken: yup.string().required(),
  DisplayName: yup.string().max(500).optional(),
  Title: yup.string().max(16).optional(),
  GivenName: yup.string().max(100).optional(),
  MiddleName: yup.string().max(100).optional(),
  FamilyName: yup.string().max(100).optional(),
  Suffix: yup.string().max(16).optional(),
  CompanyName: yup.string().optional(),
  PrimaryEmailAddr: yup.object({ Address: yup.string().email().optional() }).optional(),
  WebAddr: yup.object({ URI: yup.string().optional() }).optional(),
  PrimaryPhone: yup.object({ FreeFormNumber: yup.string().optional() }).optional(),
  Mobile: yup.object({ FreeFormNumber: yup.string().optional() }).optional(),
  TaxIdentifier: yup.string().optional(),
  AcctNum: yup.string().optional(),
  BillAddr: quickbooksCreateVendorBillAddrSchema.optional(),
  PrintOnCheckName: yup.string().optional(),
  sparse: yup.boolean().optional(),
  Vendor1099: yup.boolean().optional(),
  Active: yup.boolean().optional(),
  TermRef: yup.object({ value: yup.string().required(), name: yup.string().optional() }).optional(),
})
export type QuickbooksUpdateVendorFullRequest = yup.InferType<
  typeof quickbooksUpdateVendorFullRequestSchema
>

// BILL ------------------------------------------------------------------------------------------

/** Action step input schema of a Quickbooks expense item line detail */
const quickbooksAccountExpenseLineDetailActionStepInputSchema = yup
  .object({
    AccountRef: yup
      .object({
        value: yup.string().required(),
        name: yup.string().optional(),
      })
      .required(),
  })
  .strict()

/** Action step input schema of a Quickbooks bill line item */
const quickbooksBillLineItemActionStepInputSchema = yup.object({
  DetailType: yup.string().oneOf(['AccountBasedExpenseLineDetail']).required(),
  Amount: yup.number().required(),
  LineNum: yup.number().optional(),
  Description: yup.string().optional(),
  CustomExtensions: yup.array().of(quickbooksCustomExtensionSchema).optional(),
  AccountBasedExpenseLineDetail: quickbooksAccountExpenseLineDetailActionStepInputSchema.required(),
})
export type QuickbooksBillLineItemActionStepInput = yup.InferType<
  typeof quickbooksBillLineItemActionStepInputSchema
>

/** Base schema for Quickbooks create/update bill action step input */
export const quickbooksBillBaseActionStepInputSchema = yup.object({
  SalesTermRef: yup
    .object({
      name: yup.string().notRequired(),
      value: yup.string().notRequired(),
    })
    .notRequired(),
  DocNumber: yup.string().optional(),
  TxnDate: yup.string().optional().nullable(),
  DueDate: yup.string().optional().nullable(),
  PrivateNote: yup.string().optional(),
  VendorRef: quickbooksGetOrCreateVendorActionStepInputSchema.required(),
  CurrencyRef: yup
    .object({
      name: yup.string().notRequired(),
      value: yup.string().notRequired(),
    })
    .notRequired()
    .strict(),
  Line: yup
    .array()
    .of(quickbooksBillLineItemActionStepInputSchema)
    .min(1, 'At least one line item is required')
    .required(),
  Attachments: yup.array().of(yup.string().notRequired()).notRequired(),
})

export type QuickbooksBillBaseActionStepInput = yup.InferType<
  typeof quickbooksBillBaseActionStepInputSchema
>

/** Action step input schema for Quickbooks Create Bill */
export const quickbooksCreateBillActionStepInputSchema = quickbooksBillBaseActionStepInputSchema

// VENDOR CREDIT (CREATE) --------------------------------------------------------------------------------
/**
 * Account-based expense line detail for vendor credit create.
 * Requires `AccountRef`; other QBO fields (TaxCodeRef, CustomerRef, BillableStatus, …) pass through.
 * @see https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/vendorcredit#create-a-vendorcredit
 */
const quickbooksVendorCreditAccountExpenseLineDetailActionStepInputSchema = yup
  .object({
    AccountRef: yup
      .object({
        value: yup.string().required(),
        name: yup.string().optional(),
      })
      .required(),
  })
  .strip(false)

/** Line item for QuickBooks create vendor credit (account-based expense lines). */
export const quickbooksVendorCreditCreateLineItemActionStepInputSchema = yup.object({
  DetailType: yup.string().oneOf(['AccountBasedExpenseLineDetail']).required(),
  Amount: yup.number().required(),
  Id: yup.string().optional(),
  LineNum: yup.number().optional(),
  Description: yup.string().optional(),
  CustomExtensions: yup.array().of(quickbooksCustomExtensionSchema).optional(),
  ProjectRef: yup
    .object({
      value: yup.string().required(),
      name: yup.string().notRequired(),
    })
    .notRequired(),
  AccountBasedExpenseLineDetail:
    quickbooksVendorCreditAccountExpenseLineDetailActionStepInputSchema.required(),
})
export type QuickbooksVendorCreditCreateLineItemActionStepInput = yup.InferType<
  typeof quickbooksVendorCreditCreateLineItemActionStepInputSchema
>

/**
 * Action step input for QuickBooks Create Vendor Credit (`POST /vendorcredit`).
 * Minimum per Intuit: `VendorRef` and at least one `Line` with `Amount`.
 */
export const quickbooksCreateVendorCreditActionStepInputSchema = yup.object({
  VendorRef: quickbooksGetOrCreateVendorActionStepInputSchema.required(),
  Line: yup
    .array()
    .of(quickbooksVendorCreditCreateLineItemActionStepInputSchema)
    .min(1, 'At least one line item is required')
    .required(),
  TxnDate: yup.string().optional().nullable(),
  TotalAmt: yup.number().optional().nullable(),
  APAccountRef: yup
    .object({
      value: yup.string().notRequired(),
      name: yup.string().notRequired(),
    })
    .notRequired(),
  CurrencyRef: yup
    .object({
      name: yup.string().notRequired(),
      value: yup.string().notRequired(),
    })
    .notRequired()
    .strict(),
  PrivateNote: yup.string().optional().nullable(),
})
export type QuickbooksCreateVendorCreditActionStepInput = yup.InferType<
  typeof quickbooksCreateVendorCreditActionStepInputSchema
>

/**
 * Action step input for QuickBooks full update Vendor Credit (`POST /vendorcredit` with `Id` and
 * `SyncToken` in the body). Writable fields omitted from the request are cleared per Intuit full-update semantics.
 *
 * @see https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/vendorcredit#full-update-a-vendorcredit
 */
export const quickbooksUpdateVendorCreditActionStepInputSchema = yup
  .object({
    Id: yup.string().required('Id is required for update'),
    SyncToken: yup.string().required('SyncToken is required for update'),
    sparse: yup.boolean().optional(),
  })
  .concat(quickbooksCreateVendorCreditActionStepInputSchema)
export type QuickbooksUpdateVendorCreditActionStepInput = yup.InferType<
  typeof quickbooksUpdateVendorCreditActionStepInputSchema
>

/** Delete vendor credit — POST /vendorcredit?operation=delete with flat `Id` + `SyncToken` (per QBO samples). */
export const quickbooksDeleteVendorCreditActionStepInputSchema = yup.object({
  Id: yup.string().required(),
  SyncToken: yup.string().required(),
})
export type QuickbooksDeleteVendorCreditActionStepInput = yup.InferType<
  typeof quickbooksDeleteVendorCreditActionStepInputSchema
>

/** Action step input schema for Quickbooks Update Bill */
export const quickbooksUpdateBillActionStepInputSchema = yup
  .object({
    Id: yup.string().required('Id is required for update'),
    SyncToken: yup.string().required('SyncToken is required for update'),
    sparse: yup.boolean().optional(),
  })
  .concat(quickbooksBillBaseActionStepInputSchema)
export type QuickbooksUpdateBillActionStepInput = yup.InferType<
  typeof quickbooksUpdateBillActionStepInputSchema
>

/** Action step input schema for Quickbooks Delete Bill */
export const quickbooksDeleteBillActionStepInputSchema = yup.object({
  Id: yup.string().required('Id is required for delete'),
  SyncToken: yup.string().required('SyncToken is required for delete'),
})
export type QuickbooksDeleteBillActionStepInput = yup.InferType<
  typeof quickbooksDeleteBillActionStepInputSchema
>

/** Quickbooks Delete Bill Response Schema */
export const quickbooksDeleteBillResponseSchema = yup.object({
  Bill: yup
    .object({
      status: yup.string().required(),
      domain: yup.string().required(),
      Id: yup.string().required(),
    })
    .required(),
  time: yup.string().required(),
})
export type QuickbooksDeleteBillResponse = yup.InferType<typeof quickbooksDeleteBillResponseSchema>

/**
 * Quickbooks Create Bill Request Schema - Individual Line Item Input
 *
 * Used to construct each line item after getting the item ID from Quickbooks
 */
const quickbooksBillLineItemSchema = yup.object({
  DetailType: yup.string().required().oneOf(['AccountBasedExpenseLineDetail']),
  AccountBasedExpenseLineDetail: quickbooksAccountExpenseLineDetailActionStepInputSchema.required(),
  Amount: yup.number().required(),
  Description: yup.string().optional(),
  LineNum: yup.number().optional(),
})
export type QuickbooksBillLineItem = yup.InferType<typeof quickbooksBillLineItemSchema>

/** Quickbooks Create/Update Bill Request Schema - Base */
const quickbooksBillBaseObjectSchema = quickbooksCreateBillActionStepInputSchema.shape({
  Line: yup.array().of(quickbooksBillLineItemSchema).required().min(1),
  CustomField: yup.array().of(quickbooksSetCustomFieldInputSchema).optional(),
})

export type QuickbooksBillBaseObject = yup.InferType<typeof quickbooksBillBaseObjectSchema>

/** Quickbooks Create Bill Request Schema */
export const quickbooksCreateBillRequestSchema = quickbooksBillBaseObjectSchema
export type QuickbooksCreateBillRequest = yup.InferType<typeof quickbooksCreateBillRequestSchema>

/** Quickbooks Bill Schema (incomplete, only including core fields) */
export const quickbooksBillSchema = yup.object({
  Id: yup.string().required(),
  SyncToken: yup.string().required(),
  domain: yup.string().oneOf(['QBO']).required(),
  sparse: yup.boolean().required(),
  TxnDate: yup.string().required(),
  DueDate: yup.string().required(),
  TotalAmt: yup.number().required(),
  Balance: yup.number().required(),
  VendorRef: yup
    .object({
      name: yup.string().required(),
      value: yup.string().required(),
    })
    .required(),
  APAccountRef: yup
    .object({
      name: yup.string().required(),
      value: yup.string().required(),
    })
    .required(),
  Line: yup
    .array()
    .of(
      yup.object({
        Id: yup.string().required(),
        DetailType: yup.string().oneOf(['AccountBasedExpenseLineDetail']).required(),
        Amount: yup.number().required(),
        AccountBasedExpenseLineDetail: yup
          .object({
            AccountRef: yup
              .object({
                name: yup.string().required(),
                value: yup.string().required(),
              })
              .required(),
            TaxCodeRef: yup
              .object({
                value: yup.string().required(),
              })
              .required(),
            BillableStatus: yup
              .string()
              .oneOf(['Billable', 'NotBillable', 'HasBeenBilled'])
              .required(),
          })
          .required(),
      }),
    )
    .min(1)
    .required(),
  MetaData: yup
    .object({
      CreateTime: yup.string().required(),
      LastUpdatedTime: yup.string().required(),
    })
    .required(),
})
export type QuickbooksBill = yup.InferType<typeof quickbooksBillSchema>

/** Quickbooks Create or Update Bill Response Schema */
export const quickbooksCreateUpdateBillResponseSchema = yup.object({
  Bill: quickbooksBillSchema.required(),
})
export type QuickbooksCreateUpdateBillResponse = yup.InferType<
  typeof quickbooksCreateUpdateBillResponseSchema
>

/** Quickbooks Get Single Bill by ID Response Schema */
export const quickbooksGetBillByIdResponseSchema = yup.object({
  Bill: quickbooksBillSchema.required(),
})

/** Quickbooks Query Bill Response Schema */
export const quickbooksQueryBillResponseSchema = yup.object({
  QueryResponse: yup
    .object({
      Bill: yup.array().of(quickbooksBillSchema).optional(),
    })
    .required(),
})
export type QuickbooksQueryBillResponse = yup.InferType<typeof quickbooksQueryBillResponseSchema>

// Local rather than the shared report cell schema below, which is still in its TDZ here.
const quickbooksCashFlowReportColDataSchema = yup
  .object({
    id: yup.string().optional(),
    value: yup.string().optional(),
    href: yup.string().optional(),
  })
  .unknown(true)

const quickbooksCashFlowReportColDataContainerSchema = yup
  .object({
    ColData: yup.array().of(quickbooksCashFlowReportColDataSchema).optional(),
  })
  .unknown(true)

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type -- circular lazy refs
const createQuickbooksCashFlowReportRowsSchema = () => {
  const rowSchemaHolder: { schema: yup.Lazy<unknown> } = {
    schema: yup.lazy(() => yup.mixed().optional()),
  }

  const rowsContainerSchema = yup
    .object({
      Row: yup.lazy((value: unknown) =>
        Array.isArray(value)
          ? yup.array().of(rowSchemaHolder.schema).optional().nullable()
          : yup.mixed().optional().nullable(),
      ),
    })
    .unknown(true)

  rowSchemaHolder.schema = yup.lazy((value: unknown) => {
    if (typeof value !== 'object' || value === null) {
      return yup.mixed().optional().nullable()
    }
    return yup
      .object({
        type: yup.string().optional(),
        group: yup.string().optional(),
        Header: quickbooksCashFlowReportColDataContainerSchema
          .optional()
          .nullable()
          .default(undefined),
        Rows: rowsContainerSchema.optional().nullable().default(undefined),
        Summary: quickbooksCashFlowReportColDataContainerSchema
          .optional()
          .nullable()
          .default(undefined),
        ColData: yup.array().of(quickbooksCashFlowReportColDataSchema).optional(),
      })
      .unknown(true)
  })

  return rowsContainerSchema
}

const quickbooksCashFlowReportRowsSchema = createQuickbooksCashFlowReportRowsSchema()

const quickbooksCashFlowReportHeaderSchema = yup
  .object({
    ReportName: yup.string().optional(),
    Option: yup
      .array()
      .of(
        yup
          .object({
            Name: yup.string().optional(),
            Value: yup.string().optional(),
          })
          .unknown(true),
      )
      .optional(),
    StartPeriod: yup.string().optional(),
    EndPeriod: yup.string().optional(),
    Currency: yup.string().optional(),
    Time: yup.string().optional(),
    SummarizeColumnsBy: yup.string().optional(),
  })
  .unknown(true)

const quickbooksCashFlowReportColumnsSchema = yup
  .object({
    Column: yup
      .array()
      .of(
        yup
          .object({
            ColType: yup.string().optional(),
            ColTitle: yup.string().optional(),
          })
          .unknown(true),
      )
      .optional(),
  })
  .unknown(true)

/**
 * QuickBooks Cash Flow report JSON (`GET /reports/CashFlow`).
 *
 * @see https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/cashflow
 */
export const quickbooksCashFlowReportResponseSchema = yup
  .object({
    Header: quickbooksCashFlowReportHeaderSchema.optional().nullable().default(undefined),
    Rows: quickbooksCashFlowReportRowsSchema.optional().nullable().default(undefined),
    Columns: quickbooksCashFlowReportColumnsSchema.optional().nullable().default(undefined),
  })
  .unknown(true)
export type QuickbooksCashFlowReportResponse = yup.InferType<
  typeof quickbooksCashFlowReportResponseSchema
>

// PURCHASE ORDER ------------------------------------------------------------------------------------------

/** Action step input schema of a Quickbooks expense item line detail */
const quickbooksExpenseItemLineDetailActionStepInputSchema = yup
  .object({
    Id: yup.string().notRequired(),
    Qty: yup.number().notRequired(),
    UnitPrice: yup.number().notRequired(),
    TaxInclusiveAmt: yup.number().notRequired(),
    CreateIfNotExists: yup.boolean().notRequired(),
    ItemRef: yup
      .object({
        name: yup.string().notRequired(),
        value: yup.string().notRequired(),
        CreateIfNotExists: yup.boolean().notRequired(),
        ItemMetadata: quickbooksGetOrCreateItemMetadataActionStepInputSchema.notRequired(),
      })
      .notRequired(),
    CreateCustomerIfNotExists: yup.boolean().notRequired(),
    CustomerId: yup.string().notRequired(),
    CustomerRef: quickbooksCreateCustomerActionStepInputSchema.notRequired(),
    BillableStatus: yup.string().oneOf(['Billable', 'NotBillable', 'HasBeenBilled']).notRequired(),
  })
  .strict()

/** Action step input schema of a Quickbooks purchase order line item */
const quickbooksPurchaseOrderLineItemActionStepInputSchema = yup.object({
  DetailType: yup.string().oneOf(['ItemBasedExpenseLineDetail']).required(),
  Amount: yup.number().required(),
  LineNum: yup.number().notRequired(), // Specifies the position of the line in the collection
  Description: yup.string().notRequired(),
  CustomExtensions: yup.array().of(quickbooksCustomExtensionSchema).notRequired(),
  ItemBasedExpenseLineDetail: quickbooksExpenseItemLineDetailActionStepInputSchema.when(
    'DetailType',
    {
      is: 'ItemBasedExpenseLineDetail',
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.notRequired(),
    },
  ),
})
export type QuickbooksPurchaseOrderLineItemActionStepInput = yup.InferType<
  typeof quickbooksPurchaseOrderLineItemActionStepInputSchema
>

/** Base schema for Quickbooks create/update purchase order action step input */
export const quickbooksPurchaseOrderBaseActionStepInputSchema = yup.object({
  DocNumber: yup.string().notRequired(),
  TxnDate: yup.string().notRequired(),
  TotalAmt: yup.number().notRequired(),
  POEmail: yup
    .object({
      Address: yup.string().email().notRequired(),
    })
    .notRequired()
    .strict(),
  CreateVendorIfNotExists: yup.boolean().notRequired(),
  VendorRef: quickbooksGetOrCreateVendorActionStepInputSchema.required(),
  VendorAddr: yup
    .object({
      Id: yup.string().notRequired(),
      Line1: yup.string().notRequired(),
      Line2: yup.string().notRequired(),
      Line3: yup.string().notRequired(),
      Line4: yup.string().notRequired(),
      City: yup.string().notRequired(),
      Country: yup.string().notRequired(),
      CountrySubDivisionCode: yup.string().notRequired(),
      PostalCode: yup.string().notRequired(),
    })
    .notRequired()
    .strict(),
  ShipAddr: yup
    .object({
      Id: yup.string().notRequired(),
      Line1: yup.string().notRequired(),
      Line2: yup.string().notRequired(),
      Line3: yup.string().notRequired(),
      Line4: yup.string().notRequired(),
      City: yup.string().notRequired(),
      Country: yup.string().notRequired(),
      CountrySubDivisionCode: yup.string().notRequired(),
      PostalCode: yup.string().notRequired(),
    })
    .notRequired()
    .strict(),
  CreateAccountIfNotExists: yup.boolean().notRequired(),
  APAccountRef: yup
    .object({
      value: yup.string().notRequired(),
      accountMetadata: quickbooksGetOrCreateAccountMetadataActionStepInputSchema.notRequired(),
    })
    .required(),
  CurrencyRef: yup
    .object({
      name: yup.string().notRequired(),
      value: yup.string().notRequired(),
    })
    .notRequired()
    .strict(),
  POStatus: yup.string().oneOf(['Open', 'Closed']).notRequired(),
  EmailStatus: yup.string().oneOf(['NotSet', 'NeedToSend', 'EmailSent']).notRequired(),
  CustomField: yup
    .array()
    .of(
      yup.object({
        Key: yup.string().required(),
        Value: yup
          .string()
          .nullable()
          .transform((value): string | null => (value != null ? String(value) : value)),
      }),
    )
    .notRequired(),
  Line: yup
    .array()
    .of(quickbooksPurchaseOrderLineItemActionStepInputSchema)
    .min(1, 'At least one line item is required')
    .required(),
})

export type QuickbooksPurchaseOrderBaseActionStepInput = yup.InferType<
  typeof quickbooksPurchaseOrderBaseActionStepInputSchema
>

/** Action step input schema for Quickbooks Create PurchaseOrder */
export const quickbooksCreatePurchaseOrderActionStepInputSchema =
  quickbooksPurchaseOrderBaseActionStepInputSchema

/** Action step input schema for Quickbooks Update PurchaseOrder */
export const quickbooksUpdatePurchaseOrderActionStepInputSchema = yup
  .object({
    Id: yup.string().notRequired(),
  })
  .concat(quickbooksPurchaseOrderBaseActionStepInputSchema)
export type QuickbooksUpdatePurchaseOrderActionStepInput = yup.InferType<
  typeof quickbooksUpdatePurchaseOrderActionStepInputSchema
>

/**
 * Quickbooks Create Purchase Order Request Schema - Individual Line Item Input
 *
 * Used to construct each line item after getting the item ID from Quickbooks
 */
const quickbooksPurchaseOrderLineItemSchema = yup.object({
  DetailType: yup.string().required().oneOf(['ItemBasedExpenseLineDetail']),
  ItemBasedExpenseLineDetail: yup
    .object({
      Id: yup.string().notRequired(),
      ItemRef: yup
        .object({
          value: yup.string().required(),
        })
        .optional(),
      Qty: yup.number().optional(), // Number of items for the line
      // If supplied during create or update, overrides the unit price of the item as referenced by ItemRef
      UnitPrice: yup.number().optional(),
      TaxInclusiveAmt: yup.number().optional(),
      CustomerId: yup.string().optional(),
      CustomerRef: yup
        .object({
          value: yup.string().required(),
          name: yup.string().notRequired(),
        })
        .notRequired(),
      BillableStatus: yup.string().oneOf(['Billable', 'NotBillable', 'HasBeenBilled']).optional(),
    })
    .required()
    .strict(), // Strict mode prevents casting undefined to empty object
  Amount: yup.number().required(),
  // Total $ of this line item, must be equal to UnitPrice * Qty if both are provided.
  Description: yup.string().optional(),
  LineNum: yup.number().optional(), // Specifies the position of the line
  CustomExtensions: yup.array().of(quickbooksCustomExtensionSchema).optional(),
})
export type QuickbooksPurchaseOrderLineItem = yup.InferType<
  typeof quickbooksPurchaseOrderLineItemSchema
>

/** Quickbooks Create/Update Purchase Order Request Schema - Base */
const quickbooksPurchaseOrderBaseObjectSchema =
  quickbooksCreatePurchaseOrderActionStepInputSchema.shape({
    Line: yup.array().of(quickbooksPurchaseOrderLineItemSchema).required().min(1),
    CustomField: yup.array().of(quickbooksSetCustomFieldInputSchema).optional(),
  })

export type QuickbooksPurchaseOrderBaseObject = yup.InferType<
  typeof quickbooksPurchaseOrderBaseObjectSchema
>

/** Quickbooks Create Purchase Order Request Schema */
export const quickbooksCreatePurchaseOrderRequestSchema = quickbooksPurchaseOrderBaseObjectSchema
export type QuickbooksCreatePurchaseOrderRequest = yup.InferType<
  typeof quickbooksCreatePurchaseOrderRequestSchema
>

/** Quickbooks Update Purchase Order Request Schema */
export const quickbooksUpdatePurchaseOrderRequestSchema = yup
  .object({
    Id: yup.string().required(),
    SyncToken: yup.string().required(),
    sparse: yup.boolean().optional().nullable(),
  })
  .concat(quickbooksPurchaseOrderBaseObjectSchema)
export type QuickbooksUpdatePurchaseOrderRequest = yup.InferType<
  typeof quickbooksUpdatePurchaseOrderRequestSchema
>

/** Quickbooks Purchase Order Schema (incomplete, only including core fields) */
export const quickbooksPurchaseOrderSchema = yup.object({
  Id: yup.string().required(),
  SyncToken: yup.string().required(),
})
export type QuickbooksPurchaseOrder = yup.InferType<typeof quickbooksPurchaseOrderSchema>

/** Quickbooks Create or Update Purchase Order Response Schema */
export const quickbooksCreateUpdatePurchaseOrderResponseSchema = yup.object({
  PurchaseOrder: quickbooksPurchaseOrderSchema.required(),
})
export type QuickbooksCreateUpdatePurchaseOrderResponse = yup.InferType<
  typeof quickbooksCreateUpdatePurchaseOrderResponseSchema
>

/** Quickbooks Get Single Purchase Order by ID Response Schema */
export const quickbooksGetPurchaseOrderByIdResponseSchema = yup.object({
  PurchaseOrder: quickbooksPurchaseOrderSchema.required(),
})

/** Quickbooks Query Puchase Order Response Schema */
export const quickbooksQueryPurchaseOrderResponseSchema = yup.object({
  QueryResponse: yup
    .object({
      PurchaseOrder: yup.array().of(quickbooksPurchaseOrderSchema).optional(),
    })
    .required(),
})
export type QuickbooksQueryPurchaseOrderResponse = yup.InferType<
  typeof quickbooksQueryPurchaseOrderResponseSchema
>

/** Action step input schema for Quickbooks Delete Purchase Order */
export const quickbooksDeletePurchaseOrderActionStepInputSchema = yup.object({
  Id: yup.string().required('Id is required for delete'),
  SyncToken: yup.string().required('SyncToken is required for delete'),
})
export type QuickbooksDeletePurchaseOrderActionStepInput = yup.InferType<
  typeof quickbooksDeletePurchaseOrderActionStepInputSchema
>

/** Quickbooks Delete Purchase Order Response Schema */
export const quickbooksDeletePurchaseOrderResponseSchema = yup.object({
  PurchaseOrder: yup
    .object({
      status: yup.string().required(),
      domain: yup.string().required(),
      Id: yup.string().required(),
    })
    .required(),
  time: yup.string().required(),
})
export type QuickbooksDeletePurchaseOrderResponse = yup.InferType<
  typeof quickbooksDeletePurchaseOrderResponseSchema
>

// PURCHASE (EXPENSE / CHECK / CREDIT CARD) ----------------------------------------------------------

const QUICKBOOKS_PURCHASE_PAYMENT_TYPES = ['Cash', 'Check', 'CreditCard'] as const

/**
 * Account-based expense line detail for purchase create/update.
 * Requires `AccountRef`; other QBO fields (TaxCodeRef, BillableStatus, …) pass through.
 *
 * @see https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/purchase#create-a-purchase
 */
const quickbooksPurchaseAccountExpenseLineDetailActionStepInputSchema = yup
  .object({
    AccountRef: yup
      .object({
        value: yup.string().required(),
        name: yup.string().optional(),
      })
      .required(),
  })
  .strip(false)

/** Line item for QuickBooks create/update purchase (account-based expense lines). */
const quickbooksPurchaseAccountBasedLineItemActionStepInputSchema = yup.object({
  DetailType: yup.string().oneOf(['AccountBasedExpenseLineDetail']).required(),
  Amount: yup.number().required(),
  Id: yup.string().optional(),
  LineNum: yup.number().optional(),
  Description: yup.string().optional(),
  CustomExtensions: yup.array().of(quickbooksCustomExtensionSchema).optional(),
  ProjectRef: yup
    .object({
      value: yup.string().required(),
      name: yup.string().notRequired(),
    })
    .notRequired(),
  AccountBasedExpenseLineDetail:
    quickbooksPurchaseAccountExpenseLineDetailActionStepInputSchema.required(),
})

/** Line item for QuickBooks create/update purchase (item-based expense lines). */
const quickbooksPurchaseItemBasedLineItemActionStepInputSchema = yup.object({
  DetailType: yup.string().oneOf(['ItemBasedExpenseLineDetail']).required(),
  Amount: yup.number().required(),
  Id: yup.string().optional(),
  LineNum: yup.number().optional(),
  Description: yup.string().optional(),
  CustomExtensions: yup.array().of(quickbooksCustomExtensionSchema).optional(),
  ProjectRef: yup
    .object({
      value: yup.string().required(),
      name: yup.string().notRequired(),
    })
    .notRequired(),
  ItemBasedExpenseLineDetail: quickbooksExpenseItemLineDetailActionStepInputSchema.required(),
})

export const quickbooksPurchaseLineItemActionStepInputSchema = yup.lazy((value: unknown) => {
  if (
    typeof value === 'object' &&
    value !== null &&
    (value as { DetailType?: string }).DetailType === 'ItemBasedExpenseLineDetail'
  ) {
    return quickbooksPurchaseItemBasedLineItemActionStepInputSchema
  }
  return quickbooksPurchaseAccountBasedLineItemActionStepInputSchema
})
export type QuickbooksPurchaseLineItemActionStepInput = yup.InferType<
  typeof quickbooksPurchaseAccountBasedLineItemActionStepInputSchema
>

const quickbooksPurchaseRefSchema = yup
  .object({
    value: yup.string().required(),
    name: yup.string().optional(),
  })
  .strict()

/**
 * Action step input for QuickBooks Create Purchase (`POST /purchase`).
 * Minimum per Intuit: `PaymentType`, `AccountRef`, and at least one `Line` with `Amount`.
 *
 * @see https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/purchase#create-a-purchase
 */
export const quickbooksCreatePurchaseActionStepInputSchema = yup.object({
  PaymentType: yup.string().oneOf(QUICKBOOKS_PURCHASE_PAYMENT_TYPES).required(),
  AccountRef: quickbooksPurchaseRefSchema.required(),
  Line: yup
    .array()
    .of(quickbooksPurchaseLineItemActionStepInputSchema)
    .min(1, 'At least one line item is required')
    .required(),
  TxnDate: yup.string().optional().nullable(),
  DocNumber: yup.string().optional().nullable(),
  PrivateNote: yup.string().optional().nullable(),
  Credit: yup.boolean().optional().nullable(),
  CurrencyRef: yup
    .object({
      name: yup.string().notRequired(),
      value: yup.string().notRequired(),
    })
    .notRequired()
    .strict(),
  DepartmentRef: quickbooksPurchaseRefSchema.notRequired(),
  PaymentMethodRef: quickbooksPurchaseRefSchema.notRequired(),
  EntityRef: yup
    .object({
      value: yup.string().required(),
      name: yup.string().optional(),
      type: yup.string().oneOf(['Customer', 'Vendor', 'Employee']).optional(),
    })
    .notRequired()
    .strip(false),
  GlobalTaxCalculation: yup
    .string()
    .oneOf(['TaxExcluded', 'TaxInclusive', 'NotApplicable'])
    .optional()
    .nullable(),
  PrintStatus: yup.string().oneOf(['NotSet', 'NeedToPrint', 'PrintComplete']).optional().nullable(),
  ExchangeRate: yup.number().optional().nullable(),
  CustomField: yup.array().of(quickbooksSetCustomFieldInputSchema).optional(),
  TxnTaxDetail: yup.mixed().optional(),
  LinkedTxn: yup.array().of(yup.mixed()).optional(),
})
export type QuickbooksCreatePurchaseActionStepInput = yup.InferType<
  typeof quickbooksCreatePurchaseActionStepInputSchema
>

/**
 * Action step input for QuickBooks full/sparse update Purchase (`POST /purchase` with `Id` and
 * `SyncToken` in the body). Set `sparse: true` to update only supplied fields.
 *
 * @see https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/purchase#full-update-a-purchase
 */
export const quickbooksUpdatePurchaseActionStepInputSchema = yup
  .object({
    Id: yup.string().required('Id is required for update'),
    SyncToken: yup.string().required('SyncToken is required for update'),
    sparse: yup.boolean().optional(),
  })
  .concat(quickbooksCreatePurchaseActionStepInputSchema)
export type QuickbooksUpdatePurchaseActionStepInput = yup.InferType<
  typeof quickbooksUpdatePurchaseActionStepInputSchema
>

/** Delete purchase — POST /purchase?operation=delete with flat `Id` + `SyncToken`. */
export const quickbooksDeletePurchaseActionStepInputSchema = yup.object({
  Id: yup.string().required(),
  SyncToken: yup.string().required(),
})
export type QuickbooksDeletePurchaseActionStepInput = yup.InferType<
  typeof quickbooksDeletePurchaseActionStepInputSchema
>

/**
 * QuickBooks Purchase object (permissive — QBO returns many optional fields).
 *
 * @see https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/purchase#read-a-purchase
 */
export const quickbooksPurchaseGenericSchema = yup
  .object({
    Id: yup.string().required(),
    SyncToken: yup.string().optional().nullable(),
  })
  .strip(false)

/** QuickBooks GET /purchase/{id} response body. */
export const quickbooksGetPurchaseByIdResponseSchema = yup.object({
  Purchase: quickbooksPurchaseGenericSchema.required(),
})

/** QuickBooks POST /purchase create and update response body. */
export const quickbooksCreatePurchaseResponseSchema = yup.object({
  Purchase: quickbooksPurchaseGenericSchema.required(),
  time: yup.string().optional(),
})
export type QuickbooksCreatePurchaseResponse = yup.InferType<
  typeof quickbooksCreatePurchaseResponseSchema
>

/** Response body after POST /purchase?operation=delete */
export const quickbooksDeletedPurchaseSchema = yup.object({
  status: yup.string().oneOf(['Deleted']).required(),
  domain: yup.string().optional(),
  Id: yup.string().required(),
})

export const quickbooksDeletePurchaseResponseSchema = yup.object({
  Purchase: quickbooksDeletedPurchaseSchema.required(),
  time: yup.string().optional(),
})
export type QuickbooksDeletePurchaseResponse = yup.InferType<
  typeof quickbooksDeletePurchaseResponseSchema
>

// DEPOSIT ------------------------------------------------------------------------------------------

const QUICKBOOKS_DEPOSIT_LINKED_TXN_TYPES = [
  'Transfer',
  'Payment',
  'SalesReceipt',
  'RefundReceipt',
  'JournalEntry',
] as const

const quickbooksDepositRefSchema = yup
  .object({
    value: yup.string().required(),
    name: yup.string().optional(),
  })
  .strict()

/** LinkedTxn deposit line — funds Undeposited Funds into DepositToAccountRef. */
const quickbooksDepositLinkedTxnLineActionStepInputSchema = yup.object({
  Amount: yup.number().required(),
  LinkedTxn: yup
    .array()
    .of(
      yup.object({
        TxnId: yup.string().required(),
        TxnType: yup.string().oneOf(QUICKBOOKS_DEPOSIT_LINKED_TXN_TYPES).required(),
        TxnLineId: yup.string().optional().nullable(),
      }),
    )
    .min(1, 'At least one LinkedTxn is required for a linked deposit line')
    .required(),
  ProjectRef: quickbooksDepositRefSchema.notRequired(),
  Description: yup.string().optional().nullable(),
})

/** Direct DepositLineDetail line — deposits to DepositToAccountRef from AccountRef. */
const quickbooksDepositLineDetailLineActionStepInputSchema = yup.object({
  DetailType: yup.string().oneOf(['DepositLineDetail']).required(),
  Amount: yup.number().required(),
  DepositLineDetail: yup
    .object({
      AccountRef: quickbooksDepositRefSchema.required(),
      PaymentMethodRef: quickbooksDepositRefSchema.notRequired(),
      ClassRef: quickbooksDepositRefSchema.notRequired(),
      Entity: yup
        .object({
          value: yup.string().required(),
          name: yup.string().optional(),
          type: yup.string().oneOf(['Customer', 'Vendor', 'Employee']).optional(),
        })
        .nullable()
        .optional()
        .default(undefined),
      CheckNum: yup.string().optional().nullable(),
      TxnType: yup.string().optional().nullable(),
    })
    .required(),
  ProjectRef: quickbooksDepositRefSchema.notRequired(),
  Description: yup.string().optional().nullable(),
  Id: yup.string().optional().nullable(),
})

export const quickbooksDepositLineItemActionStepInputSchema = yup.lazy((value: unknown) => {
  if (
    typeof value === 'object' &&
    value !== null &&
    (value as { DetailType?: string }).DetailType === 'DepositLineDetail'
  ) {
    return quickbooksDepositLineDetailLineActionStepInputSchema
  }
  return quickbooksDepositLinkedTxnLineActionStepInputSchema
})

/**
 * Action step input for QuickBooks Create Deposit (`POST /deposit`).
 * Requires `DepositToAccountRef` and at least one line (linkedTxn or DepositLineDetail).
 *
 * @see https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/deposit#create-a-deposit
 */
export const quickbooksCreateDepositActionStepInputSchema = yup.object({
  DepositToAccountRef: quickbooksDepositRefSchema.required(),
  Line: yup
    .array()
    .of(quickbooksDepositLineItemActionStepInputSchema)
    .min(1, 'At least one line item is required')
    .required(),
  TxnDate: yup.string().optional().nullable(),
  PrivateNote: yup.string().optional().nullable(),
  CurrencyRef: yup
    .object({
      name: yup.string().notRequired(),
      value: yup.string().notRequired(),
    })
    .notRequired()
    .strict(),
  ExchangeRate: yup.number().optional().nullable(),
  DepartmentRef: quickbooksDepositRefSchema.notRequired(),
  GlobalTaxCalculation: yup
    .string()
    .oneOf(['TaxExcluded', 'TaxInclusive', 'NotApplicable'])
    .optional()
    .nullable(),
  CashBack: yup.mixed().optional(),
  TxnTaxDetail: yup.mixed().optional(),
  TxnSource: yup.string().optional().nullable(),
})
export type QuickbooksCreateDepositActionStepInput = yup.InferType<
  typeof quickbooksCreateDepositActionStepInputSchema
>

const quickbooksDepositFullUpdateMinimumFieldsTest = (
  value: unknown,
): value is Record<string, unknown> => {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return false
  }
  const v = value as Record<string, unknown>
  if (typeof v.Id !== 'string' || v.Id.trim().length === 0) {
    return false
  }
  if (typeof v.SyncToken !== 'string' || v.SyncToken.trim().length === 0) {
    return false
  }
  if (v.sparse === true) {
    return false
  }
  const depositRef = v.DepositToAccountRef
  if (depositRef === null || typeof depositRef !== 'object' || Array.isArray(depositRef)) {
    return false
  }
  const dr = depositRef as Record<string, unknown>
  if (typeof dr.value !== 'string' || dr.value.trim().length === 0) {
    return false
  }
  if (!Array.isArray(v.Line) || v.Line.length === 0) {
    return false
  }
  return true
}

const quickbooksDepositSparseUpdateMinimumFieldsTest = (
  value: unknown,
): value is Record<string, unknown> => {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return false
  }
  const v = value as Record<string, unknown>
  if (v.sparse !== true) {
    return false
  }
  if (typeof v.Id !== 'string' || v.Id.trim().length === 0) {
    return false
  }
  if (typeof v.SyncToken !== 'string' || v.SyncToken.trim().length === 0) {
    return false
  }
  return true
}

/**
 * Full update `POST /deposit` — send writable fields from a read (GET) response; omitted fields become NULL.
 * Do not set `sparse: true` (use `quickbooksSparseUpdateDepositActionStepInputSchema` for sparse updates).
 *
 * @see https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/deposit#full-update-a-deposit
 */
export const quickbooksUpdateDepositActionStepInputSchema: yup.Schema<Record<string, unknown>> = yup
  .mixed<Record<string, unknown>>()
  .defined()
  .required()
  .test(
    'qbo-deposit-full-update-fields',
    'Full update requires Id, SyncToken, DepositToAccountRef with value, at least one Line, and sparse must not be true (map from GET deposit response)',
    quickbooksDepositFullUpdateMinimumFieldsTest,
  )

export type QuickbooksUpdateDepositActionStepInput = Record<string, unknown>

/**
 * Sparse update `POST /deposit` with `sparse: true` — only supplied fields are updated.
 * Intuit’s sample sends only `Id`, `SyncToken`, `sparse`, and fields to change (e.g. `TxnDate`);
 * `Line` and `DepositToAccountRef` are not required on every sparse request.
 *
 * @see https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/deposit#sparse-update-a-deposit
 */
export const quickbooksSparseUpdateDepositActionStepInputSchema: yup.Schema<
  Record<string, unknown>
> = yup
  .mixed<Record<string, unknown>>()
  .defined()
  .required()
  .test(
    'qbo-deposit-sparse-update-fields',
    'Sparse update requires Id, SyncToken, and sparse: true',
    quickbooksDepositSparseUpdateMinimumFieldsTest,
  )

export type QuickbooksSparseUpdateDepositActionStepInput = Record<string, unknown>

/**
 * QuickBooks Deposit object (permissive — QBO returns many optional fields).
 *
 * @see https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/deposit#read-a-deposit
 */
export const quickbooksDepositGenericSchema = yup
  .object({
    Id: yup.string().required(),
    SyncToken: yup.string().optional().nullable(),
  })
  .strip(false)

/** QuickBooks GET /deposit/{id} response body. */
export const quickbooksGetDepositByIdResponseSchema = yup.object({
  Deposit: quickbooksDepositGenericSchema.required(),
})

/** QuickBooks POST /deposit create and update response body. */
export const quickbooksCreateDepositResponseSchema = yup.object({
  Deposit: quickbooksDepositGenericSchema.required(),
  time: yup.string().optional(),
})
export type QuickbooksCreateDepositResponse = yup.InferType<
  typeof quickbooksCreateDepositResponseSchema
>

/** Alias for update response (same shape as create). */
export const quickbooksUpdateDepositResponseSchema = quickbooksCreateDepositResponseSchema
export type QuickbooksUpdateDepositResponse = QuickbooksCreateDepositResponse

/** Delete deposit — POST /deposit?operation=delete with flat `Id` + `SyncToken`. */
export const quickbooksDeleteDepositActionStepInputSchema = yup.object({
  Id: yup.string().required().trim().min(1),
  SyncToken: yup.string().required().trim().min(1),
})
export type QuickbooksDeleteDepositActionStepInput = yup.InferType<
  typeof quickbooksDeleteDepositActionStepInputSchema
>

/** Response body after POST /deposit?operation=delete */
export const quickbooksDeletedDepositSchema = yup.object({
  status: yup.string().oneOf(['Deleted']).required(),
  domain: yup.string().optional(),
  Id: yup.string().required(),
})

export const quickbooksDeleteDepositResponseSchema = yup.object({
  Deposit: quickbooksDeletedDepositSchema.required(),
  time: yup.string().optional(),
})
export type QuickbooksDeleteDepositResponse = yup.InferType<
  typeof quickbooksDeleteDepositResponseSchema
>

// TRANSFER ------------------------------------------------------------------------------------------

/**
 * QuickBooks Transfer object (permissive — QBO returns many optional fields).
 *
 * @see https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/transfer#read-a-transfer
 */
export const quickbooksTransferGenericSchema = yup
  .object({
    Id: yup.string().required(),
    SyncToken: yup.string().optional().nullable(),
  })
  .strip(false)

/** QuickBooks GET /transfer/{id} response body. */
export const quickbooksGetTransferByIdResponseSchema = yup.object({
  Transfer: quickbooksTransferGenericSchema.required(),
})

const quickbooksTransferRefSchema = yup
  .object({
    value: yup.string().required(),
    name: yup.string().optional(),
  })
  .strict()

/**
 * Action step input for QuickBooks Create Transfer (`POST /transfer`).
 * Requires `Amount`, `FromAccountRef`, and `ToAccountRef` (both Asset accounts).
 *
 * @see https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/transfer#create-an-transfer
 */
export const quickbooksCreateTransferActionStepInputSchema = yup.object({
  Amount: yup.number().required(),
  FromAccountRef: quickbooksTransferRefSchema.required(),
  ToAccountRef: quickbooksTransferRefSchema.required(),
  TxnDate: yup.string().optional().nullable(),
  PrivateNote: yup.string().optional().nullable(),
})
export type QuickbooksCreateTransferActionStepInput = yup.InferType<
  typeof quickbooksCreateTransferActionStepInputSchema
>

/** QuickBooks POST /transfer create and update response body. */
export const quickbooksCreateTransferResponseSchema = yup.object({
  Transfer: quickbooksTransferGenericSchema.required(),
  time: yup.string().optional(),
})
export type QuickbooksCreateTransferResponse = yup.InferType<
  typeof quickbooksCreateTransferResponseSchema
>

const quickbooksTransferFullUpdateMinimumFieldsTest = (
  value: unknown,
): value is Record<string, unknown> => {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return false
  }
  const v = value as Record<string, unknown>
  if (typeof v.Id !== 'string' || v.Id.trim().length === 0) {
    return false
  }
  if (typeof v.SyncToken !== 'string' || v.SyncToken.trim().length === 0) {
    return false
  }
  if (v.sparse === true) {
    return false
  }
  if (typeof v.Amount !== 'number' && typeof v.Amount !== 'string') {
    return false
  }
  const fromRef = v.FromAccountRef
  if (fromRef === null || typeof fromRef !== 'object' || Array.isArray(fromRef)) {
    return false
  }
  const fr = fromRef as Record<string, unknown>
  if (typeof fr.value !== 'string' || fr.value.trim().length === 0) {
    return false
  }
  const toRef = v.ToAccountRef
  if (toRef === null || typeof toRef !== 'object' || Array.isArray(toRef)) {
    return false
  }
  const tr = toRef as Record<string, unknown>
  if (typeof tr.value !== 'string' || tr.value.trim().length === 0) {
    return false
  }
  return true
}

const quickbooksTransferSparseUpdateMinimumFieldsTest = (
  value: unknown,
): value is Record<string, unknown> => {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return false
  }
  const v = value as Record<string, unknown>
  if (v.sparse !== true) {
    return false
  }
  if (typeof v.Id !== 'string' || v.Id.trim().length === 0) {
    return false
  }
  if (typeof v.SyncToken !== 'string' || v.SyncToken.trim().length === 0) {
    return false
  }
  return true
}

/**
 * Full update `POST /transfer` — send writable fields from a read (GET) response; omitted fields
 * become NULL. Do not set `sparse: true` (use sparse schema for sparse updates).
 *
 * @see https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/transfer#full-update-an-transfer
 */
export const quickbooksUpdateTransferActionStepInputSchema: yup.Schema<Record<string, unknown>> =
  yup
    .mixed<Record<string, unknown>>()
    .defined()
    .required()
    .test(
      'qbo-transfer-full-update-fields',
      'Full update requires Id, SyncToken, Amount, FromAccountRef with value, ToAccountRef with value, and sparse must not be true (map from GET transfer response)',
      quickbooksTransferFullUpdateMinimumFieldsTest,
    )

export type QuickbooksUpdateTransferActionStepInput = Record<string, unknown>

/**
 * Sparse update `POST /transfer` with `sparse: true` — only supplied fields are updated.
 * Requires `Id`, `SyncToken`, and `sparse: true`; include only the fields to change.
 *
 * @see https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/transfer#sparse-update-an-transfer
 */
export const quickbooksSparseUpdateTransferActionStepInputSchema: yup.Schema<
  Record<string, unknown>
> = yup
  .mixed<Record<string, unknown>>()
  .defined()
  .required()
  .test(
    'qbo-transfer-sparse-update-fields',
    'Sparse update requires Id, SyncToken, and sparse: true',
    quickbooksTransferSparseUpdateMinimumFieldsTest,
  )

export type QuickbooksSparseUpdateTransferActionStepInput = Record<string, unknown>

/** Alias for update response (same shape as create). */
export const quickbooksUpdateTransferResponseSchema = quickbooksCreateTransferResponseSchema
export type QuickbooksUpdateTransferResponse = QuickbooksCreateTransferResponse

/** Delete transfer — POST /transfer?operation=delete with flat `Id` + `SyncToken`. */
export const quickbooksDeleteTransferActionStepInputSchema = yup.object({
  Id: yup.string().required().trim().min(1),
  SyncToken: yup.string().required().trim().min(1),
})
export type QuickbooksDeleteTransferActionStepInput = yup.InferType<
  typeof quickbooksDeleteTransferActionStepInputSchema
>

/** Response body after POST /transfer?operation=delete */
export const quickbooksDeletedTransferSchema = yup.object({
  status: yup.string().oneOf(['Deleted']).required(),
  domain: yup.string().optional(),
  Id: yup.string().required(),
})

export const quickbooksDeleteTransferResponseSchema = yup.object({
  Transfer: quickbooksDeletedTransferSchema.required(),
  time: yup.string().optional(),
})
export type QuickbooksDeleteTransferResponse = yup.InferType<
  typeof quickbooksDeleteTransferResponseSchema
>

// TAX AGENCY ---------------------------------------------------------------------------------------
// @see https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/taxagency

/**
 * QuickBooks TaxAgency object (permissive — QBO returns many optional fields).
 *
 * @see https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/taxagency
 */
export const quickbooksTaxAgencyGenericSchema = yup
  .object({
    Id: yup.string().required(),
    SyncToken: yup.string().optional().nullable(),
    DisplayName: yup.string().optional().nullable(),
    TaxTrackedOnSales: yup.boolean().optional().nullable(),
    TaxTrackedOnPurchases: yup.boolean().optional().nullable(),
    LastFileDate: yup.string().optional().nullable(),
    TaxRegistrationNumber: yup.string().optional().nullable(),
    TaxAgencyConfig: yup.string().optional().nullable(),
    domain: yup.string().optional().nullable(),
    sparse: yup.boolean().optional().nullable(),
    MetaData: quickbooksIntuitEntityMetaDataSchema.optional().nullable(),
  })
  .strip(false)

export type QuickbooksTaxAgency = yup.InferType<typeof quickbooksTaxAgencyGenericSchema>

/** QuickBooks GET /taxagency/{id} and POST /taxagency response body. */
export const quickbooksGetTaxAgencyByIdResponseSchema = yup.object({
  TaxAgency: quickbooksTaxAgencyGenericSchema.required(),
  time: yup.string().optional(),
})
export type QuickbooksGetTaxAgencyByIdResponse = yup.InferType<
  typeof quickbooksGetTaxAgencyByIdResponseSchema
>

/** Alias for create response envelope (`TaxAgency` + optional `time`). */
export const quickbooksCreateTaxAgencyResponseSchema = quickbooksGetTaxAgencyByIdResponseSchema
export type QuickbooksCreateTaxAgencyResponse = QuickbooksGetTaxAgencyByIdResponse

/**
 * Action-step / API body for POST /taxagency.
 * DisplayName is required; other writable fields are optional per Intuit docs.
 */
export const quickbooksCreateTaxAgencyActionStepInputSchema = yup.object({
  DisplayName: yup.string().required().trim().min(1),
  TaxTrackedOnSales: yup.boolean().optional(),
  TaxTrackedOnPurchases: yup.boolean().optional(),
  TaxRegistrationNumber: yup.string().optional(),
})
export type QuickbooksCreateTaxAgencyActionStepInput = yup.InferType<
  typeof quickbooksCreateTaxAgencyActionStepInputSchema
>

// TAX CODE ------------------------------------------------------------------------------------------
// @see https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/taxcode
// Create via TaxService (not TaxCode POST). Read/query use /taxcode and /query.

/**
 * TaxRateDetail nested under TaxCode SalesTaxRateList / PurchaseTaxRateList.
 *
 * @see https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/taxcode
 */
export const quickbooksTaxRateDetailSchema = yup
  .object({
    // default(undefined) avoids yup casting omitted refs to {} under .strip(false), which would
    // then fail value-required nested schemas when QBO omits the field.
    TaxRateRef: quickbooksReferenceTypeValueRequiredSchema.optional().nullable().default(undefined),
    TaxTypeApplicable: yup.string().optional().nullable(),
    TaxOrder: yup.number().optional().nullable(),
  })
  .strip(false)

export type QuickbooksTaxRateDetail = yup.InferType<typeof quickbooksTaxRateDetailSchema>

/**
 * TaxRateList wrapping TaxRateDetail[] on a TaxCode (sales or purchase).
 *
 * @see https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/taxcode
 */
export const quickbooksTaxRateListSchema = yup
  .object({
    TaxRateDetail: yup
      .array()
      .of(quickbooksTaxRateDetailSchema)
      .optional()
      .nullable()
      .default(undefined),
  })
  .strip(false)

export type QuickbooksTaxRateList = yup.InferType<typeof quickbooksTaxRateListSchema>

/**
 * QuickBooks TaxCode object (permissive — QBO returns many optional fields).
 *
 * @see https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/taxcode
 */
export const quickbooksTaxCodeGenericSchema = yup
  .object({
    Id: yup.string().required(),
    Name: yup.string().optional().nullable(),
    SyncToken: yup.string().optional().nullable(),
    PurchaseTaxRateList: quickbooksTaxRateListSchema.optional().nullable().default(undefined),
    SalesTaxRateList: quickbooksTaxRateListSchema.optional().nullable().default(undefined),
    TaxGroup: yup.boolean().optional().nullable(),
    Taxable: yup.boolean().optional().nullable(),
    Active: yup.boolean().optional().nullable(),
    Description: yup.string().optional().nullable(),
    Hidden: yup.boolean().optional().nullable(),
    TaxCodeConfigType: yup.string().optional().nullable(),
    domain: yup.string().optional().nullable(),
    sparse: yup.boolean().optional().nullable(),
    MetaData: quickbooksIntuitEntityMetaDataSchema.optional().nullable().default(undefined),
  })
  .strip(false)

export type QuickbooksTaxCode = yup.InferType<typeof quickbooksTaxCodeGenericSchema>

/** QuickBooks GET /taxcode/{id} response body. */
export const quickbooksGetTaxCodeByIdResponseSchema = yup.object({
  TaxCode: quickbooksTaxCodeGenericSchema.required(),
  time: yup.string().optional(),
})
export type QuickbooksGetTaxCodeByIdResponse = yup.InferType<
  typeof quickbooksGetTaxCodeByIdResponseSchema
>

// TAX PAYMENT ---------------------------------------------------------------------------------------
// Tax Payment/Refund against a filed taxReturn. AU, CA, and UK locales only.
// @see https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/taxpayment

/**
 * QuickBooks TaxPayment object (permissive — QBO returns many optional fields).
 *
 * Intuit field table lists `TxnDate`; documented JSON samples use `PaymentDate`. Both are
 * accepted as optional so either shape validates.
 *
 * @see https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/taxpayment
 */
export const quickbooksTaxPaymentGenericSchema = yup
  .object({
    Id: yup.string().required(),
    SyncToken: yup.string().optional().nullable(),
    Refund: yup.boolean().optional().nullable(),
    /** Field-table name for the tax payment date (read-only). */
    TxnDate: yup.string().optional().nullable(),
    /** Sample-response name for the tax payment date (read-only). */
    PaymentDate: yup.string().optional().nullable(),
    // default(undefined) avoids yup casting omitted refs to {} under .strip(false), which would
    // then fail value-required nested schemas when QBO omits the field.
    PaymentAccountRef: quickbooksReferenceTypeValueRequiredSchema
      .optional()
      .nullable()
      .default(undefined),
    Description: yup.string().optional().nullable(),
    PaymentAmount: yup.number().optional().nullable(),
    domain: yup.string().optional().nullable(),
    sparse: yup.boolean().optional().nullable(),
    MetaData: quickbooksIntuitEntityMetaDataSchema.optional().nullable().default(undefined),
  })
  .strip(false)

export type QuickbooksTaxPayment = yup.InferType<typeof quickbooksTaxPaymentGenericSchema>

/** QuickBooks GET /taxpayment/{id} response body. */
export const quickbooksGetTaxPaymentByIdResponseSchema = yup.object({
  TaxPayment: quickbooksTaxPaymentGenericSchema.required(),
  time: yup.string().optional(),
})
export type QuickbooksGetTaxPaymentByIdResponse = yup.InferType<
  typeof quickbooksGetTaxPaymentByIdResponseSchema
>

// TAX RATE ------------------------------------------------------------------------------------------
// @see https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/taxrate
// Create via TaxService (not TaxRate POST). Read/query use /taxrate and /query.

/**
 * EffectiveTaxRate nested on TaxRate — rate applicable for a date range.
 *
 * @see https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/taxrate
 */
export const quickbooksEffectiveTaxRateSchema = yup
  .object({
    RateValue: yup.number().optional().nullable(),
    EffectiveDate: yup.string().optional().nullable(),
    EndDate: yup.string().optional().nullable(),
  })
  .strip(false)

export type QuickbooksEffectiveTaxRate = yup.InferType<typeof quickbooksEffectiveTaxRateSchema>

/**
 * QuickBooks TaxRate object (permissive — QBO returns many optional fields).
 *
 * @see https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/taxrate
 */
export const quickbooksTaxRateGenericSchema = yup
  .object({
    Id: yup.string().required(),
    SyncToken: yup.string().optional().nullable(),
    RateValue: yup.number().optional().nullable(),
    Name: yup.string().optional().nullable(),
    // default(undefined) avoids yup casting omitted refs to {} under .strip(false), which would
    // then fail value-required nested schemas when QBO omits the field.
    AgencyRef: quickbooksReferenceTypeValueRequiredSchema.optional().nullable().default(undefined),
    SpecialTaxType: yup.string().optional().nullable(),
    EffectiveTaxRate: yup
      .array()
      .of(quickbooksEffectiveTaxRateSchema)
      .optional()
      .nullable()
      .default(undefined),
    DisplayType: yup.string().optional().nullable(),
    TaxReturnLineRef: quickbooksReferenceTypeValueRequiredSchema
      .optional()
      .nullable()
      .default(undefined),
    Active: yup.boolean().optional().nullable(),
    MetaData: quickbooksIntuitEntityMetaDataSchema.optional().nullable().default(undefined),
    OriginalTaxRate: yup.string().optional().nullable(),
    Description: yup.string().optional().nullable(),
    domain: yup.string().optional().nullable(),
    sparse: yup.boolean().optional().nullable(),
  })
  .strip(false)

export type QuickbooksTaxRate = yup.InferType<typeof quickbooksTaxRateGenericSchema>

/** QuickBooks GET /taxrate/{id} response body. */
export const quickbooksGetTaxRateByIdResponseSchema = yup.object({
  TaxRate: quickbooksTaxRateGenericSchema.required(),
  time: yup.string().optional(),
})
export type QuickbooksGetTaxRateByIdResponse = yup.InferType<
  typeof quickbooksGetTaxRateByIdResponseSchema
>

// TAX CLASSIFICATION --------------------------------------------------------------------------------
// Read-only TaxClassificationService (minorversion >= 34). Not available via /query SQL.
// @see https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/taxclassification

/**
 * ItemTypeEnum values for TaxClassification.ApplicableTo per Intuit docs.
 * Docs sample uses `Noninventory`; field table / Item.Type use `NonInventory` — accept both.
 */
export const QUICKBOOKS_TAX_CLASSIFICATION_APPLICABLE_TO_VALUES = [
  'Inventory',
  'Noninventory',
  'NonInventory',
  'Bundle',
  'Service',
] as const

export type QuickbooksTaxClassificationApplicableTo =
  (typeof QUICKBOOKS_TAX_CLASSIFICATION_APPLICABLE_TO_VALUES)[number]

/**
 * QuickBooks TaxClassification object.
 *
 * Field casing follows the Intuit TaxClassification sample object (camelCase for scalar
 * fields; `ParentRef` remains PascalCase). `.strip(false)` keeps any additional keys.
 *
 * @see https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/taxclassification
 */
export const quickbooksTaxClassificationGenericSchema = yup
  .object({
    id: yup.string().required(),
    code: yup.string().optional().nullable(),
    name: yup.string().optional().nullable(),
    description: yup.string().optional().nullable(),
    level: yup.string().optional().nullable(),
    ParentRef: yup
      .object({
        value: yup.string().required(),
        name: yup.string().optional().nullable(),
      })
      .required(),
    applicableTo: yup
      .array()
      .of(
        yup
          .string()
          .oneOf([...QUICKBOOKS_TAX_CLASSIFICATION_APPLICABLE_TO_VALUES])
          .required(),
      )
      .optional()
      .nullable(),
  })
  .strip(false)

/** QuickBooks GET /taxclassification/{id} response body. */
export const quickbooksGetTaxClassificationByIdResponseSchema = yup.object({
  TaxClassification: quickbooksTaxClassificationGenericSchema.required(),
})

/**
 * QuickBooks GET /taxclassification (and children / level) response body.
 *
 * TaxClassificationService docs only document `QueryResponse.TaxClassification[]` —
 * not the SQL-query pagination fields (`startPosition` / `maxResults` / `totalCount`).
 */
export const quickbooksListTaxClassificationsResponseSchema = yup.object({
  QueryResponse: yup
    .object({
      TaxClassification: yup
        .array()
        .of(quickbooksTaxClassificationGenericSchema.required())
        .optional(),
    })
    .required(),
  time: yup.string().optional(),
})

// PAYMENT ------------------------------------------------------------------------------------------

/** Quickbooks Payment Line Item Schema */
const quickbooksPaymentLineItemSchema = yup.object({
  Amount: yup.number().required(),
  CustomExtensions: yup.array().of(quickbooksCustomExtensionSchema).notRequired(),
  LinkedTxn: yup.array().of(
    yup.object({
      TxnId: yup.string().required(),
      TxnType: yup.string().required(),
    }),
  ),
})

/** Quickbooks Payment Schema (incomplete, only including core fields) */
export const quickbooksPaymentSchema = yup.object({
  Id: yup.string().required(),
  SyncToken: yup.string().required(),
  TotalAmt: yup.number().required(),
  UnappliedAmt: yup.number().optional().nullable(),
  TxnDate: yup.date().optional().nullable(),
  CustomerRef: yup
    .object({
      value: yup.string().required(),
      name: yup.string().optional().nullable(),
    })
    .required(),
  Line: yup.array().of(quickbooksPaymentLineItemSchema).optional(),
})
export type QuickbooksPayment = yup.InferType<typeof quickbooksPaymentSchema>

/** Quickbooks Get Payment by ID Response Schema */
export const quickbooksGetPaymentByIdResponseSchema = yup.object({
  Payment: quickbooksPaymentSchema.required(),
})

/** Quickbooks Create Payment Action Step Input Schema */
export const quickbooksCreatePaymentActionStepInputSchema = yup.object({
  CustomerRef: yup
    .object({
      value: yup.string().required(),
      name: yup.string().optional().nullable(),
    })
    .required(),
  TotalAmt: yup.number().required(),
  TxnDate: yup.string().optional().nullable(),
  Line: yup
    .array()
    .of(
      yup.object({
        Amount: yup.number().required(),
        CustomExtensions: yup.array().of(quickbooksCustomExtensionSchema).optional(),
        LinkedTxn: yup
          .array()
          .of(
            yup.object({
              TxnId: yup.string().required(),
              TxnType: yup.string().required(),
            }),
          )
          .required(),
      }),
    )
    .min(1, 'At least one line item is required')
    .required(),
  PaymentMethodRef: yup
    .object({
      value: yup.string().optional(),
      name: yup.string().optional().nullable(),
    })
    .optional(),
  DepositToAccountRef: yup
    .object({
      value: yup.string().optional(),
      name: yup.string().optional().nullable(),
    })
    .optional(),
  PrivateNote: yup.string().optional().nullable(),
  UnappliedAmt: yup.number().optional().nullable(),
})
export type QuickbooksCreatePaymentActionStepInput = yup.InferType<
  typeof quickbooksCreatePaymentActionStepInputSchema
>

/** Quickbooks Create Payment Request Schema */
export const quickbooksCreatePaymentRequestSchema = quickbooksCreatePaymentActionStepInputSchema
export type QuickbooksCreatePaymentRequest = yup.InferType<
  typeof quickbooksCreatePaymentRequestSchema
>

/** Quickbooks Create Payment Response Schema */
export const quickbooksCreatePaymentResponseSchema = yup.object({
  Payment: quickbooksPaymentSchema.required(),
})
export type QuickbooksCreatePaymentResponse = yup.InferType<
  typeof quickbooksCreatePaymentResponseSchema
>

// BILL PAYMENT ------------------------------------------------------------------------------------------

/** Quickbooks Bill Payment Line Item Schema */
const quickbooksBillPaymentLineItemSchema = yup.object({
  Amount: yup.number().required(),
  LinkedTxn: yup
    .array()
    .of(
      yup.object({
        TxnId: yup.string().required(),
        TxnType: yup.string().required(),
      }),
    )
    .optional(),
})

/** Quickbooks Credit Card Payment Schema */
const quickbooksCreditCardPaymentSchema = yup.object({
  CCAccountRef: yup
    .object({
      value: yup.string().required(),
      name: yup.string().optional().nullable(),
    })
    .optional(),
})

/** Quickbooks Check Payment Schema */
const quickbooksCheckPaymentSchema = yup.object({
  PrintStatus: yup.string().optional().nullable(),
  BankAccountRef: yup
    .object({
      value: yup.string().required(),
      name: yup.string().optional().nullable(),
    })
    .optional(),
})

/** Quickbooks Bill Payment Schema */
export const quickbooksBillPaymentSchema = yup.object({
  Id: yup.string().required(),
  SyncToken: yup.string().required(),
  DocNumber: yup.string().optional().nullable(),
  TxnDate: yup.date().optional().nullable(),
  TotalAmt: yup.number().required(),
  PayType: yup.string().required(),
  PrivateNote: yup.string().optional().nullable(),
  sparse: yup.boolean().optional(),
  VendorRef: yup
    .object({
      value: yup.string().required(),
      name: yup.string().optional().nullable(),
    })
    .optional(),
  CurrencyRef: yup
    .object({
      value: yup.string().required(),
      name: yup.string().optional().nullable(),
    })
    .optional(),
  CreditCardPayment: quickbooksCreditCardPaymentSchema.optional(),
  CheckPayment: quickbooksCheckPaymentSchema.optional(),
  Line: yup.array().of(quickbooksBillPaymentLineItemSchema).optional(),
  MetaData: yup
    .object({
      CreateTime: yup.string().optional(),
      LastUpdatedTime: yup.string().optional(),
    })
    .optional(),
  domain: yup.string().optional(),
})
export type QuickbooksBillPayment = yup.InferType<typeof quickbooksBillPaymentSchema>

/** Quickbooks Get Single Bill Payment by ID Response Schema */
export const quickbooksGetBillPaymentByIdResponseSchema = yup.object({
  BillPayment: quickbooksBillPaymentSchema.required(),
})
export type QuickbooksGetBillPaymentByIdResponse = yup.InferType<
  typeof quickbooksGetBillPaymentByIdResponseSchema
>

/** Quickbooks Create Bill Payment Action Step Input Schema */
export const quickbooksCreateBillPaymentActionStepInputSchema = yup.object({
  VendorRef: yup
    .object({
      value: yup.string().required(),
      name: yup.string().optional().nullable(),
    })
    .required(),
  TotalAmt: yup.number().required(),
  PayType: yup.string().oneOf(['Check', 'CreditCard']).required(),
  Line: yup
    .array()
    .of(
      yup.object({
        Amount: yup.number().required(),
        LinkedTxn: yup
          .array()
          .of(
            yup.object({
              TxnId: yup.string().required(),
              TxnType: yup.string().required(),
            }),
          )
          .min(1, 'At least one linked transaction is required per line item')
          .required(),
      }),
    )
    .min(1, 'At least one line item is required')
    .required(),
  CurrencyRef: yup
    .object({
      value: yup.string().required(),
      name: yup.string().optional().nullable(),
    })
    .optional(),
  CheckPayment: yup
    .object({
      BankAccountRef: yup
        .object({
          value: yup.string().required(),
          name: yup.string().optional().nullable(),
        })
        .optional(),
      PrintStatus: yup.string().optional().nullable(),
    })
    .optional(),
  CreditCardPayment: yup
    .object({
      CCAccountRef: yup
        .object({
          value: yup.string().required(),
          name: yup.string().optional().nullable(),
        })
        .optional(),
    })
    .optional(),
  TxnDate: yup.string().optional().nullable(),
  DocNumber: yup.string().optional().nullable(),
  PrivateNote: yup.string().optional().nullable(),
})
export type QuickbooksCreateBillPaymentActionStepInput = yup.InferType<
  typeof quickbooksCreateBillPaymentActionStepInputSchema
>

/** Quickbooks Create Bill Payment Response Schema */
export const quickbooksCreateBillPaymentResponseSchema = yup.object({
  BillPayment: quickbooksBillPaymentSchema.required(),
})
export type QuickbooksCreateBillPaymentResponse = yup.InferType<
  typeof quickbooksCreateBillPaymentResponseSchema
>

/** Quickbooks Void Bill Payment Action Step Input Schema */
export const quickbooksVoidBillPaymentActionStepInputSchema = yup.object({
  Id: yup.string().required(),
  SyncToken: yup.string().required(),
})
export type QuickbooksVoidBillPaymentActionStepInput = yup.InferType<
  typeof quickbooksVoidBillPaymentActionStepInputSchema
>

/** Quickbooks Void Bill Payment Response Schema */
export const quickbooksVoidBillPaymentResponseSchema = yup.object({
  BillPayment: quickbooksBillPaymentSchema.required(),
})
export type QuickbooksVoidBillPaymentResponse = yup.InferType<
  typeof quickbooksVoidBillPaymentResponseSchema
>

/** Quickbooks Delete Bill Payment Action Step Input Schema */
export const quickbooksDeleteBillPaymentActionStepInputSchema = yup.object({
  Id: yup.string().required(),
  SyncToken: yup.string().required(),
})
export type QuickbooksDeleteBillPaymentActionStepInput = yup.InferType<
  typeof quickbooksDeleteBillPaymentActionStepInputSchema
>

/** Quickbooks Delete Bill Payment Response Schema */
export const quickbooksDeleteBillPaymentResponseSchema = yup.object({
  BillPayment: yup
    .object({
      status: yup.string().required(),
      domain: yup.string().required(),
      Id: yup.string().required(),
    })
    .required(),
})
export type QuickbooksDeleteBillPaymentResponse = yup.InferType<
  typeof quickbooksDeleteBillPaymentResponseSchema
>

// JOURNAL ENTRY ------------------------------------------------------------------------------------------

/** Action step input schema of a Quickbooks journal entry line detail */
const quickbooksJournalEntryLineDetailActionStepInputSchema = yup.object({
  PostingType: yup.string().oneOf(['Credit', 'Debit']).required(),
  TaxApplicableOn: yup.string().oneOf(['Sales', 'Purchase']).notRequired(),
  TaxInclusiveAmt: yup.number().notRequired(),
  BillableStatus: yup.string().oneOf(['Billable', 'NotBillable', 'HasBeenBilled']).notRequired(),
  TaxAmount: yup.number().notRequired(),
  ClassRef: yup
    .object({
      name: yup.string().notRequired(),
      value: yup.string().notRequired(),
    })
    .notRequired(),
  DepartmentRef: yup
    .object({
      name: yup.string().notRequired(),
      value: yup.string().notRequired(),
    })
    .notRequired(),
  TaxCodeRef: yup
    .object({
      name: yup.string().notRequired(),
      value: yup.string().notRequired(),
    })
    .notRequired(),
  AccountRef: yup
    .object({
      name: yup.string().optional(),
      value: yup.string().required(),
    })
    .required(),
  JournalCodeRef: yup
    .object({
      name: yup.string().notRequired(),
      value: yup.string().notRequired(),
    })
    .notRequired(),
  Entity: yup
    .object({
      EntityRef: yup
        .object({
          name: yup.string().notRequired(),
          value: yup.string().notRequired(),
        })
        .notRequired(),
      Type: yup.string().oneOf(['Vendor', 'Employee', 'Customer']).notRequired(),
    })
    .notRequired(),
  ProjectRef: yup
    .object({
      name: yup.string().notRequired(),
      value: yup.string().notRequired(),
    })
    .notRequired(),
})

/** Action step input schema of a Quickbooks journal entry line */
const quickbooksJournalEntryLineActionStepInputSchema = yup.object({
  JournalEntryLineDetail: quickbooksJournalEntryLineDetailActionStepInputSchema.required(),
  DetailType: yup.string().oneOf(['JournalEntryLineDetail']).required(),
  Amount: yup.number().required(),
  CustomExtensions: yup.array().of(quickbooksCustomExtensionSchema).notRequired(),
  Id: yup.string().notRequired(),
  Description: yup.string().notRequired(),
  LineNum: yup.number().notRequired(),
  ProjectRef: yup
    .object({
      name: yup.string().notRequired(),
      value: yup.string().notRequired(),
    })
    .notRequired(),
})

/** Quickbooks Create Journal Entry Action Step Input Schema */
export const quickbooksCreateJournalEntryActionStepInputSchema = yup.object({
  DocNumber: yup.string().notRequired(),
  TxnDate: yup.string().optional().nullable(),
  PrivateNote: yup.string().max(4000).notRequired(),
  ExchangeRate: yup.number().notRequired(),
  Adjustment: yup.boolean().notRequired(),
  GlobalTaxCalculation: yup.string().oneOf(['TaxExcluded', 'TaxInclusive']).notRequired(),
  TxnTaxDetail: yup
    .object({
      TxnTaxCodeRef: yup
        .object({
          value: yup.string().notRequired(),
          name: yup.string().notRequired(),
        })
        .notRequired(),
      TotalTax: yup.number().notRequired(),
      TaxLine: yup.array().of(quickbooksTaxLineObject).notRequired(),
    })
    .notRequired(),
  Line: yup
    .array()
    .of(quickbooksJournalEntryLineActionStepInputSchema)
    .min(2, 'At least two line items are required')
    .required(),
  CurrencyRef: yup
    .object({
      name: yup.string().notRequired(),
      value: yup.string().notRequired(),
    })
    .notRequired(),
})
export type QuickbooksCreateJournalEntryActionStepInput = yup.InferType<
  typeof quickbooksCreateJournalEntryActionStepInputSchema
>

/**
 * Request body for POST /journalentry?operation=delete (minimum `Id` + `SyncToken` per Intuit).
 *
 * @see https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/journalentry#delete-a-journalentry
 */
export const quickbooksDeleteJournalEntryActionStepInputSchema = yup.object({
  Id: yup.string().trim().required(),
  SyncToken: yup.string().trim().required(),
})
export type QuickbooksDeleteJournalEntryActionStepInput = yup.InferType<
  typeof quickbooksDeleteJournalEntryActionStepInputSchema
>

/** Quickbooks Create Journal Entry Request Schema */
export const quickbooksCreateJournalEntryRequestSchema =
  quickbooksCreateJournalEntryActionStepInputSchema
export type QuickbooksCreateJournalEntryRequest = yup.InferType<
  typeof quickbooksCreateJournalEntryRequestSchema
>

/** Quickbooks Journal Entry Line Detail Response Schema */
const quickbooksJournalEntryLineDetailResponseSchema = yup.object({
  PostingType: yup.string().required(),
  AccountRef: yup
    .object({
      name: yup.string().required(),
      value: yup.string().required(),
    })
    .required(),
  TaxApplicableOn: yup.string().optional(),
  TaxInclusiveAmt: yup.number().optional(),
  BillableStatus: yup.string().optional(),
  TaxAmount: yup.number().optional(),
  TaxCodeRef: yup
    .object({
      name: yup.string().optional(),
      value: yup.string().optional(),
    })
    .optional(),
  ClassRef: yup
    .object({
      name: yup.string().optional(),
      value: yup.string().optional(),
    })
    .optional(),
  DepartmentRef: yup
    .object({
      name: yup.string().optional(),
      value: yup.string().optional(),
    })
    .optional(),
  Entity: yup
    .object({
      EntityRef: yup
        .object({
          name: yup.string().optional(),
          value: yup.string().optional(),
        })
        .optional(),
      Type: yup.string().optional(),
    })
    .optional(),
})

/** Quickbooks Journal Entry Line Response Schema */
const quickbooksJournalEntryLineResponseSchema = yup.object({
  JournalEntryLineDetail: quickbooksJournalEntryLineDetailResponseSchema.required(),
  DetailType: yup.string().required(),
  Amount: yup.number().required(),
  Id: yup.string().required(),
  Description: yup.string().optional(),
  LineNum: yup.number().optional(),
  ProjectRef: yup
    .object({
      name: yup.string().optional(),
      value: yup.string().optional(),
    })
    .optional(),
})

/** QuickBooks Journal Entry entity shape (create response and query `JournalEntry` rows). */
export const quickbooksJournalEntryResponseSchema = yup.object({
  Id: yup.string().required(),
  SyncToken: yup.string().required(),
  domain: yup.string().oneOf(['QBO']).required(),
  TxnDate: yup.string().required(),
  sparse: yup.boolean().required(),
  Adjustment: yup.boolean().required(),
  Line: yup.array().of(quickbooksJournalEntryLineResponseSchema).required(),
  MetaData: yup
    .object({
      CreateTime: yup.string().required(),
      LastUpdatedTime: yup.string().required(),
    })
    .required(),
  DocNumber: yup.string().optional(),
  PrivateNote: yup.string().optional(),
  CurrencyRef: yup
    .object({
      name: yup.string().optional(),
      value: yup.string().optional(),
    })
    .optional(),
  ExchangeRate: yup.number().optional(),
  GlobalTaxCalculation: yup.string().optional(),
  TxnTaxDetail: yup
    .object({
      TotalTax: yup.number().optional(),
    })
    .optional(),
  TotalAmt: yup.number().optional(),
  HomeTotalAmt: yup.number().optional(),
})

/** Quickbooks Create Journal Entry Response Schema */
export const quickbooksCreateJournalEntryResponseSchema = yup.object({
  JournalEntry: quickbooksJournalEntryResponseSchema.required(),
  time: yup.string().optional(),
})
export type QuickbooksCreateJournalEntryResponse = yup.InferType<
  typeof quickbooksCreateJournalEntryResponseSchema
>

// TRIAL BALANCE REPORT ---------------------------------------------------------------------------------
// https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/trialbalance#query-a-report

/** Accounting basis query parameter for Trial Balance report */
export const QUICKBOOKS_TRIAL_BALANCE_ACCOUNTING_METHOD_VALUES = ['Cash', 'Accrual'] as const

/** Sort order for Trial Balance report */
export const QUICKBOOKS_TRIAL_BALANCE_SORT_ORDER_VALUES = ['ascend', 'descend'] as const

/** `summarize_column_by` query parameter values (Intuit docs) */
export const QUICKBOOKS_TRIAL_BALANCE_SUMMARIZE_COLUMN_BY_VALUES = [
  'Total',
  'Month',
  'Week',
  'Days',
  'Quarter',
  'Year',
  'Customers',
  'Vendors',
  'Classes',
  'Departments',
  'Employees',
  'ProductsAndServices',
  'FiscalYear',
] as const

/** Action step input (report query params); all fields optional — QuickBooks applies defaults. */
export const quickbooksGetTrialBalanceReportActionStepInputSchema = yup.object({
  accounting_method: yup
    .string()
    .oneOf([...QUICKBOOKS_TRIAL_BALANCE_ACCOUNTING_METHOD_VALUES])
    .optional(),
  end_date: yup.string().optional(),
  date_macro: yup.string().optional(),
  sort_order: yup
    .string()
    .oneOf([...QUICKBOOKS_TRIAL_BALANCE_SORT_ORDER_VALUES])
    .optional(),
  summarize_column_by: yup
    .string()
    .oneOf([...QUICKBOOKS_TRIAL_BALANCE_SUMMARIZE_COLUMN_BY_VALUES])
    .optional(),
  start_date: yup.string().optional(),
})
export type QuickbooksGetTrialBalanceReportActionStepInput = yup.InferType<
  typeof quickbooksGetTrialBalanceReportActionStepInputSchema
>

/**
 * Builds the query string for `GET /reports/TrialBalance` (excluding `minorversion`, which the client adds).
 */
export const buildQuickbooksTrialBalanceReportQueryString = (
  data: QuickbooksGetTrialBalanceReportActionStepInput,
): string => {
  const params = new URLSearchParams()
  if (data.accounting_method !== undefined) {
    params.set('accounting_method', data.accounting_method)
  }
  if (data.end_date !== undefined && data.end_date !== '') {
    params.set('end_date', data.end_date)
  }
  if (data.date_macro !== undefined && data.date_macro !== '') {
    params.set('date_macro', data.date_macro)
  }
  if (data.sort_order !== undefined) {
    params.set('sort_order', data.sort_order)
  }
  if (data.summarize_column_by !== undefined) {
    params.set('summarize_column_by', data.summarize_column_by)
  }
  if (data.start_date !== undefined && data.start_date !== '') {
    params.set('start_date', data.start_date)
  }
  return params.toString()
}

/** Validated Trial Balance report JSON (QuickBooks report layout; rows/columns are nested and locale-dependent). */
export const quickbooksTrialBalanceReportResponseSchema = yup.object({
  Header: yup
    .object({
      ReportName: yup.string().optional(),
      Option: yup
        .array()
        .of(
          yup.object({
            Name: yup.string().optional(),
            Value: yup.string().optional(),
          }),
        )
        .optional(),
      DateMacro: yup.string().optional(),
      ReportBasis: yup.string().optional(),
      StartPeriod: yup.string().optional(),
      EndPeriod: yup.string().optional(),
      Time: yup.string().optional(),
      Currency: yup.string().optional(),
    })
    .optional(),
  Columns: yup
    .object({
      Column: yup.mixed().optional(),
    })
    .optional(),
  Rows: yup
    .object({
      Row: yup.mixed().optional(),
    })
    .optional(),
  time: yup.string().optional(),
})
export type QuickbooksTrialBalanceReportResponse = yup.InferType<
  typeof quickbooksTrialBalanceReportResponseSchema
>

// TAX SUMMARY REPORT ------------------------------------------------------------------------------------
// https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/taxsummary#query-a-report
// Applicable for non-US locale companies only.

/** Action step input for Tax Summary report query params. `agency_id` is required by Intuit. */
export const quickbooksGetTaxSummaryReportActionStepInputSchema = yup.object({
  agency_id: yup.string().required(),
  accounting_method: yup
    .string()
    .oneOf([...QUICKBOOKS_TRIAL_BALANCE_ACCOUNTING_METHOD_VALUES])
    .optional(),
  end_date: yup.string().optional(),
  date_macro: yup.string().optional(),
  sort_order: yup
    .string()
    .oneOf([...QUICKBOOKS_TRIAL_BALANCE_SORT_ORDER_VALUES])
    .optional(),
  start_date: yup.string().optional(),
})
export type QuickbooksGetTaxSummaryReportActionStepInput = yup.InferType<
  typeof quickbooksGetTaxSummaryReportActionStepInputSchema
>

/**
 * Builds the query string for `GET /reports/TaxSummary` (excluding `minorversion`, which the client
 * adds).
 *
 * Optional enum fields are typed as `string` here so empty mapped values (`''`) can be omitted
 * without TS2367 against the narrower yup `oneOf` unions.
 */
export const buildQuickbooksTaxSummaryReportQueryString = (data: {
  agency_id: string
  accounting_method?: string
  end_date?: string
  date_macro?: string
  sort_order?: string
  start_date?: string
}): string => {
  const params = new URLSearchParams()
  params.set('agency_id', data.agency_id)
  if (data.accounting_method !== undefined && data.accounting_method !== '') {
    params.set('accounting_method', data.accounting_method)
  }
  if (data.end_date !== undefined && data.end_date !== '') {
    params.set('end_date', data.end_date)
  }
  if (data.date_macro !== undefined && data.date_macro !== '') {
    params.set('date_macro', data.date_macro)
  }
  if (data.sort_order !== undefined && data.sort_order !== '') {
    params.set('sort_order', data.sort_order)
  }
  if (data.start_date !== undefined && data.start_date !== '') {
    params.set('start_date', data.start_date)
  }
  return params.toString()
}

/**
 * Validated Tax Summary report JSON (QuickBooks report layout; rows/columns are nested and
 * locale-dependent).
 */
export const quickbooksTaxSummaryReportResponseSchema = yup.object({
  Header: yup
    .object({
      ReportName: yup.string().optional(),
      Option: yup
        .array()
        .of(
          yup.object({
            Name: yup.string().optional(),
            Value: yup.string().optional(),
          }),
        )
        .optional(),
      StartPeriod: yup.string().optional(),
      EndPeriod: yup.string().optional(),
      Time: yup.string().optional(),
      Currency: yup.string().optional(),
      SummarizeColumnsBy: yup.string().optional(),
    })
    .optional(),
  Columns: yup
    .object({
      Column: yup.mixed().optional(),
    })
    .optional(),
  Rows: yup
    .object({
      Row: yup.mixed().optional(),
    })
    .optional(),
  time: yup.string().optional(),
})
export type QuickbooksTaxSummaryReportResponse = yup.InferType<
  typeof quickbooksTaxSummaryReportResponseSchema
>

// COMPANY INFO ------------------------------------------------------------------------------------------

const quickbooksCompanyInfoEmailSchema = yup.object({
  Address: yup.string().max(100).optional(),
})

const quickbooksCompanyInfoWebAddrSchema = yup.object({
  URI: yup.string().max(1000).optional(),
})

const quickbooksCompanyInfoPrimaryPhoneSchema = yup.object({
  FreeFormNumber: yup.string().max(20).optional(),
})

const quickbooksCompanyInfoNameValueSchema = yup.object({
  Name: yup.string().required(),
  Value: yup.string().optional(),
})

const quickbooksCompanyInfoMetaDataSchema = yup.object({
  CreateTime: yup.string().optional(),
  LastUpdatedTime: yup.string().optional(),
})

/**
 * Core QuickBooks Online `CompanyInfo` object (read response and query rows).
 * @see https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/companyinfo
 */
export const quickbooksCompanyInfoBodySchema = yup.object({
  Id: yup.string().required(),
  SyncToken: yup.string().optional(),
  domain: yup.string().optional(),
  sparse: yup.boolean().optional(),
  CompanyName: yup.string().optional(),
  LegalName: yup.string().max(1024).optional(),
  CompanyAddr: quickbooksPhysicalAddressSchema.optional(),
  LegalAddr: quickbooksPhysicalAddressSchema.optional(),
  CustomerCommunicationAddr: quickbooksPhysicalAddressSchema.optional(),
  PrimaryPhone: quickbooksCompanyInfoPrimaryPhoneSchema.optional(),
  Email: quickbooksCompanyInfoEmailSchema.optional(),
  WebAddr: quickbooksCompanyInfoWebAddrSchema.optional(),
  SupportedLanguages: yup.string().optional(),
  Country: yup.string().optional(),
  FiscalYearStartMonth: yup.string().optional(),
  CompanyStartDate: yup.string().optional(),
  EmployerId: yup.string().optional(),
  NameValue: yup.array().of(quickbooksCompanyInfoNameValueSchema).optional(),
  MetaData: quickbooksCompanyInfoMetaDataSchema.optional(),
})
export type QuickbooksCompanyInfoBody = yup.InferType<typeof quickbooksCompanyInfoBodySchema>

export const quickbooksGetCompanyInfoResponseSchema = yup.object({
  CompanyInfo: quickbooksCompanyInfoBodySchema.required(),
  time: yup.string().optional(),
})
export type QuickbooksGetCompanyInfoResponse = yup.InferType<
  typeof quickbooksGetCompanyInfoResponseSchema
>

export const quickbooksUpdateCompanyInfoResponseSchema = quickbooksGetCompanyInfoResponseSchema
export type QuickbooksUpdateCompanyInfoResponse = yup.InferType<
  typeof quickbooksUpdateCompanyInfoResponseSchema
>

/**
 * Writable CompanyInfo full-update body. QuickBooks requires `Id`, `SyncToken`, `CompanyName`,
 * and `CompanyAddr` for update; other writable fields should be supplied from a prior read when
 * performing a full update so Intuit does not null them out.
 */
export const quickbooksUpdateCompanyInfoActionStepInputSchema = yup
  .object({
    Id: yup.string().required(),
    SyncToken: yup.string().required(),
    CompanyName: yup.string().max(1024).required(),
    CompanyAddr: quickbooksPhysicalAddressSchema.required(),
    LegalAddr: quickbooksPhysicalAddressSchema.optional(),
    CustomerCommunicationAddr: quickbooksPhysicalAddressSchema.optional(),
    PrimaryPhone: quickbooksCompanyInfoPrimaryPhoneSchema.optional(),
    Email: quickbooksCompanyInfoEmailSchema.optional(),
    WebAddr: quickbooksCompanyInfoWebAddrSchema.optional(),
    SupportedLanguages: yup.string().optional(),
    Country: yup.string().optional(),
    LegalName: yup.string().max(1024).optional(),
    FiscalYearStartMonth: yup.string().optional(),
    sparse: yup.boolean().optional(),
    domain: yup.string().optional(),
    NameValue: yup.array().of(quickbooksCompanyInfoNameValueSchema).optional(),
    MetaData: quickbooksCompanyInfoMetaDataSchema.optional(),
    CompanyStartDate: yup.string().optional(),
    EmployerId: yup.string().optional(),
  })
  .noUnknown(false)

export type QuickbooksUpdateCompanyInfoActionStepInput = yup.InferType<
  typeof quickbooksUpdateCompanyInfoActionStepInputSchema
>

/**
 * Successful delete response (`JournalEntry.status` is `Deleted`).
 *
 * @see https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/journalentry#delete-a-journalentry
 */
export const quickbooksDeleteJournalEntryResponseSchema = yup.object({
  JournalEntry: yup
    .object({
      status: yup.string().oneOf(['Deleted']).required(),
      domain: yup.string().oneOf(['QBO']).required(),
      Id: yup.string().required(),
    })
    .required(),
  time: yup.string().optional(),
})
export type QuickbooksDeleteJournalEntryResponse = yup.InferType<
  typeof quickbooksDeleteJournalEntryResponseSchema
>

/**
 * GET /journalentry/{id} response envelope — same shape as create (`JournalEntry` + optional `time`).
 * Intuit documents the same entity fields for read and create responses.
 *
 * @see https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/journalentry#read-a-journalentry
 */
export const quickbooksGetJournalEntryResponseSchema = quickbooksCreateJournalEntryResponseSchema
export type QuickbooksGetJournalEntryResponse = QuickbooksCreateJournalEntryResponse

// GENERIC QUERY ------------------------------------------------------------------------------------------

/** Action step input schema for QuickBooks generic query */
export const quickbooksGenericQueryActionStepInputSchema = yup.object({
  query: yup.string().required(),
})
export type QuickbooksGenericQueryActionStepInput = yup.InferType<
  typeof quickbooksGenericQueryActionStepInputSchema
>

/**
 * Generic query input restricted to JournalEntry queries (GET /v3/company/{realmId}/query).
 *
 * @see https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/journalentry#query-a-journalentry
 */
export const quickbooksJournalEntriesQueryActionStepInputSchema =
  quickbooksGenericQueryActionStepInputSchema.shape({
    query: yup
      .string()
      .required()
      .test(
        'references-journal-entry',
        'Query must reference the JournalEntry entity (for example SELECT * FROM JournalEntry WHERE ...)',
        (value) => {
          if (typeof value !== 'string') {
            return false
          }
          return /\bJournalEntry\b/i.test(value)
        },
      ),
  })

export type QuickbooksJournalEntriesQueryActionStepInput = yup.InferType<
  typeof quickbooksJournalEntriesQueryActionStepInputSchema
>

/** QuickBooks Account Schema for generic query responses */
export const quickbooksAccountGenericSchema = yup.object({
  Id: yup.string().required(),
  Name: yup.string().required(),
  FullyQualifiedName: yup.string().optional(),
  domain: yup.string().optional(),
  Classification: yup.string().optional(),
  AccountSubType: yup.string().optional(),
  CurrencyRef: yup
    .object({
      name: yup.string().optional(),
      value: yup.string().optional(),
    })
    .optional(),
  CurrentBalanceWithSubAccounts: yup.number().optional(),
  sparse: yup.boolean().optional(),
  MetaData: yup
    .object({
      CreateTime: yup.string().optional(),
      LastUpdatedTime: yup.string().optional(),
    })
    .optional(),
  AccountType: yup.string().optional(),
  CurrentBalance: yup.number().optional(),
  Active: yup.boolean().optional(),
  SyncToken: yup.string().optional(),
  SubAccount: yup.boolean().optional(),
})

/** QuickBooks Department Schema for generic query responses */
export const quickbooksDepartmentGenericSchema = yup.object({
  Id: yup.string().required(),
  Name: yup.string().required(),
  FullyQualifiedName: yup.string().optional(),
  domain: yup.string().optional(),
  SyncToken: yup.string().optional(),
  SubDepartment: yup.boolean().optional(),
  sparse: yup.boolean().optional(),
  Active: yup.boolean().optional(),
  MetaData: yup
    .object({
      CreateTime: yup.string().optional(),
      LastUpdatedTime: yup.string().optional(),
    })
    .optional(),
})

/** QuickBooks Customer Schema for generic query responses */
export const quickbooksCustomerGenericSchema = yup.object({
  Id: yup.string().required(),
  DisplayName: yup.string().required(),
  domain: yup.string().optional(),
  FamilyName: yup.string().optional(),
  GivenName: yup.string().optional(),
  MiddleName: yup.string().optional(),
  FullyQualifiedName: yup.string().optional(),
  DefaultTaxCodeRef: yup
    .object({
      value: yup.string().optional(),
    })
    .optional(),
  PrimaryEmailAddr: yup
    .object({
      Address: yup.string().optional(),
    })
    .optional(),
  PreferredDeliveryMethod: yup.string().optional(),
  BillWithParent: yup.boolean().optional(),
  Job: yup.boolean().optional(),
  BalanceWithJobs: yup.number().optional(),
  PrimaryPhone: yup
    .object({
      FreeFormNumber: yup.string().optional(),
    })
    .optional(),
  Active: yup.boolean().optional(),
  MetaData: yup
    .object({
      CreateTime: yup.string().optional(),
      LastUpdatedTime: yup.string().optional(),
    })
    .optional(),
  BillAddr: yup
    .object({
      City: yup.string().optional(),
      Line1: yup.string().optional(),
      PostalCode: yup.string().optional(),
      Lat: yup.string().optional(),
      Long: yup.string().optional(),
      CountrySubDivisionCode: yup.string().optional(),
      Id: yup.string().optional(),
    })
    .optional(),
  Notes: yup.string().optional(),
  Taxable: yup.boolean().optional(),
  Balance: yup.number().optional(),
  SyncToken: yup.string().optional(),
  CompanyName: yup.string().optional(),
  ShipAddr: yup
    .object({
      City: yup.string().optional(),
      Line1: yup.string().optional(),
      PostalCode: yup.string().optional(),
      Lat: yup.string().optional(),
      Long: yup.string().optional(),
      CountrySubDivisionCode: yup.string().optional(),
      Id: yup.string().optional(),
    })
    .optional(),
  PrintOnCheckName: yup.string().optional(),
  sparse: yup.boolean().optional(),
})

/** QuickBooks Vendor Schema for generic query responses */
export const quickbooksVendorGenericSchema = yup.object({
  Id: yup.string().required(),
  DisplayName: yup.string().required(),
  domain: yup.string().optional(),
  Vendor1099: yup.boolean().optional(),
  GivenName: yup.string().optional(),
  FamilyName: yup.string().optional(),
  MiddleName: yup.string().optional(),
  Title: yup.string().optional(),
  Suffix: yup.string().optional(),
  CompanyName: yup.string().optional(),
  AcctNum: yup.string().optional(),
  PrintOnCheckName: yup.string().optional(),
  TaxIdentifier: yup.string().optional(),
  PrimaryEmailAddr: yup
    .object({
      Address: yup.string().optional(),
    })
    .optional(),
  WebAddr: yup
    .object({
      URI: yup.string().optional(),
    })
    .optional(),
  PrimaryPhone: yup
    .object({
      FreeFormNumber: yup.string().optional(),
    })
    .optional(),
  Mobile: yup
    .object({
      FreeFormNumber: yup.string().optional(),
    })
    .optional(),
  BillAddr: yup
    .object({
      City: yup.string().optional(),
      Line1: yup.string().optional(),
      PostalCode: yup.string().optional(),
      Lat: yup.string().optional(),
      Long: yup.string().optional(),
      CountrySubDivisionCode: yup.string().optional(),
      Id: yup.string().optional(),
    })
    .optional(),
  TermRef: yup
    .object({
      value: yup.string().optional(),
    })
    .optional(),
  Balance: yup.number().optional(),
  SyncToken: yup.string().optional(),
  sparse: yup.boolean().optional(),
  Active: yup.boolean().optional(),
  MetaData: yup
    .object({
      CreateTime: yup.string().optional(),
      LastUpdatedTime: yup.string().optional(),
    })
    .optional(),
})

export const quickbooksEmployeeGenericSchema = yup.object({
  Id: yup.string().required(),
  SyncToken: yup.string().optional(),
  domain: yup.string().optional(),
  DisplayName: yup.string().optional(),
  Title: yup.string().optional(),
  GivenName: yup.string().optional(),
  MiddleName: yup.string().optional(),
  FamilyName: yup.string().optional(),
  Suffix: yup.string().optional(),
  PrintOnCheckName: yup.string().optional(),
  EmployeeNumber: yup.string().optional(),
  SSN: yup.string().optional(),
  Gender: yup.string().optional(),
  BirthDate: yup.string().optional(),
  HiredDate: yup.string().optional(),
  ReleasedDate: yup.string().optional(),
  Active: yup.boolean().optional(),
  BillableTime: yup.boolean().optional(),
  Organization: yup.boolean().optional(),
  CostRate: yup.number().optional(),
  BillRate: yup.number().optional(),
  V4IDPseudonym: yup.string().optional(),
  sparse: yup.boolean().optional(),
  PrimaryAddr: yup
    .object({
      Id: yup.string().optional(),
      Line1: yup.string().optional(),
      Line2: yup.string().optional(),
      Line3: yup.string().optional(),
      Line4: yup.string().optional(),
      Line5: yup.string().optional(),
      City: yup.string().optional(),
      Country: yup.string().optional(),
      CountrySubDivisionCode: yup.string().optional(),
      PostalCode: yup.string().optional(),
      Lat: yup.string().optional(),
      Long: yup.string().optional(),
    })
    .optional(),
  PrimaryEmailAddr: yup
    .object({
      Address: yup.string().optional(),
    })
    .optional(),
  PrimaryPhone: yup
    .object({
      FreeFormNumber: yup.string().optional(),
    })
    .optional(),
  Mobile: yup
    .object({
      FreeFormNumber: yup.string().optional(),
    })
    .optional(),
  MetaData: yup
    .object({
      CreateTime: yup.string().optional(),
      LastUpdatedTime: yup.string().optional(),
    })
    .optional(),
})

/**
 * CreditMemo rows from QuickBooks Query API (`Select * from CreditMemo …`).
 * Only `Id` is validated; `.strip(false)` keeps Line, addresses, refs, etc.
 * @see https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/creditmemo#query-a-credit-memo
 */
export const quickbooksCreditMemoGenericSchema = yup
  .object({
    Id: yup.string().required(),
  })
  .strip(false)

/**
 * SalesReceipt rows from QuickBooks Query API (`Select * from SalesReceipt …`).
 * Only `Id` is validated; `.strip(false)` keeps Line, addresses, refs, etc.
 * @see https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/salesreceipt#query-a-sales-receipt
 */
export const quickbooksSalesReceiptGenericSchema = yup
  .object({
    Id: yup.string().required(),
    DocNumber: yup.string().optional().nullable(),
    SyncToken: yup.string().optional().nullable(),
    TotalAmt: yup.number().optional().nullable(),
    Balance: yup.number().optional().nullable(),
  })
  .strip(false)

/**
 * RefundReceipt rows from QuickBooks Query API (`Select * from RefundReceipt …`).
 * Only `Id` is validated; `.strip(false)` keeps full fields (Line, refs, tax, addresses, etc).
 * @see https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/refundreceipt#query-a-refund-receipt
 */
export const quickbooksRefundReceiptGenericSchema = yup
  .object({
    Id: yup.string().required(),
    DocNumber: yup.string().optional().nullable(),
    SyncToken: yup.string().optional().nullable(),
    TotalAmt: yup.number().optional().nullable(),
    Balance: yup.number().optional().nullable(),
  })
  .strip(false)

/**
 * VendorCredit rows from QuickBooks Query API (`Select * from VendorCredit …`).
 * Only `Id` is validated; `.strip(false)` keeps Line, refs, etc.
 * @see https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/vendorcredit#query-a-vendorcredit
 */
export const quickbooksVendorCreditGenericSchema = yup
  .object({
    Id: yup.string().required(),
    SyncToken: yup.string().optional().nullable(),
  })
  .strip(false)

/**
 * QuickBooks GET /creditmemo/{id} response body.
 * @see https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/creditmemo#read-a-credit-memo
 */
export const quickbooksGetCreditMemoByIdResponseSchema = yup.object({
  CreditMemo: quickbooksCreditMemoGenericSchema.required(),
})

/**
 * QuickBooks GET /salesreceipt/{id} response body.
 * @see https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/salesreceipt#read-a-sales-receipt
 */
export const quickbooksGetSalesReceiptByIdResponseSchema = yup.object({
  SalesReceipt: quickbooksSalesReceiptGenericSchema.required(),
})

/**
 * QuickBooks GET /refundreceipt/{id} response body.
 * @see https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/refundreceipt#read-a-refund-receipt
 */
export const quickbooksGetRefundReceiptByIdResponseSchema = yup.object({
  RefundReceipt: quickbooksRefundReceiptGenericSchema.required(),
})

/**
 * QuickBooks GET /vendorcredit/{id} response body.
 * @see https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/vendorcredit#read-a-vendorcredit
 */
export const quickbooksGetVendorCreditByIdResponseSchema = yup.object({
  VendorCredit: quickbooksVendorCreditGenericSchema.required(),
})

/**
 * QuickBooks GET /reports/VendorExpenses response (Report Service).
 * Structure varies by company and query params; `Header` is always present on success.
 *
 * @see https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/vendorexpenses
 */
export const quickbooksVendorExpensesReportResponseSchema = yup
  .object({
    Header: yup.object().strip(false).required(),
    Rows: yup.mixed().optional(),
    Columns: yup.mixed().optional(),
  })
  .strip(false)
export type QuickbooksVendorExpensesReportResponse = yup.InferType<
  typeof quickbooksVendorExpensesReportResponseSchema
>

/** QuickBooks POST /vendorcredit create response body. */
export const quickbooksCreateVendorCreditResponseSchema = yup.object({
  VendorCredit: quickbooksVendorCreditGenericSchema.required(),
  time: yup.string().optional(),
})
export type QuickbooksCreateVendorCreditResponse = yup.InferType<
  typeof quickbooksCreateVendorCreditResponseSchema
>

/** Response body after POST /vendorcredit?operation=delete */
export const quickbooksDeletedVendorCreditSchema = yup.object({
  status: yup.string().oneOf(['Deleted']).required(),
  domain: yup.string().optional(),
  Id: yup.string().required(),
})

export const quickbooksDeleteVendorCreditResponseSchema = yup.object({
  VendorCredit: quickbooksDeletedVendorCreditSchema.required(),
  time: yup.string().optional(),
})
export type QuickbooksDeleteVendorCreditResponse = yup.InferType<
  typeof quickbooksDeleteVendorCreditResponseSchema
>

/** QuickBooks POST /salesreceipt response body. */
export const quickbooksCreateSalesReceiptResponseSchema = yup.object({
  SalesReceipt: quickbooksSalesReceiptGenericSchema.required(),
})
export type QuickbooksCreateSalesReceiptResponse = yup.InferType<
  typeof quickbooksCreateSalesReceiptResponseSchema
>

/** QuickBooks POST /refundreceipt response body. */
export const quickbooksCreateRefundReceiptResponseSchema = yup.object({
  RefundReceipt: quickbooksRefundReceiptGenericSchema.required(),
})
export type QuickbooksCreateRefundReceiptResponse = yup.InferType<
  typeof quickbooksCreateRefundReceiptResponseSchema
>

/** Same response envelope as create. */
export const quickbooksUpdateRefundReceiptResponseSchema =
  quickbooksCreateRefundReceiptResponseSchema
export type QuickbooksUpdateRefundReceiptResponse = QuickbooksCreateRefundReceiptResponse

/** Same response envelope as create. */
export const quickbooksUpdateSalesReceiptResponseSchema = quickbooksCreateSalesReceiptResponseSchema
export type QuickbooksUpdateSalesReceiptResponse = QuickbooksCreateSalesReceiptResponse

/** Delete sales receipt — POST body (`Id` + `SyncToken` per QBO samples). */
export const quickbooksDeleteSalesReceiptActionStepInputSchema = yup.object({
  Id: yup.string().required(),
  SyncToken: yup.string().required(),
})
export type QuickbooksDeleteSalesReceiptActionStepInput = yup.InferType<
  typeof quickbooksDeleteSalesReceiptActionStepInputSchema
>

/** Response shape after POST .../salesreceipt?operation=delete */
export const quickbooksDeletedSalesReceiptSchema = yup.object({
  status: yup.string().oneOf(['Deleted']).required(),
  domain: yup.string().optional(),
  Id: yup.string().required(),
})

export const quickbooksDeleteSalesReceiptResponseSchema = yup.object({
  SalesReceipt: quickbooksDeletedSalesReceiptSchema.required(),
})
export type QuickbooksDeleteSalesReceiptResponse = yup.InferType<
  typeof quickbooksDeleteSalesReceiptResponseSchema
>

/** Delete refund receipt — POST body (`Id` + `SyncToken` per QBO samples). */
export const quickbooksDeleteRefundReceiptActionStepInputSchema = yup.object({
  Id: yup.string().required(),
  SyncToken: yup.string().required(),
})
export type QuickbooksDeleteRefundReceiptActionStepInput = yup.InferType<
  typeof quickbooksDeleteRefundReceiptActionStepInputSchema
>

/** Response shape after POST .../refundreceipt?operation=delete */
export const quickbooksDeletedRefundReceiptSchema = yup.object({
  status: yup.string().oneOf(['Deleted']).required(),
  domain: yup.string().optional(),
  Id: yup.string().required(),
})

export const quickbooksDeleteRefundReceiptResponseSchema = yup.object({
  RefundReceipt: quickbooksDeletedRefundReceiptSchema.required(),
})
export type QuickbooksDeleteRefundReceiptResponse = yup.InferType<
  typeof quickbooksDeleteRefundReceiptResponseSchema
>

export const quickbooksBudgetGenericSchema = yup.object({
  Id: yup.string().required(),
  SyncToken: yup.string().optional(),
  MetaData: quickbooksIntuitEntityMetaDataSchema.optional(),
  Name: yup.string().optional(),
  StartDate: yup.string().optional(),
  EndDate: yup.string().optional(),
  BudgetType: yup
    .string()
    .oneOf([...QUICKBOOKS_BUDGET_TYPE_VALUES])
    .optional(),
  BudgetEntryType: yup
    .string()
    .oneOf([...QUICKBOOKS_BUDGET_ENTRY_TYPE_VALUES])
    .optional(),
  Active: yup.boolean().optional(),
  BudgetDetail: yup.array().of(quickbooksBudgetDetailSchema).optional(),
  domain: yup.string().optional(),
  sparse: yup.boolean().optional(),
})

/** QuickBooks Invoice Schema for generic query responses */
export const quickbooksInvoiceGenericSchema = yup.object({
  Id: yup.string().required(),
  DocNumber: yup.string().optional().nullable(),
  Balance: yup.number().optional().nullable(),
  TotalAmt: yup.number().optional().nullable(),
  TxnDate: yup.date().optional().nullable(),
  DueDate: yup.date().optional().nullable(),
  LinkedTxn: yup
    .array()
    .of(
      yup.object({
        TxnId: yup.string().required(),
        TxnType: yup.string().required(),
      }),
    )
    .optional(),
})

/** QuickBooks Payment Schema for generic query responses */
export const quickbooksPaymentGenericSchema = yup.object({
  Id: yup.string().required(),
  TotalAmt: yup.number().optional().nullable(),
  TxnDate: yup.date().optional().nullable(),
  Line: yup
    .array()
    .of(
      yup.object({
        Amount: yup.number().optional().nullable(),
        LinkedTxn: yup
          .array()
          .of(
            yup.object({
              TxnId: yup.string().required(),
              TxnType: yup.string().required(),
            }),
          )
          .optional(),
      }),
    )
    .optional(),
})

/** QuickBooks generic query response schema */
export const quickbooksGenericQueryResponseSchema = yup.object({
  QueryResponse: yup
    .object({
      maxResults: yup.number().optional(),
      startPosition: yup.number().optional(),
      totalCount: yup.number().optional(),
      Account: yup.array().of(quickbooksAccountGenericSchema).optional(),
      Department: yup.array().of(quickbooksDepartmentGenericSchema).optional(),
      Customer: yup.array().of(quickbooksCustomerGenericSchema).optional(),
      Employee: yup.array().of(quickbooksEmployeeGenericSchema).optional(),
      TaxAgency: yup.array().of(quickbooksTaxAgencyGenericSchema).optional(),
      TaxCode: yup.array().of(quickbooksTaxCodeGenericSchema).optional(),
      TaxPayment: yup.array().of(quickbooksTaxPaymentGenericSchema).optional(),
      TaxRate: yup.array().of(quickbooksTaxRateGenericSchema).optional(),
      Vendor: yup.array().of(quickbooksVendorGenericSchema).optional(),
      CompanyInfo: yup
        .mixed()
        .optional()
        .test(
          'company-info-query-shape',
          'QueryResponse.CompanyInfo must be a CompanyInfo object or array of CompanyInfo',
          (value) => {
            if (value === undefined || value === null) {
              return true
            }
            if (Array.isArray(value)) {
              return value.every((item) => quickbooksCompanyInfoBodySchema.isValidSync(item))
            }
            if (typeof value === 'object') {
              return quickbooksCompanyInfoBodySchema.isValidSync(value)
            }
            return false
          },
        ),
      CreditMemo: yup.array().of(quickbooksCreditMemoGenericSchema).optional(),
      SalesReceipt: yup.array().of(quickbooksSalesReceiptGenericSchema).optional(),
      RefundReceipt: yup.array().of(quickbooksRefundReceiptGenericSchema).optional(),
      VendorCredit: yup.array().of(quickbooksVendorCreditGenericSchema).optional(),
      JournalEntry: yup.array().of(quickbooksJournalEntryResponseSchema).optional(),
      Budget: yup.array().of(quickbooksBudgetGenericSchema).optional(),
      Invoice: yup.array().of(quickbooksInvoiceGenericSchema).optional(),
      Payment: yup.array().of(quickbooksPaymentGenericSchema).optional(),
    })
    .required(),
  time: yup.string().optional(),
})

/** Quickbooks Get Single Employee by ID Response Schema */
export const quickbooksGetEmployeeByIdResponseSchema = yup.object({
  Employee: quickbooksEmployeeGenericSchema.required(),
  time: yup.string().optional(),
})
export type QuickbooksGetEmployeeByIdResponse = yup.InferType<
  typeof quickbooksGetEmployeeByIdResponseSchema
>
export type QuickbooksGenericQueryResponse = yup.InferType<
  typeof quickbooksGenericQueryResponseSchema
>

// EMPLOYEE CREATE / UPDATE (REST POST /employee) ----------------------------------------------------
// @see https://developer.intuit.com/app/developer/qbo/docs/api/accounting/most-commonly-used/employee

/** Gender values supported on Employee create/update. */
export const QUICKBOOKS_EMPLOYEE_GENDER_VALUES = ['Male', 'Female'] as const
export type QuickbooksEmployeeGender = (typeof QUICKBOOKS_EMPLOYEE_GENDER_VALUES)[number]

/**
 * DisplayName, Title, GivenName, MiddleName, FamilyName, Suffix, and PrintOnCheckName
 * must not contain colon (:), tab, or newline characters.
 */
const quickbooksEmployeeNameHasForbiddenChars = (value: string): boolean => {
  return value.includes(':') || value.includes('\t') || value.includes('\n')
}

const quickbooksEmployeeNameStringSchema = yup
  .string()
  .nullable()
  .optional()
  .test(
    'no-colon-tab-newline',
    'Must not contain colon (:), tab, or newline characters',
    (value) => {
      if (value === undefined || value === null || value === '') {
        return true
      }
      return !quickbooksEmployeeNameHasForbiddenChars(value)
    },
  )

/** PhysicalAddress subset used on Employee.PrimaryAddr. */
const quickbooksEmployeePhysicalAddressSchema = yup.object({
  Id: yup.string().nullable().optional(),
  Line1: yup.string().nullable().optional(),
  Line2: yup.string().nullable().optional(),
  Line3: yup.string().nullable().optional(),
  Line4: yup.string().nullable().optional(),
  Line5: yup.string().nullable().optional(),
  City: yup.string().max(255).nullable().optional(),
  Country: yup.string().nullable().optional(),
  CountrySubDivisionCode: yup.string().max(255).nullable().optional(),
  PostalCode: yup.string().nullable().optional(),
  Lat: yup.string().nullable().optional(),
  Long: yup.string().nullable().optional(),
})

/** EmailAddress subset used on Employee.PrimaryEmailAddr. Must contain @ and . when set. */
const quickbooksEmployeeEmailAddressSchema = yup.object({
  Address: yup
    .string()
    .nullable()
    .optional()
    .test(
      'at-and-dot',
      'PrimaryEmailAddr.Address must contain an at sign (@) and a dot (.)',
      (value) => {
        if (value === undefined || value === null || value === '') {
          return true
        }
        return value.includes('@') && value.includes('.')
      },
    ),
})

/** TelephoneNumber subset used on Employee.PrimaryPhone and Employee.Mobile. */
const quickbooksEmployeeTelephoneNumberSchema = yup.object({
  FreeFormNumber: yup.string().nullable().optional(),
})

const quickbooksEmployeeHasGivenOrFamilyName = (value: {
  GivenName?: string | null
  FamilyName?: string | null
}): boolean => {
  const givenName = value.GivenName?.trim() ?? ''
  const familyName = value.FamilyName?.trim() ?? ''
  return givenName !== '' || familyName !== ''
}

const quickbooksEmployeeBillRateRequiresBillableTime = (value: {
  BillRate?: number | null
  BillableTime?: boolean | null
}): boolean => {
  if (value.BillRate === undefined || value.BillRate === null) {
    return true
  }
  return value.BillableTime === true
}

/**
 * Writable Employee fields for POST /employee (create and update).
 * Id, SyncToken, sparse, domain, MetaData, and V4IDPseudonym are excluded (update adds Id/SyncToken/sparse).
 *
 * When QuickBooks Payroll is enabled, Title, Suffix, PrintOnCheckName, BillRate, and SSN are not
 * supported; DisplayName is read-only. PrimaryAddr.City, CountrySubDivisionCode, and PostalCode
 * are required when Payroll is enabled.
 */
export const quickbooksEmployeeWriteFieldsSchema = yup.object({
  PrimaryAddr: quickbooksEmployeePhysicalAddressSchema.nullable().optional(),
  PrimaryEmailAddr: quickbooksEmployeeEmailAddressSchema.nullable().optional(),
  DisplayName: quickbooksEmployeeNameStringSchema,
  Title: quickbooksEmployeeNameStringSchema,
  BillableTime: yup.boolean().nullable().optional(),
  GivenName: quickbooksEmployeeNameStringSchema,
  BirthDate: yup.string().nullable().optional(),
  MiddleName: quickbooksEmployeeNameStringSchema,
  SSN: yup.string().nullable().optional(),
  PrimaryPhone: quickbooksEmployeeTelephoneNumberSchema.nullable().optional(),
  Active: yup.boolean().nullable().optional(),
  ReleasedDate: yup.string().nullable().optional(),
  CostRate: yup.number().nullable().optional(),
  Mobile: quickbooksEmployeeTelephoneNumberSchema.nullable().optional(),
  Gender: yup
    .string()
    .oneOf([...QUICKBOOKS_EMPLOYEE_GENDER_VALUES])
    .nullable()
    .optional(),
  HiredDate: yup.string().nullable().optional(),
  BillRate: yup.number().nullable().optional(),
  Organization: yup.boolean().nullable().optional(),
  Suffix: quickbooksEmployeeNameStringSchema,
  FamilyName: quickbooksEmployeeNameStringSchema,
  PrintOnCheckName: quickbooksEmployeeNameStringSchema,
  EmployeeNumber: yup.string().nullable().optional(),
})
export type QuickbooksEmployeeWriteFields = yup.InferType<
  typeof quickbooksEmployeeWriteFieldsSchema
>

/**
 * Action-step / API body for POST /employee (create).
 * At least one of GivenName or FamilyName is required.
 */
export const quickbooksCreateEmployeeActionStepInputSchema = quickbooksEmployeeWriteFieldsSchema
  .test(
    'at-least-one-name',
    'At least one of GivenName or FamilyName is required when creating an employee',
    (value) => quickbooksEmployeeHasGivenOrFamilyName(value),
  )
  .test(
    'bill-rate-requires-billable-time',
    'BillRate can only be set if BillableTime is true',
    (value) => quickbooksEmployeeBillRateRequiresBillableTime(value),
  )
export type QuickbooksCreateEmployeeActionStepInput = yup.InferType<
  typeof quickbooksCreateEmployeeActionStepInputSchema
>

/** Alias for create/update response envelope (`Employee` + optional `time`). */
export const quickbooksCreateEmployeeResponseSchema = quickbooksGetEmployeeByIdResponseSchema
export type QuickbooksCreateEmployeeResponse = QuickbooksGetEmployeeByIdResponse

/**
 * Action-step / API body for POST /employee (full or sparse update).
 * Id and SyncToken are required. GivenName/FamilyName are not required on sparse update.
 */
export const quickbooksUpdateEmployeeActionStepInputSchema = quickbooksEmployeeWriteFieldsSchema
  .concat(
    yup.object({
      Id: yup.string().required(),
      SyncToken: yup.string().required(),
      sparse: yup.boolean().nullable().optional(),
    }),
  )
  .test(
    'bill-rate-requires-billable-time',
    'BillRate can only be set if BillableTime is true',
    (value) => quickbooksEmployeeBillRateRequiresBillableTime(value),
  )
export type QuickbooksUpdateEmployeeActionStepInput = yup.InferType<
  typeof quickbooksUpdateEmployeeActionStepInputSchema
>
export const quickbooksUpdateEmployeeResponseSchema = quickbooksGetEmployeeByIdResponseSchema
export type QuickbooksUpdateEmployeeResponse = QuickbooksGetEmployeeByIdResponse

// GENERAL LEDGER REPORT (Reports API) ------------------------------------------------------------------

/**
 * Query parameters for the QuickBooks General Ledger report (Reports API).
 * See: https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/generalledger
 * Names match the QuickBooks Online query string (snake_case).
 */
export const quickbooksGeneralLedgerReportActionStepInputSchema = yup.object({
  start_date: yup.string().optional(),
  end_date: yup.string().optional(),
  date_macro: yup.string().optional(),
  accounting_method: yup.string().optional(),
  account_type: yup.string().optional(),
  summarize_column_by: yup.string().optional(),
  sort_by: yup.string().optional(),
  sort_order: yup.string().optional(),
  columns: yup.string().optional(),
  account: yup.string().optional(),
  source_account: yup.string().optional(),
  customer: yup.string().optional(),
  vendor: yup.string().optional(),
  department: yup.string().optional(),
  class: yup.string().optional(),
})
export type QuickbooksGeneralLedgerReportActionStepInput = yup.InferType<
  typeof quickbooksGeneralLedgerReportActionStepInputSchema
>

/**
 * Ordered query-string keys for `GET /reports/GeneralLedger`. Shared so handlers and docs stay aligned
 * with {@link quickbooksGeneralLedgerReportActionStepInputSchema}.
 */
export const QUICKBOOKS_GENERAL_LEDGER_REPORT_QUERY_PARAM_KEYS = [
  'start_date',
  'end_date',
  'date_macro',
  'accounting_method',
  'account_type',
  'summarize_column_by',
  'sort_by',
  'sort_order',
  'columns',
  'account',
  'source_account',
  'customer',
  'vendor',
  'department',
  'class',
] as const satisfies ReadonlyArray<keyof QuickbooksGeneralLedgerReportActionStepInput>

/** One cell in a report row (QuickBooks Reports API JSON uses PascalCase keys). */
const quickbooksReportColDataSchema = yup
  .object({
    id: yup.string().optional(),
    value: yup.string().optional(),
    href: yup.string().optional(),
  })
  .unknown(true)

const quickbooksGeneralLedgerReportOptionPairSchema = yup
  .object({
    Name: yup.string().optional(),
    Value: yup.string().optional(),
  })
  .unknown(true)

const quickbooksGeneralLedgerReportHeaderSchema = yup
  .object({
    ReportName: yup.string().optional(),
    Option: yup.array().of(quickbooksGeneralLedgerReportOptionPairSchema).optional(),
    ReportBasis: yup.string().optional(),
    StartPeriod: yup.string().optional(),
    EndPeriod: yup.string().optional(),
    Currency: yup.string().optional(),
    Time: yup.string().optional(),
    SummarizeColumnsBy: yup.string().optional(),
    Customer: yup.string().optional(),
    Vendor: yup.string().optional(),
    Item: yup.string().optional(),
    Employee: yup.string().optional(),
    Class: yup.string().optional(),
    Department: yup.string().optional(),
  })
  .unknown(true)

const quickbooksGeneralLedgerReportColumnSchema = yup
  .object({
    ColType: yup.string().optional(),
    ColTitle: yup.string().optional(),
  })
  .unknown(true)

const quickbooksGeneralLedgerReportColumnsSchema = yup
  .object({
    Column: yup.array().of(quickbooksGeneralLedgerReportColumnSchema).optional(),
  })
  .unknown(true)

// Circular lazy refs; inferred return type is the accurate `yup` schema shape.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type -- see above
const createQuickbooksGeneralLedgerReportRowsSchemas = () => {
  const reportRowSchemaHolder: { schema: yup.Lazy<unknown> } = {
    schema: yup.lazy(() => yup.mixed().optional()),
  }

  const rowsContainerSchema = yup
    .object({
      Row: yup.lazy(() => yup.array().of(reportRowSchemaHolder.schema).optional()),
    })
    .unknown(true)

  const sectionHeaderSchema = yup
    .object({
      ColData: yup.array().of(quickbooksReportColDataSchema).optional(),
    })
    .unknown(true)

  const summarySchema = yup
    .object({
      ColData: yup.array().of(quickbooksReportColDataSchema).optional(),
    })
    .unknown(true)

  reportRowSchemaHolder.schema = yup.lazy((value: unknown) => {
    if (typeof value !== 'object' || value === null) {
      return yup.mixed().optional()
    }
    const row = value as Record<string, unknown>
    if (row.type === 'Data') {
      return yup
        .object({
          type: yup.string().oneOf(['Data']).optional(),
          ColData: yup.array().of(quickbooksReportColDataSchema).optional(),
        })
        .unknown(true)
    }
    return yup
      .object({
        type: yup.string().optional(),
        group: yup.string().optional(),
        Header: sectionHeaderSchema.optional(),
        Rows: rowsContainerSchema.optional(),
        Summary: summarySchema.optional(),
        ColData: yup.array().of(quickbooksReportColDataSchema).optional(),
      })
      .unknown(true)
  })

  return { rowsContainerSchema }
}

const { rowsContainerSchema: quickbooksGeneralLedgerReportRowsSchema } =
  createQuickbooksGeneralLedgerReportRowsSchemas()

/**
 * QuickBooks General Ledger report JSON (`GET /reports/GeneralLedger`).
 * Shaped per Reports API docs; `.unknown(true)` on objects preserves fields Intuit adds over time.
 *
 * @see https://developer.intuit.com/app/developer/qbo/docs/api/accounting/all-entities/generalledger
 */
export const quickbooksGeneralLedgerReportResponseSchema = yup
  .object({
    Header: quickbooksGeneralLedgerReportHeaderSchema.optional(),
    Columns: quickbooksGeneralLedgerReportColumnsSchema.optional(),
    Rows: quickbooksGeneralLedgerReportRowsSchema.optional(),
    time: yup.string().optional(),
  })
  .unknown(true)
export type QuickbooksGeneralLedgerReportResponse = yup.InferType<
  typeof quickbooksGeneralLedgerReportResponseSchema
>

export const quickbooksGraphqlPageInfoSchema = yup.object({
  hasNextPage: yup.boolean().required(),
  hasPreviousPage: yup.boolean().required(),
  startCursor: yup.string().nullable().optional(),
  endCursor: yup.string().nullable().optional(),
})

export const quickbooksCustomDimensionDefinitionSchema = yup.object({
  id: yup.string().required(),
  label: yup.string().required(),
  active: yup.boolean().required(),
})

export const quickbooksGetCustomDimensionDefinitionsActionStepInputSchema = yup.object({
  first: yup.number().integer().positive().optional(),
  after: yup.string().nullable().optional(),
  last: yup.number().integer().positive().optional(),
  before: yup.string().nullable().optional(),
})
export type QuickbooksGetCustomDimensionDefinitionsActionStepInput = yup.InferType<
  typeof quickbooksGetCustomDimensionDefinitionsActionStepInputSchema
>

export const quickbooksGetCustomDimensionDefinitionsResponseSchema = yup.object({
  data: yup
    .object({
      appFoundationsActiveCustomDimensionDefinitions: yup
        .object({
          edges: yup
            .array()
            .of(yup.object({ node: quickbooksCustomDimensionDefinitionSchema.required() }))
            .required(),
          pageInfo: quickbooksGraphqlPageInfoSchema.optional(),
          totalCount: yup.number().optional(),
        })
        .required(),
    })
    .required(),
})

export const quickbooksCustomDimensionValueSchema = yup.object({
  id: yup.string().required(),
  definitionId: yup.string().required(),
  label: yup.string().required(),
  active: yup.boolean().required(),
  parentId: yup.string().nullable().optional(),
  fullyQualifiedLabel: yup.string().nullable().optional(),
  level: yup.number().nullable().optional(),
})

export const quickbooksGetCustomDimensionValuesActionStepInputSchema = yup.object({
  first: yup.number().integer().positive().optional(),
  after: yup.string().nullable().optional(),
  last: yup.number().integer().positive().optional(),
  before: yup.string().nullable().optional(),
  filters: yup
    .object({
      definitionId: yup.string().required(),
      parentId: yup.string().nullable().optional(),
    })
    .required(),
})
export type QuickbooksGetCustomDimensionValuesActionStepInput = yup.InferType<
  typeof quickbooksGetCustomDimensionValuesActionStepInputSchema
>

export const quickbooksGetCustomDimensionValuesResponseSchema = yup.object({
  data: yup
    .object({
      appFoundationsActiveCustomDimensionValues: yup
        .object({
          edges: yup
            .array()
            .of(yup.object({ node: quickbooksCustomDimensionValueSchema.required() }))
            .required(),
          pageInfo: quickbooksGraphqlPageInfoSchema.required(),
          totalCount: yup.number().required(),
        })
        .required(),
    })
    .required(),
})

/**
 * Persona / user on a project.
 * External GraphQL selection requests `id` only (broader fields are not on Intuit’s published schema);
 * name-like fields stay optional so validation tolerates them if ever present.
 */
export const quickbooksProjectManagementPersonaSchema = yup.object({
  id: yup.string().required(),
  displayName: yup.string().nullable().optional(),
  firstName: yup.string().nullable().optional(),
  lastName: yup.string().nullable().optional(),
})

/** Same shape as {@link quickbooksProjectManagementPersonaSchema} (assignee / completedBy). */
export const quickbooksProjectManagementUserSchema = quickbooksProjectManagementPersonaSchema

/**
 * Customer or client linked to a project.
 * Selection uses `id` only on external GraphQL; `name` optional if present.
 */
export const quickbooksProjectManagementClientSchema = yup.object({
  id: yup.string().required(),
  name: yup.string().nullable().optional(),
})

/** Same shape as {@link quickbooksProjectManagementClientSchema} (GraphQL `customer` vs `client`). */
export const quickbooksProjectManagementCustomerSchema = quickbooksProjectManagementClientSchema

/** Linked account / company on a Premium Project (GraphQL: account { id }) */
export const quickbooksProjectManagementCompanySchema = yup.object({
  id: yup.string().required(),
})

/**
 * GraphQL id-only reference inputs used on create/update: `ProjectManagement_CompanyInput` (`account`),
 * `ProjectManagement_CustomerInput` (`customer`), `ProjectManagement_ClientInput` (`client`) — each
 * requires `id` only; unknown keys are rejected.
 */
export const quickbooksProjectManagementGraphqlEntityIdInputSchema = yup
  .object({
    id: yup.string().required(),
  })
  .noUnknown(true)

/** Create/update: `account` / `client` / `customer` — optional `id`; handler omits when empty (UI may send `{}`). */
export const quickbooksProjectManagementGraphqlEntityIdInputOptionalIdSchema = yup
  .object({
    id: yup.string().notRequired(),
  })
  .noUnknown(true)

/** Common_GeoLocation (lat/long as string decimals per Intuit docs) */
export const quickbooksCommonGeoLocationSchema = yup.object({
  latitude: yup.string().nullable().optional(),
  longitude: yup.string().nullable().optional(),
})

/** Common_Ordinal — classification / importance of the contact (GraphQL enum → string in JSON) */
export const QUICKBOOKS_COMMON_ORDINAL_VALUES = [
  'OTHER',
  'PRIMARY',
  'SECONDARY',
  'TERTIARY',
  'UNKNOWN',
] as const

/** Common_ContactPurpose (GraphQL enum → string in JSON) */
export const QUICKBOOKS_COMMON_CONTACT_PURPOSE_VALUES = ['BILLING', 'SHIPPING'] as const

/** Common_ContactUsage (GraphQL enum → string in JSON) */
export const QUICKBOOKS_COMMON_CONTACT_USAGE_VALUES = [
  'PERSONAL',
  'BUSINESS',
  'PUBLIC',
  'ALTERNATIVE',
] as const

/**
 * Common_ContactVariation.
 * External selection uses purpose + usage only; `ordinal` is optional if present.
 */
export const quickbooksCommonContactVariationSchema = yup.object({
  ordinal: yup
    .string()
    .oneOf([...QUICKBOOKS_COMMON_ORDINAL_VALUES])
    .nullable()
    .optional(),
  purpose: yup
    .string()
    .oneOf([...QUICKBOOKS_COMMON_CONTACT_PURPOSE_VALUES])
    .nullable()
    .optional(),
  usage: yup
    .string()
    .oneOf([...QUICKBOOKS_COMMON_CONTACT_USAGE_VALUES])
    .nullable()
    .optional(),
})

/** Common_Verification */
export const quickbooksCommonVerificationSchema = yup.object({
  method: yup.string().nullable().optional(),
  status: yup.string().nullable().optional(),
  time: yup.string().nullable().optional(),
  type: yup.string().nullable().optional(),
})

/** Qb_PostalAddress as returned by Projects GraphQL */
export const quickbooksProjectManagementPostalAddressSchema = yup.object({
  streetAddressLine1: yup.string().nullable().optional(),
  streetAddressLine2: yup.string().nullable().optional(),
  streetAddressLine3: yup.string().nullable().optional(),
  city: yup.string().nullable().optional(),
  /** ISO-3166-style country code from API (serialized as string in JSON) */
  country: yup.string().nullable().optional(),
  state: yup.string().nullable().optional(),
  postalCode: yup.string().nullable().optional(),
  geoLocation: quickbooksCommonGeoLocationSchema.nullable().optional(),
  variation: quickbooksCommonContactVariationSchema.nullable().optional(),
  verification: quickbooksCommonVerificationSchema.nullable().optional(),
})

/** Qb_EmailAddress as returned by Projects GraphQL */
export const quickbooksProjectManagementEmailAddressEntrySchema = yup.object({
  domain: yup.string().nullable().optional(),
  email: yup.string().nullable().optional(),
  /** Local-part / label before @ per Intuit docs */
  name: yup.string().nullable().optional(),
  variation: quickbooksCommonContactVariationSchema.nullable().optional(),
  verification: quickbooksCommonVerificationSchema.nullable().optional(),
})

/** ProjectManagement_ExternalReference */
export const quickbooksProjectManagementExternalReferenceSchema = yup.object({
  externalBlob: yup.mixed().nullable().optional(),
  externalKey: yup.string().nullable().optional(),
  externalReferenceId: yup.string().nullable().optional(),
})

export const quickbooksProjectManagementProjectSchema = yup.object({
  id: yup.string().required(),
  name: yup.string().nullable().optional(),
  description: yup.string().nullable().optional(),
  startDate: yup.string().nullable().optional(),
  dueDate: yup.string().nullable().optional(),
  completedDate: yup.string().nullable().optional(),
  completionRate: yup.number().nullable().optional(),
  deleted: yup.boolean().nullable().optional(),
  pinned: yup.boolean().nullable().optional(),
  priority: yup.number().nullable().optional(),
  status: yup.string().nullable().optional(),
  type: yup.string().nullable().optional(),
  version: yup.number().nullable().optional(),
  client: quickbooksProjectManagementClientSchema.nullable().optional(),
  customer: quickbooksProjectManagementCustomerSchema.nullable().optional(),
  account: quickbooksProjectManagementCompanySchema.nullable().optional(),
  addresses: yup.array().of(quickbooksProjectManagementPostalAddressSchema).nullable().optional(),
  emailAddress: yup
    .array()
    .of(quickbooksProjectManagementEmailAddressEntrySchema)
    .nullable()
    .optional(),
  externalReferences: yup
    .array()
    .of(quickbooksProjectManagementExternalReferenceSchema)
    .nullable()
    .optional(),
  assignee: quickbooksProjectManagementPersonaSchema.nullable().optional(),
  completedBy: quickbooksProjectManagementUserSchema.nullable().optional(),
})

export const quickbooksGetProjectManagementProjectResponseSchema = yup.object({
  data: yup
    .object({
      projectManagementProject: quickbooksProjectManagementProjectSchema.nullable().required(),
    })
    .required(),
})

/** `ProjectManagement_OrderBy` enum values (GraphQL → string in JSON) */
export const QUICKBOOKS_PROJECT_MANAGEMENT_ORDER_BY_VALUES = [
  'COMPLETED_DATE_ASC',
  'COMPLETED_DATE_DESC',
  'DUE_DATE_ASC',
  'DUE_DATE_DESC',
  'NAME_ASC',
  'NAME_DESC',
  'START_DATE_ASC',
  'START_DATE_DESC',
  'STATUS_ASC',
  'STATUS_DESC',
  'TYPE_ASC',
  'TYPE_DESC',
] as const

/** `ProjectManagement_Status` enum values (GraphQL → string in JSON) */
export const QUICKBOOKS_PROJECT_MANAGEMENT_STATUS_VALUES = [
  'BLOCKED',
  'CANCELED',
  'COMPLETE',
  'IN_PROGRESS',
  'OPEN',
  'OTHER',
  'WAITING_ON_CLIENT',
] as const

export type QuickbooksProjectManagementStatus =
  (typeof QUICKBOOKS_PROJECT_MANAGEMENT_STATUS_VALUES)[number]

export const quickbooksProjectManagementProjectsConnectionSchema = yup.object({
  edges: yup
    .array()
    .of(
      yup.object({
        node: quickbooksProjectManagementProjectSchema.required(),
      }),
    )
    .required(),
  pageInfo: quickbooksGraphqlPageInfoSchema.required(),
  totalCount: yup.number().optional(),
})

export const quickbooksGetProjectManagementProjectsResponseSchema = yup.object({
  data: yup
    .object({
      projectManagementProjects: quickbooksProjectManagementProjectsConnectionSchema.required(),
    })
    .required(),
})

/** `ProjectManagement_Settings` from QuickBooks GraphQL `projectManagementSettings` */
export const quickbooksProjectManagementSettingsSchema = yup.object({
  id: yup.string().required(),
  projectEstimateCostChangesBlocked: yup.boolean().nullable().optional(),
  projectsBudgetsEnabled: yup.boolean().nullable().optional(),
  projectsEnabled: yup.boolean().nullable().optional(),
})

export const quickbooksGetProjectManagementSettingsResponseSchema = yup.object({
  data: yup
    .object({
      projectManagementSettings: quickbooksProjectManagementSettingsSchema.required(),
    })
    .required(),
})

/** Action step `data` for create project — `ProjectManagement_CreateProjectInput` (GraphQL) */
export const quickbooksCreateProjectManagementProjectActionStepInputSchema = yup.object({
  name: yup.string().required(),
  description: yup.string().notRequired(),
  type: yup.string().notRequired(),
  status: yup
    .string()
    .oneOf([...QUICKBOOKS_PROJECT_MANAGEMENT_STATUS_VALUES], 'Invalid ProjectManagement_Status')
    .notRequired(),
  startDate: yup.string().notRequired(),
  dueDate: yup.string().notRequired(),
  completedDate: yup.string().notRequired(),
  priority: yup.number().integer().min(0).max(9).notRequired(),
  pinned: yup.boolean().notRequired(),
  completionRate: yup.mixed().notRequired(),
  account: quickbooksProjectManagementGraphqlEntityIdInputOptionalIdSchema.notRequired(),
  addresses: yup.mixed().notRequired(),
  assignee: yup.mixed().notRequired(),
  client: quickbooksProjectManagementGraphqlEntityIdInputOptionalIdSchema.notRequired(),
  completedBy: yup.mixed().notRequired(),
  customer: quickbooksProjectManagementGraphqlEntityIdInputSchema.notRequired(),
  emailAddress: yup.mixed().notRequired(),
  externalReferences: yup.mixed().notRequired(),
})

export type QuickbooksCreateProjectManagementProjectActionStepInput = yup.InferType<
  typeof quickbooksCreateProjectManagementProjectActionStepInputSchema
>

/** `ProjectManagement_Error` member of the `ProjectManagement_ProjectResponse` union (create/delete) */
export const quickbooksProjectManagementErrorResultSchema = yup.object({
  __typename: yup.string().oneOf(['ProjectManagement_Error']).optional(),
  message: yup.string().nullable().optional(),
  path: yup.mixed().nullable().optional(),
  locations: yup.mixed().nullable().optional(),
  classification: yup.mixed().nullable().optional(),
  extensions: yup.mixed().nullable().optional(),
})

export type QuickbooksProjectManagementErrorResult = yup.InferType<
  typeof quickbooksProjectManagementErrorResultSchema
>

/**
 * GraphQL union `ProjectManagement_ProjectResponse` → `ProjectManagement_Project` | `ProjectManagement_Error`
 * (inline fragments in the document; the JSON value is one object or the other).
 */
export const quickbooksProjectManagementProjectUnionResultSchema = yup.lazy((value: unknown) => {
  if (typeof value === 'object' && value !== null) {
    const o = value as Record<string, unknown>
    if (o.__typename === 'ProjectManagement_Error' || (o.message != null && o.id == null)) {
      return quickbooksProjectManagementErrorResultSchema.required()
    }
  }
  return quickbooksProjectManagementProjectSchema.required()
})

export const isProjectManagementErrorUnionResult = (
  value: unknown,
): value is QuickbooksProjectManagementErrorResult => {
  if (typeof value !== 'object' || value === null) {
    return false
  }
  const o = value as Record<string, unknown>
  return o.__typename === 'ProjectManagement_Error' || (o.message != null && o.id == null)
}

/**
 * GraphQL union `ProjectManagement_SettingsResponse` → `ProjectManagement_Settings` | `ProjectManagement_Error`.
 */
export const quickbooksProjectManagementSettingsUnionResultSchema = yup.lazy((value: unknown) => {
  if (typeof value === 'object' && value !== null) {
    const o = value as Record<string, unknown>
    if (o.__typename === 'ProjectManagement_Error' || (o.message != null && o.id == null)) {
      return quickbooksProjectManagementErrorResultSchema.required()
    }
  }
  return quickbooksProjectManagementSettingsSchema.required()
})

export const quickbooksCreateProjectManagementProjectGraphqlResponseSchema = yup.object({
  data: yup
    .object({
      projectManagementCreateProject: quickbooksProjectManagementProjectUnionResultSchema,
    })
    .required(),
})

export type QuickbooksCreateProjectManagementProjectGraphqlResponse = yup.InferType<
  typeof quickbooksCreateProjectManagementProjectGraphqlResponseSchema
>

/** Action step `data` for delete project — `ProjectManagement_DeleteProjectInput` (GraphQL) */
export const quickbooksDeleteProjectManagementProjectActionStepInputSchema = yup.object({
  id: yup.string().required(),
  version: yup.number().integer().required(),
})

export type QuickbooksDeleteProjectManagementProjectActionStepInput = yup.InferType<
  typeof quickbooksDeleteProjectManagementProjectActionStepInputSchema
>

export const quickbooksDeleteProjectManagementProjectGraphqlResponseSchema = yup.object({
  data: yup
    .object({
      projectManagementDeleteProject: quickbooksProjectManagementProjectUnionResultSchema,
    })
    .required(),
})

export type QuickbooksDeleteProjectManagementProjectGraphqlResponse = yup.InferType<
  typeof quickbooksDeleteProjectManagementProjectGraphqlResponseSchema
>

export const quickbooksProjectManagementUpdateSettingsGraphqlResponseSchema = yup.object({
  data: yup
    .object({
      projectManagementUpdateSettings: quickbooksProjectManagementSettingsUnionResultSchema,
    })
    .required(),
})

/**
 * Project update write: `id` and `version` (last fetch) plus any subset of project fields. Each
 * set field is sent to the matching GraphQL mutation; `version` is advanced after each response.
 * Fields without a dedicated mutation (e.g. `type`, `assignee`, `externalReferences`) are sent in
 * one `projectManagementUpdateProject` call.
 *
 * `projectManagementSettings` is rejected: that Intuit input is company-level
 * (`projectManagementUpdateSettings`) and must not ride along on a PROJECT update.
 * Existing steps that still include the field fail validation at run time — remove it
 * from the step rather than expecting a no-op or a partial project update.
 */
export const quickbooksUpdateProjectManagementProjectActionStepInputSchema = yup
  .object({
    id: yup.string().required(),
    version: yup.number().integer().required(),
    name: yup.string().notRequired(),
    description: yup.string().notRequired(),
    type: yup.string().notRequired(),
    status: yup
      .string()
      .oneOf([...QUICKBOOKS_PROJECT_MANAGEMENT_STATUS_VALUES], 'Invalid ProjectManagement_Status')
      .notRequired(),
    startDate: yup.string().notRequired(),
    dueDate: yup.string().notRequired(),
    completedDate: yup.string().notRequired(),
    priority: yup.number().integer().min(0).max(9).notRequired(),
    pinned: yup.boolean().notRequired(),
    completionRate: yup.mixed().notRequired(),
    account: quickbooksProjectManagementGraphqlEntityIdInputOptionalIdSchema.notRequired(),
    addresses: yup.mixed().notRequired(),
    assignee: yup.mixed().notRequired(),
    client: quickbooksProjectManagementGraphqlEntityIdInputOptionalIdSchema.notRequired(),
    customer: quickbooksProjectManagementGraphqlEntityIdInputOptionalIdSchema.notRequired(),
    completedBy: yup.mixed().notRequired(),
    emailAddress: yup.mixed().notRequired(),
    externalReferences: yup.mixed().notRequired(),
    projectManagementSettings: yup
      .mixed()
      .notRequired()
      .test(
        'forbidden-company-settings',
        'projectManagementSettings cannot be set on a PROJECT update because it mutates QuickBooks company settings. Remove it from this step; PROJECT update only changes a single project.',
        (value) => value === undefined,
      ),
  })
  .test(
    'qb-project-update-one-operation',
    'Provide at least one project field to update',
    (value) => {
      if (!value) {
        return false
      }
      const rest: Record<string, unknown> = { ...value }
      delete rest.id
      delete rest.version
      delete rest.projectManagementSettings
      return Object.values(rest).some((field) => {
        if (field === undefined || field === null) {
          return false
        }
        if (typeof field === 'object' && !Array.isArray(field) && Object.keys(field).length === 0) {
          return false
        }
        return true
      })
    },
  )
export type QuickbooksUpdateProjectManagementProjectActionStepInput = yup.InferType<
  typeof quickbooksUpdateProjectManagementProjectActionStepInputSchema
>

export type QuickbooksGetProjectManagementProjectResponse = yup.InferType<
  typeof quickbooksGetProjectManagementProjectResponseSchema
>

export type QuickbooksGetProjectManagementProjectsResponse = yup.InferType<
  typeof quickbooksGetProjectManagementProjectsResponseSchema
>

export type QuickbooksGetProjectManagementSettingsResponse = yup.InferType<
  typeof quickbooksGetProjectManagementSettingsResponseSchema
>

// Custom Field Definitions -------------------------------------------------------------------------
// Intuit GraphQL Premium API: appFoundationsCustomFieldDefinitions
// Returns the custom field definitions configured on the connected QuickBooks account. Custom
// fields are user-defined attributes attached to QuickBooks entities (transactions, invoices, etc.)
// unlike Custom Dimensions which are a separate tagging concept.
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/queries/appFoundationsCustomFieldDefinitions

/** Data type of the custom field, per AppFoundations_CustomExtensionDataType enum. */
export const quickbooksCustomFieldDataTypes = [
  'DATE',
  'NUMBER',
  'OBJECT_LIST',
  'STRING',
  'STRING_LIST',
  'UNKNOWN',
] as const

/**
 * Canonical values for `associations[].associatedEntity` (URI-style paths) observed on
 * the live IES schema. Intuit's docs example value `"transaction"` is rejected.
 *
 * UI category → URI mapping (sandbox-verified 2026-04-29 by creating one custom field per
 * category in the IES UI and reading them back via Get Custom Field Definitions):
 * - `/transactions/Transaction` — UI category "Transaction"
 * - `/network/Contact` — UI categories "Customer" and "Vendor" share this URI;
 *   sub-association (`CUSTOMER` vs `VENDOR`) differentiates them
 * - `/work/Project` — UI category "Project"
 *
 * Schema validation stays permissive (no Yup `.oneOf`) since Intuit's domain config can ship
 * additional entity URIs we haven't seen.
 */
export const quickbooksCustomFieldAssociatedEntities = [
  '/transactions/Transaction',
  '/network/Contact',
  '/work/Project',
] as const

/**
 * Canonical values for `subAssociations[].associatedEntity` (SCREAMING_SNAKE_CASE codes),
 * sandbox-verified 2026-04-29 by creating one custom field per UI form and reading via Get.
 *
 * Contact-parent subs (`/network/Contact`):
 * - `CUSTOMER` — Customer category
 * - `VENDOR` — Vendor category
 *
 * Project-parent sub (`/work/Project`):
 * - `PROJECT`
 *
 * Transaction-parent subs (`/transactions/Transaction`) — note the `SALE_*` / `PURCHASE_*`
 * prefix scheme (not the form name as printed in the UI):
 * - `SALE` — Sales Receipt
 * - `SALE_INVOICE` — Invoice
 * - `SALE_ESTIMATE` — Estimate
 * - `SALE_CREDIT` — Credit Memo
 * - `SALE_REFUND` — Refund Receipt
 * - `SALE_ORDER` — Sales Order
 * - `PURCHASE_ORDER` — Purchase Order
 * - `PURCHASE_BILL` — Bill
 * - `PURCHASE_CHECK` — Check
 * - `PURCHASE` — Expense
 * - `PURCHASE_CREDIT` — Vendor Credit
 * - `PURCHASE_CREDIT_CARD_CREDIT` — Credit Card Credit
 *
 * Discover canonical values for any sandbox by creating fields via the IES UI and running
 * Get Custom Field Definitions (the read query selects `associations.subAssociations.associatedEntity`).
 */
export const quickbooksCustomFieldSubAssociationEntities = [
  'CUSTOMER',
  'VENDOR',
  'PROJECT',
  'SALE',
  'SALE_INVOICE',
  'SALE_ESTIMATE',
  'SALE_CREDIT',
  'SALE_REFUND',
  'SALE_ORDER',
  'PURCHASE_ORDER',
  'PURCHASE_BILL',
  'PURCHASE_CHECK',
  'PURCHASE',
  'PURCHASE_CREDIT',
  'PURCHASE_CREDIT_CARD_CREDIT',
] as const

/** Subset of association fields the live IES schema returns; canonical strings differ
 * from Intuit's docs example (see `project_quickbooks_custom_field_definitions_associations` memory). */
export const quickbooksCustomFieldDefinitionSubAssociationSchema = yup.object({
  associatedEntity: yup.string().nullable().optional(),
})

export const quickbooksCustomFieldDefinitionAssociationSchema = yup.object({
  associatedEntity: yup.string().nullable().optional(),
  subAssociations: yup
    .array()
    .of(quickbooksCustomFieldDefinitionSubAssociationSchema.required())
    .nullable()
    .optional(),
})

export const quickbooksCustomFieldDefinitionSchema = yup.object({
  id: yup.string().required(),
  label: yup.string().nullable().optional(),
  active: yup.boolean().nullable().optional(),
  dataType: yup.string().oneOf(quickbooksCustomFieldDataTypes).required(),
  colorCode: yup.string().nullable().optional(),
  entityVersion: yup.number().integer().nullable().optional(),
  legacyIDV2: yup.string().nullable().optional(),
  associations: yup
    .array()
    .of(quickbooksCustomFieldDefinitionAssociationSchema.required())
    .nullable()
    .optional(),
})

export const quickbooksGetCustomFieldDefinitionsResponseSchema = yup.object({
  data: yup
    .object({
      appFoundationsCustomFieldDefinitions: yup
        .object({
          edges: yup
            .array()
            .of(yup.object({ node: quickbooksCustomFieldDefinitionSchema.required() }))
            .required(),
          pageInfo: quickbooksGraphqlPageInfoSchema.nullable().optional(),
        })
        .required(),
    })
    .required(),
})

export const quickbooksCreateDimensionActionStepInputSchema = yup.object({
  definitionId: yup.string().required(),
  label: yup.string().required(),
  parentId: yup.string().nullable().optional(),
})
export type QuickbooksCreateDimensionActionStepInput = yup.InferType<
  typeof quickbooksCreateDimensionActionStepInputSchema
>

export const quickbooksUpdateDimensionActionStepInputSchema = yup.object({
  definitionId: yup.string().required(),
  id: yup.string().required(),
  label: yup.string().nullable().optional(),
})
export type QuickbooksUpdateDimensionActionStepInput = yup.InferType<
  typeof quickbooksUpdateDimensionActionStepInputSchema
>

const quickbooksCustomDimensionValueSharedInfoSchema = yup
  .object({
    sharedAccountIds: yup.array().of(yup.string().required()).optional(),
    sharedDefinitionId: yup.string().nullable().optional(),
    sharedId: yup.string().nullable().optional(),
    sharedParentId: yup.string().nullable().optional(),
  })
  .optional()

export const quickbooksCreateDimensionResponseSchema = yup.object({
  data: yup
    .object({
      appFoundationsCommonCreateCustomDimensionValue: yup
        .object({
          active: yup.boolean().required(),
          definitionId: yup.string().required(),
          fullyQualifiedLabel: yup.string().nullable().optional(),
          id: yup.string().required(),
          label: yup.string().required(),
          level: yup.number().nullable().optional(),
          parentId: yup.string().nullable().optional(),
          sharedInfo: quickbooksCustomDimensionValueSharedInfoSchema,
        })
        .required(),
    })
    .required(),
})

export const quickbooksUpdateDimensionResponseSchema = yup.object({
  data: yup
    .object({
      appFoundationsCommonUpdateCustomDimensionValue: yup
        .object({
          active: yup.boolean().required(),
          definitionId: yup.string().required(),
          fullyQualifiedLabel: yup.string().nullable().optional(),
          id: yup.string().required(),
          label: yup.string().required(),
          level: yup.number().nullable().optional(),
          parentId: yup.string().nullable().optional(),
          sharedInfo: quickbooksCustomDimensionValueSharedInfoSchema,
        })
        .required(),
    })
    .required(),
})

// Mutation: appFoundationsCreateCustomFieldDefinition
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/mutations/appFoundationsCreateCustomFieldDefinition

/** Possible operations on a custom field association/sub-association. */
export const quickbooksCustomExtensionAllowedOperations = ['PRINT', 'UNKNOWN'] as const

/** Whether an entity association is on the inclusion or exclusion list. */
export const quickbooksCustomExtensionAssociationConditions = [
  'EXCLUDED',
  'INCLUDED',
  'UNKNOWN',
] as const

/** Validation enforcement level for a custom field definition. */
export const quickbooksCustomExtensionValidationEnforcements = ['HARD', 'SOFT'] as const

export const quickbooksCustomExtensionValidationOptionsInputSchema = yup.object({
  enforcement: yup
    .string()
    .oneOf([...quickbooksCustomExtensionValidationEnforcements])
    .nullable()
    .optional(),
  required: yup.boolean().nullable().optional(),
})

export const quickbooksCustomExtensionSubAssociationInputSchema = yup.object({
  associatedEntity: yup.string().required(),
  active: yup.boolean().nullable().optional(),
  allowedOperations: yup
    .array()
    .of(
      yup
        .string()
        .oneOf([...quickbooksCustomExtensionAllowedOperations])
        .required(),
    )
    .nullable()
    .optional(),
  validationOptions: quickbooksCustomExtensionValidationOptionsInputSchema.nullable().optional(),
})

export const quickbooksCustomExtensionAssociationsInputSchema = yup.object({
  associatedEntity: yup.string().required(),
  active: yup.boolean().nullable().optional(),
  allowedOperations: yup
    .array()
    .of(
      yup
        .string()
        .oneOf([...quickbooksCustomExtensionAllowedOperations])
        .required(),
    )
    .nullable()
    .optional(),
  associationCondition: yup
    .string()
    .oneOf([...quickbooksCustomExtensionAssociationConditions])
    .nullable()
    .optional(),
  subAssociations: yup
    .array()
    .of(quickbooksCustomExtensionSubAssociationInputSchema.required())
    .nullable()
    .optional(),
  validationOptions: quickbooksCustomExtensionValidationOptionsInputSchema.nullable().optional(),
})

export const quickbooksCustomFieldDefinitionDropDownOptionInputSchema = yup.object({
  value: yup.string().required(),
  id: yup.string().nullable().optional(),
  active: yup.boolean().nullable().optional(),
  order: yup.number().integer().nullable().optional(),
})

/** Action step input schema for QuickBooks Create Custom Field Definition. */
export const quickbooksCreateCustomFieldDefinitionActionStepInputSchema = yup.object({
  label: yup.string().required(),
  dataType: yup
    .string()
    .oneOf([...quickbooksCustomFieldDataTypes])
    .required(),
  associations: yup
    .array()
    .of(quickbooksCustomExtensionAssociationsInputSchema.required())
    .min(1)
    .required(),
  active: yup.boolean().nullable().optional(),
  dropDownOptions: yup
    .array()
    .of(quickbooksCustomFieldDefinitionDropDownOptionInputSchema.required())
    .nullable()
    .optional(),
})

export type QuickbooksCreateCustomFieldDefinitionActionStepInput = yup.InferType<
  typeof quickbooksCreateCustomFieldDefinitionActionStepInputSchema
>

export const quickbooksCreateCustomFieldDefinitionResponseSchema = yup.object({
  data: yup
    .object({
      appFoundationsCreateCustomFieldDefinition: quickbooksCustomFieldDefinitionSchema.required(),
    })
    .required(),
})

// Mutation: appFoundationsUpdateCustomFieldDefinition
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/mutations/appFoundationsUpdateCustomFieldDefinition

/** Action step input schema for QuickBooks Update Custom Field Definition. */
export const quickbooksUpdateCustomFieldDefinitionActionStepInputSchema = yup.object({
  id: yup.string().required(),
  active: yup.boolean().nullable().optional(),
  associations: yup
    .array()
    .of(quickbooksCustomExtensionAssociationsInputSchema.required())
    .nullable()
    .optional(),
  dataType: yup
    .string()
    .oneOf([...quickbooksCustomFieldDataTypes])
    .nullable()
    .optional(),
  dropDownOptions: yup
    .array()
    .of(quickbooksCustomFieldDefinitionDropDownOptionInputSchema.required())
    .nullable()
    .optional(),
  label: yup.string().nullable().optional(),
  // Intuit's docs mark legacyIDV2 as optional, but the live IES schema rejects
  // Update mutations without it (sandbox-verified 2026-04-29: FIELD_MISSING from
  // custom-extensions-service). Kept optional in the schema to mirror docs;
  // PMOs map this in from Get Custom Field Definitions.
  legacyIDV2: yup.string().nullable().optional(),
})

export type QuickbooksUpdateCustomFieldDefinitionActionStepInput = yup.InferType<
  typeof quickbooksUpdateCustomFieldDefinitionActionStepInputSchema
>

export const quickbooksUpdateCustomFieldDefinitionResponseSchema = yup.object({
  data: yup
    .object({
      appFoundationsUpdateCustomFieldDefinition: quickbooksCustomFieldDefinitionSchema.required(),
    })
    .required(),
})

// Sales Tax GraphQL (`indirectTaxCalculateSaleTransactionTax`) -------------------------------------
// Field names follow the official qbexternal SDL. Mutation returns
// `IndirectTax_TaxCalculationPayload` (concrete type: `taxCalculation` + `warnings`).
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/mutations/indirectTaxCalculateSaleTransactionTax
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/inputobjects/IndirectTax_TaxCalculationInput
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/objects/IndirectTax_TaxCalculationPayload

/** `Common_MoneyAmount` / `Common_MoneyAmountInput` (`value` + optional `currency`). */
export const quickbooksIndirectTaxMoneySchema = yup.object({
  value: yup.number().nullable().optional(),
  currency: yup.string().nullable().optional(),
})

/** `Common_MoneyAmountInput` when a money object is present. */
export const quickbooksIndirectTaxMoneyInputSchema = yup.object({
  value: yup.number().required(),
  currency: yup.string().optional().nullable(),
})

/** `IndirectTax_AddressInput` — free-form and/or structured fields. */
export const quickbooksIndirectTaxAddressInputSchema = yup
  .object({
    freeFormAddressLine: yup.string().optional(),
    city: yup.string().optional(),
    country: yup.string().optional(),
    county: yup.string().optional(),
    postalCode: yup.string().optional(),
    state: yup.string().optional(),
    streetAddressLine1: yup.string().optional(),
    streetAddressLine2: yup.string().optional(),
    streetAddressLine3: yup.string().optional(),
  })
  .test('address-has-a-field', 'Address must include at least one field', (value) => {
    if (value == null) {
      return true
    }
    return Object.values(value).some((field) => field != null && field !== '')
  })

const optionalMoneyInputSchema = quickbooksIndirectTaxMoneyInputSchema
  .optional()
  .nullable()
  .default(undefined)

const optionalAddressInputSchema = quickbooksIndirectTaxAddressInputSchema
  .optional()
  .nullable()
  .default(undefined)

/** `IndirectTax_ShipmentInput` — either `shipFromAddress` or `shipToAddress` is required. */
export const quickbooksIndirectTaxShippingInputSchema = yup
  .object({
    shipFromAddress: optionalAddressInputSchema,
    shipToAddress: optionalAddressInputSchema,
    shippingFee: optionalMoneyInputSchema,
  })
  .test('ship-from-or-ship-to', 'Either shipFromAddress or shipToAddress is required', (value) => {
    if (value == null) {
      return false
    }
    return value.shipFromAddress != null || value.shipToAddress != null
  })

export const quickbooksIndirectTaxLineItemInputSchema = yup.object({
  // Defaults to 1 in the Intuit schema when omitted.
  numberOfUnits: yup.number().optional().nullable(),
  pricePerUnitExcludingTaxes: optionalMoneyInputSchema,
  totalPriceExcludingTaxes: optionalMoneyInputSchema,
  productVariantTaxability: yup
    .object({
      productVariantId: yup.string().required(),
    })
    .optional()
    .nullable()
    .default(undefined),
})

/**
 * Action step `data` for calculate tax — GraphQL `IndirectTax_TaxCalculationInput`.
 * `subject` is optional (no exemption when omitted). `lineItems` may be `[]`.
 */
export const quickbooksCalculateSaleTransactionTaxActionStepInputSchema = yup.object({
  transactionDate: yup.string().required(),
  subject: yup
    .object({
      qbCustomerId: yup.string().required(),
    })
    .optional()
    .nullable()
    .default(undefined),
  shipping: quickbooksIndirectTaxShippingInputSchema.required(),
  lineItems: yup.array().of(quickbooksIndirectTaxLineItemInputSchema.required()).required(),
})
export type QuickbooksCalculateSaleTransactionTaxActionStepInput = {
  transactionDate: string
  subject?: { qbCustomerId: string } | null
  shipping: {
    shipFromAddress?: yup.InferType<typeof quickbooksIndirectTaxAddressInputSchema> | null
    shipToAddress?: yup.InferType<typeof quickbooksIndirectTaxAddressInputSchema> | null
    shippingFee?: yup.InferType<typeof quickbooksIndirectTaxMoneyInputSchema> | null
  }
  lineItems: Array<{
    numberOfUnits?: number | null
    pricePerUnitExcludingTaxes?: yup.InferType<typeof quickbooksIndirectTaxMoneyInputSchema> | null
    totalPriceExcludingTaxes?: yup.InferType<typeof quickbooksIndirectTaxMoneyInputSchema> | null
    productVariantTaxability?: { productVariantId: string } | null
  }>
}

/** `IndirectTax_Error` interface (`errorCode`, `message`). */
export const quickbooksIndirectTaxErrorSchema = yup.object({
  errorCode: yup.string().nullable().optional(),
  message: yup.string().nullable().optional(),
})

type IndirectTaxAppliedRateValue = number | { value?: number | null; currency?: string | null }

/**
 * `IndirectTax_AppliedRate` union: `IndirectTax_RatePercentage.rate` is Decimal;
 * `IndirectTax_RateAmount.rate` is `Common_MoneyAmount`.
 */
export const quickbooksIndirectTaxAppliedRateSchema = yup.object({
  rate: yup
    .mixed<IndirectTaxAppliedRateValue>((value: unknown): value is IndirectTaxAppliedRateValue => {
      if (value == null || typeof value === 'number') {
        return true
      }
      return typeof value === 'object'
    })
    .nullable()
    .optional(),
})

export const quickbooksIndirectTaxTaxRateSchema = yup.object({
  taxRateReferenceId: yup.string().nullable().optional(),
})

export const quickbooksIndirectTaxTaxRateDetailSchema = yup.object({
  name: yup.string().nullable().optional(),
  rateClassification: yup.string().nullable().optional(),
  rateLevel: yup.string().nullable().optional(),
  taxRateComplianceCode: yup.string().nullable().optional(),
  taxRate: quickbooksIndirectTaxTaxRateSchema.nullable().optional(),
})

export const quickbooksIndirectTaxTaxCalculationDetailSchema = yup.object({
  ratePercentageApplied: quickbooksIndirectTaxAppliedRateSchema.nullable().optional(),
  taxableAmount: quickbooksIndirectTaxMoneySchema.nullable().optional(),
  taxAmount: quickbooksIndirectTaxMoneySchema.nullable().optional(),
  taxExemptAmount: quickbooksIndirectTaxMoneySchema.nullable().optional(),
  taxCategory: yup
    .object({
      id: yup.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  taxRate: quickbooksIndirectTaxTaxRateDetailSchema.nullable().optional(),
})

/**
 * `IndirectTax_RawAddress` union (`IndirectTax_FreeFormAddress` | `IndirectTax_Address`) plus
 * structured fields on `IndirectTax_Address` itself.
 */
export const quickbooksIndirectTaxCalculatedAddressSchema = yup.object({
  streetAddressLine1: yup.string().nullable().optional(),
  streetAddressLine2: yup.string().nullable().optional(),
  streetAddressLine3: yup.string().nullable().optional(),
  city: yup.string().nullable().optional(),
  country: yup.string().nullable().optional(),
  county: yup.string().nullable().optional(),
  postalCode: yup.string().nullable().optional(),
  state: yup.string().nullable().optional(),
  rawAddress: yup
    .object({
      freeformAddressLine: yup.string().nullable().optional(),
      streetAddressLine1: yup.string().nullable().optional(),
      streetAddressLine2: yup.string().nullable().optional(),
      streetAddressLine3: yup.string().nullable().optional(),
      city: yup.string().nullable().optional(),
      country: yup.string().nullable().optional(),
      county: yup.string().nullable().optional(),
      postalCode: yup.string().nullable().optional(),
      state: yup.string().nullable().optional(),
    })
    .nullable()
    .optional(),
})

export const quickbooksIndirectTaxCalculatedLineItemSchema = yup.object({
  numberOfUnits: yup.number().nullable().optional(),
  pricePerUnitExcludingTaxes: quickbooksIndirectTaxMoneySchema.nullable().optional(),
  totalPriceExcludingTaxes: quickbooksIndirectTaxMoneySchema.nullable().optional(),
  taxAmount: quickbooksIndirectTaxMoneySchema.nullable().optional(),
  productVariantTaxability: yup
    .object({
      classificationCode: yup.string().nullable().optional(),
      product: yup
        .object({
          id: yup.string().nullable().optional(),
        })
        .nullable()
        .optional(),
    })
    .nullable()
    .optional(),
  taxDetails: yup
    .array()
    .of(quickbooksIndirectTaxTaxCalculationDetailSchema.required())
    .nullable()
    .optional(),
})

export const quickbooksIndirectTaxCalculationSchema = yup.object({
  id: yup.string().nullable().optional(),
  transactionDate: yup.string().nullable().optional(),
  subject: yup
    .object({
      customer: yup
        .object({
          id: yup.string().nullable().optional(),
        })
        .nullable()
        .optional(),
      taxExemption: yup.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  shipping: yup
    .object({
      shipFromAddress: quickbooksIndirectTaxCalculatedAddressSchema.nullable().optional(),
      shipToAddress: quickbooksIndirectTaxCalculatedAddressSchema.nullable().optional(),
      shippingFee: quickbooksIndirectTaxMoneySchema.nullable().optional(),
      taxAmount: quickbooksIndirectTaxMoneySchema.nullable().optional(),
      taxDetails: yup
        .array()
        .of(quickbooksIndirectTaxTaxCalculationDetailSchema.required())
        .nullable()
        .optional(),
    })
    .nullable()
    .optional(),
  lineItems: yup
    .object({
      nodes: yup
        .array()
        .of(quickbooksIndirectTaxCalculatedLineItemSchema.required())
        .nullable()
        .optional(),
    })
    .nullable()
    .optional(),
  taxTotals: yup
    .object({
      // Object schemas default to {}; without default(undefined), a missing key becomes {} and
      // .required() still passes. Callers map this amount into invoices, so require the field.
      totalTaxAmountExcludingShipping: quickbooksIndirectTaxMoneySchema
        .default(undefined)
        .required(),
      aggregatedTaxesExcludingShippingByRate: yup
        .array()
        .of(quickbooksIndirectTaxTaxCalculationDetailSchema.required())
        .nullable()
        .optional(),
    })
    .default(undefined)
    .required(),
})

export const quickbooksCalculateSaleTransactionTaxGraphqlResponseSchema = yup.object({
  data: yup
    .object({
      indirectTaxCalculateSaleTransactionTax: yup
        .object({
          // Object schemas default to {}; without default(undefined), a missing taxCalculation
          // becomes {} and .required() passes because all calculation fields are optional.
          taxCalculation: quickbooksIndirectTaxCalculationSchema.default(undefined).required(),
          warnings: yup
            .array()
            .of(quickbooksIndirectTaxErrorSchema.required())
            .nullable()
            .optional(),
        })
        .required(),
    })
    .required(),
})
export type QuickbooksCalculateSaleTransactionTaxGraphqlResponse = yup.InferType<
  typeof quickbooksCalculateSaleTransactionTaxGraphqlResponseSchema
>

// Payroll benefit policy GraphQL (`payrollCreateBenefitPolicy` / `payrollUpdateBenefitPolicy`) -----
// Field names follow the official qbexternal SDL. Mutations return the union
// `Payroll_CreateBenefitPolicyPayload` / `Payroll_UpdateBenefitPolicyPayload`
// (`PayloadSuccess.policy` | `PayloadError`).
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/mutations/payrollCreateBenefitPolicy
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/inputobjects/Payroll_CreateBenefitPolicyInput
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/objects/Payroll_BenefitPolicy
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/objects/Payroll_VariableStringField
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/objects/Qb_AlternateId

/** `Payroll_VariableStringField` (`key` + `description` + `value`). */
export const quickbooksPayrollVariableStringFieldSchema = yup.object({
  key: yup.string().nullable().optional(),
  description: yup.string().nullable().optional(),
  value: yup.string().nullable().optional(),
})

/** `Qb_AlternateId` (`id` + `nameSpace`). */
export const quickbooksAlternateIdSchema = yup.object({
  id: yup.string().nullable().optional(),
  nameSpace: yup.string().nullable().optional(),
})

/** `Payroll_BenefitPolicy` returned on create/update success. */
export const quickbooksPayrollBenefitPolicySchema = yup.object({
  id: yup.string().required(),
  name: yup.string().nullable().optional(),
  active: yup.boolean().nullable().optional(),
  alternateIds: yup.array().of(quickbooksAlternateIdSchema.required()).nullable().optional(),
  // Nested yup objects default to {}. Keep omitted QBO fields absent instead of `{}`.
  category: quickbooksPayrollVariableStringFieldSchema.nullable().optional().default(undefined),
  subCategory: quickbooksPayrollVariableStringFieldSchema.nullable().optional().default(undefined),
  statutoryType: quickbooksPayrollVariableStringFieldSchema
    .nullable()
    .optional()
    .default(undefined),
})

export type QuickbooksPayrollBenefitPolicy = yup.InferType<
  typeof quickbooksPayrollBenefitPolicySchema
>

/**
 * `Payroll_CreateBenefitPolicyInput`. SDL does not mark fields required; workflow examples always
 * send `name` and `statutoryType`, so the action step requires those two.
 *
 * @see https://developer.intuit.com/app/developer/payroll-time/docs/workflows/payroll-benefits
 */
export const quickbooksCreateBenefitPolicyActionStepInputSchema = yup.object({
  name: yup.string().required(),
  statutoryType: yup.string().required(),
  isDefault: yup.boolean().nullable().optional(),
  providerName: yup.string().nullable().optional(),
  providerReferenceId: yup.string().nullable().optional(),
})

export type QuickbooksCreateBenefitPolicyActionStepInput = yup.InferType<
  typeof quickbooksCreateBenefitPolicyActionStepInputSchema
>

const payrollBenefitPolicyPayloadErrorTypenames = [
  'Payroll_CreateBenefitPolicyPayloadError',
  'Payroll_UpdateBenefitPolicyPayloadError',
] as const

/** `Payroll_CreateBenefitPolicyPayloadError` / `Payroll_UpdateBenefitPolicyPayloadError`. */
export const quickbooksPayrollBenefitPolicyPayloadErrorSchema = yup.object({
  __typename: yup
    .string()
    .oneOf([...payrollBenefitPolicyPayloadErrorTypenames])
    .optional(),
  code: yup.string().nullable().optional(),
  employeeDeductionId: yup.string().nullable().optional(),
  employeeId: yup.string().nullable().optional(),
  employerDeductionId: yup.string().nullable().optional(),
  message: yup.string().nullable().optional(),
  type: yup.string().nullable().optional(),
})

export type QuickbooksPayrollBenefitPolicyPayloadError = yup.InferType<
  typeof quickbooksPayrollBenefitPolicyPayloadErrorSchema
>

const payrollBenefitPolicyPayloadSuccessTypenames = [
  'Payroll_CreateBenefitPolicyPayloadSuccess',
  'Payroll_UpdateBenefitPolicyPayloadSuccess',
] as const

export const quickbooksPayrollBenefitPolicyPayloadSuccessSchema = yup.object({
  __typename: yup
    .string()
    .oneOf([...payrollBenefitPolicyPayloadSuccessTypenames])
    .optional(),
  policy: quickbooksPayrollBenefitPolicySchema.default(undefined).required(),
})

export const isPayrollBenefitPolicyPayloadError = (
  value: unknown,
): value is QuickbooksPayrollBenefitPolicyPayloadError => {
  if (typeof value !== 'object' || value === null) {
    return false
  }
  const o = value as Record<string, unknown>
  return (
    o.__typename === 'Payroll_CreateBenefitPolicyPayloadError' ||
    o.__typename === 'Payroll_UpdateBenefitPolicyPayloadError' ||
    (o.policy == null && (o.message != null || o.code != null || o.type != null))
  )
}

export const formatPayrollBenefitPolicyPayloadError = (
  error: QuickbooksPayrollBenefitPolicyPayloadError,
): string => {
  const parts = [error.type, error.message, error.code].filter((part): part is string => {
    return part != null && part !== ''
  })
  if (parts.length > 0) {
    return parts.join(': ')
  }
  return JSON.stringify(error)
}

/**
 * GraphQL union `Payroll_CreateBenefitPolicyPayload` /
 * `Payroll_UpdateBenefitPolicyPayload`.
 */
export const quickbooksPayrollBenefitPolicyPayloadUnionSchema = yup.lazy((value: unknown) => {
  if (isPayrollBenefitPolicyPayloadError(value)) {
    return quickbooksPayrollBenefitPolicyPayloadErrorSchema.required()
  }
  return quickbooksPayrollBenefitPolicyPayloadSuccessSchema.required()
})

export const quickbooksCreateBenefitPolicyGraphqlResponseSchema = yup.object({
  data: yup
    .object({
      payrollCreateBenefitPolicy: quickbooksPayrollBenefitPolicyPayloadUnionSchema,
    })
    .required(),
})

export type QuickbooksCreateBenefitPolicyGraphqlResponse = yup.InferType<
  typeof quickbooksCreateBenefitPolicyGraphqlResponseSchema
>

/**
 * `Payroll_UpdateBenefitPolicyInput`. SDL: `id`, optional `name` / `isDefault` /
 * `providerReferenceId`. `statutoryType` is not writable on update.
 *
 * @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/inputobjects/Payroll_UpdateBenefitPolicyInput
 */
export const quickbooksUpdateBenefitPolicyActionStepInputSchema = yup.object({
  id: yup.string().required(),
  name: yup.string().nullable().optional(),
  isDefault: yup.boolean().nullable().optional(),
  providerReferenceId: yup.string().nullable().optional(),
})

export type QuickbooksUpdateBenefitPolicyActionStepInput = yup.InferType<
  typeof quickbooksUpdateBenefitPolicyActionStepInputSchema
>

export const quickbooksUpdateBenefitPolicyGraphqlResponseSchema = yup.object({
  data: yup
    .object({
      payrollUpdateBenefitPolicy: quickbooksPayrollBenefitPolicyPayloadUnionSchema,
    })
    .required(),
})

export type QuickbooksUpdateBenefitPolicyGraphqlResponse = yup.InferType<
  typeof quickbooksUpdateBenefitPolicyGraphqlResponseSchema
>

// Payroll pension policy GraphQL (`payrollCreatePensionPolicy` / `payrollUpdatePensionPolicy`) -----
// Field names follow the official qbexternal SDL. Mutations return the union
// `Payroll_CreatePensionPolicyPayload` / `Payroll_UpdatePensionPolicyPayload`
// (`Payroll_CreatePensionPolicySuccess.policy` | `Payroll_DeductionError`).
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/mutations/payrollCreatePensionPolicy
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/inputobjects/Payroll_CreatePensionPolicyInput
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/objects/Payroll_PensionPolicy
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/objects/Payroll_DeductionError

/** `Payroll_PensionPolicy` returned on create/update success. */
export const quickbooksPayrollPensionPolicySchema = yup.object({
  id: yup.string().required(),
  name: yup.string().nullable().optional(),
  active: yup.boolean().nullable().optional(),
  alternateIds: yup.array().of(quickbooksAlternateIdSchema.required()).nullable().optional(),
  // Nested yup objects default to {}. Keep omitted QBO fields absent instead of `{}`.
  category: quickbooksPayrollVariableStringFieldSchema.nullable().optional().default(undefined),
  subCategory: quickbooksPayrollVariableStringFieldSchema.nullable().optional().default(undefined),
  statutoryType: quickbooksPayrollVariableStringFieldSchema
    .nullable()
    .optional()
    .default(undefined),
})

export type QuickbooksPayrollPensionPolicy = yup.InferType<
  typeof quickbooksPayrollPensionPolicySchema
>

/**
 * `Payroll_CreatePensionPolicyInput`. SDL marks `name` and `statutoryType` required.
 *
 * @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/inputobjects/Payroll_CreatePensionPolicyInput
 */
export const quickbooksCreatePensionPolicyActionStepInputSchema = yup.object({
  name: yup.string().required(),
  statutoryType: yup.string().required(),
})

export type QuickbooksCreatePensionPolicyActionStepInput = yup.InferType<
  typeof quickbooksCreatePensionPolicyActionStepInputSchema
>

/**
 * `Payroll_UpdatePensionPolicyInput`. SDL: required `id` + `name`; optional `active`.
 * `statutoryType` is not writable on update.
 *
 * @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/inputobjects/Payroll_UpdatePensionPolicyInput
 */
export const quickbooksUpdatePensionPolicyActionStepInputSchema = yup.object({
  id: yup.string().required(),
  name: yup.string().required(),
  active: yup.boolean().nullable().optional(),
})

export type QuickbooksUpdatePensionPolicyActionStepInput = yup.InferType<
  typeof quickbooksUpdatePensionPolicyActionStepInputSchema
>

/** `Payroll_DeductionError` union member on create/update pension policy. */
export const quickbooksPayrollDeductionErrorSchema = yup.object({
  __typename: yup.string().oneOf(['Payroll_DeductionError']).optional(),
  code: yup.string().nullable().optional(),
  employeeDeductionId: yup.string().nullable().optional(),
  employeeId: yup.string().nullable().optional(),
  employerDeductionId: yup.string().nullable().optional(),
  message: yup.string().nullable().optional(),
  type: yup.string().nullable().optional(),
})

export type QuickbooksPayrollDeductionError = yup.InferType<
  typeof quickbooksPayrollDeductionErrorSchema
>

const payrollPensionPolicyPayloadSuccessTypenames = [
  'Payroll_CreatePensionPolicySuccess',
  'Payroll_UpdatePensionPolicySuccess',
] as const

export const quickbooksPayrollPensionPolicyPayloadSuccessSchema = yup.object({
  __typename: yup
    .string()
    .oneOf([...payrollPensionPolicyPayloadSuccessTypenames])
    .optional(),
  policy: quickbooksPayrollPensionPolicySchema.default(undefined).required(),
})

export const isPayrollDeductionError = (
  value: unknown,
): value is QuickbooksPayrollDeductionError => {
  if (typeof value !== 'object' || value === null) {
    return false
  }
  const o = value as Record<string, unknown>
  return (
    o.__typename === 'Payroll_DeductionError' ||
    (o.policy == null && (o.message != null || o.code != null || o.type != null))
  )
}

export const formatPayrollDeductionError = (error: QuickbooksPayrollDeductionError): string => {
  const parts = [error.type, error.message, error.code].filter((part): part is string => {
    return part != null && part !== ''
  })
  if (parts.length > 0) {
    return parts.join(': ')
  }
  return JSON.stringify(error)
}

/**
 * GraphQL union `Payroll_CreatePensionPolicyPayload` /
 * `Payroll_UpdatePensionPolicyPayload`.
 */
export const quickbooksPayrollPensionPolicyPayloadUnionSchema = yup.lazy((value: unknown) => {
  if (isPayrollDeductionError(value)) {
    return quickbooksPayrollDeductionErrorSchema.required()
  }
  return quickbooksPayrollPensionPolicyPayloadSuccessSchema.required()
})

export const quickbooksCreatePensionPolicyGraphqlResponseSchema = yup.object({
  data: yup
    .object({
      payrollCreatePensionPolicy: quickbooksPayrollPensionPolicyPayloadUnionSchema,
    })
    .required(),
})

export type QuickbooksCreatePensionPolicyGraphqlResponse = yup.InferType<
  typeof quickbooksCreatePensionPolicyGraphqlResponseSchema
>

export const quickbooksUpdatePensionPolicyGraphqlResponseSchema = yup.object({
  data: yup
    .object({
      payrollUpdatePensionPolicy: quickbooksPayrollPensionPolicyPayloadUnionSchema,
    })
    .required(),
})

export type QuickbooksUpdatePensionPolicyGraphqlResponse = yup.InferType<
  typeof quickbooksUpdatePensionPolicyGraphqlResponseSchema
>

// Payroll misc deduction policy GraphQL (`payrollCreateMiscDeductionPolicy` /
// `payrollUpdateMiscDeductionPolicy`) -------------------------------------------------------------
// Field names follow the documented qbexternal mutation + workflow examples.
// Mutations return `Payroll_CreateMiscDeductionPolicySuccess.policy` |
// `Payroll_DeductionError`.
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/mutations/payrollCreateMiscDeductionPolicy
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/inputobjects/Payroll_CreateMiscDeductionPolicyInput
// @see https://developer.intuit.com/app/developer/payroll-time/docs/workflows/payroll-deductions

/**
 * Policy object returned on create/update misc deduction success, and on
 * `payrollEmployerInfo.deductions` nodes. Documented mutation selection: `id`,
 * `alternateIds`, `name`, `category`, `subCategory`, `statutoryType`. Employer-info
 * also selects optional `active`.
 */
export const quickbooksPayrollMiscDeductionPolicySchema = yup.object({
  id: yup.string().required(),
  name: yup.string().nullable().optional(),
  active: yup.boolean().nullable().optional(),
  alternateIds: yup.array().of(quickbooksAlternateIdSchema.required()).nullable().optional(),
  // Nested yup objects default to {}. Keep omitted QBO fields absent instead of `{}`.
  category: quickbooksPayrollVariableStringFieldSchema.nullable().optional().default(undefined),
  subCategory: quickbooksPayrollVariableStringFieldSchema.nullable().optional().default(undefined),
  statutoryType: quickbooksPayrollVariableStringFieldSchema
    .nullable()
    .optional()
    .default(undefined),
})

export type QuickbooksPayrollMiscDeductionPolicy = yup.InferType<
  typeof quickbooksPayrollMiscDeductionPolicySchema
>

/**
 * `Payroll_CreateMiscDeductionPolicyInput`. Workflow examples always send `name` and
 * `statutoryType`.
 *
 * @see https://developer.intuit.com/app/developer/payroll-time/docs/workflows/payroll-deductions
 */
export const quickbooksCreateMiscDeductionPolicyActionStepInputSchema = yup.object({
  name: yup.string().required(),
  statutoryType: yup.string().required(),
})

export type QuickbooksCreateMiscDeductionPolicyActionStepInput = yup.InferType<
  typeof quickbooksCreateMiscDeductionPolicyActionStepInputSchema
>

const payrollMiscDeductionPolicyPayloadSuccessTypenames = [
  'Payroll_CreateMiscDeductionPolicySuccess',
  'Payroll_UpdateMiscDeductionPolicySuccess',
] as const

export const quickbooksPayrollMiscDeductionPolicyPayloadSuccessSchema = yup.object({
  __typename: yup
    .string()
    .oneOf([...payrollMiscDeductionPolicyPayloadSuccessTypenames])
    .optional(),
  policy: quickbooksPayrollMiscDeductionPolicySchema.default(undefined).required(),
})

/**
 * GraphQL union for create/update misc deduction policy payloads.
 */
export const quickbooksPayrollMiscDeductionPolicyPayloadUnionSchema = yup.lazy((value: unknown) => {
  if (isPayrollDeductionError(value)) {
    return quickbooksPayrollDeductionErrorSchema.required()
  }
  return quickbooksPayrollMiscDeductionPolicyPayloadSuccessSchema.required()
})

export const quickbooksCreateMiscDeductionPolicyGraphqlResponseSchema = yup.object({
  data: yup
    .object({
      payrollCreateMiscDeductionPolicy: quickbooksPayrollMiscDeductionPolicyPayloadUnionSchema,
    })
    .required(),
})

export type QuickbooksCreateMiscDeductionPolicyGraphqlResponse = yup.InferType<
  typeof quickbooksCreateMiscDeductionPolicyGraphqlResponseSchema
>

/**
 * `Payroll_UpdateMiscDeductionPolicyInput`. Workflow examples always send `id` and `name`.
 * `statutoryType` is not writable on update.
 *
 * @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/mutations/payrollUpdateMiscDeductionPolicy
 * @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/inputobjects/Payroll_UpdateMiscDeductionPolicyInput
 * @see https://developer.intuit.com/app/developer/payroll-time/docs/workflows/payroll-deductions
 */
export const quickbooksUpdateMiscDeductionPolicyActionStepInputSchema = yup.object({
  id: yup.string().required(),
  name: yup.string().required(),
})

export type QuickbooksUpdateMiscDeductionPolicyActionStepInput = yup.InferType<
  typeof quickbooksUpdateMiscDeductionPolicyActionStepInputSchema
>

export const quickbooksUpdateMiscDeductionPolicyGraphqlResponseSchema = yup.object({
  data: yup
    .object({
      payrollUpdateMiscDeductionPolicy: quickbooksPayrollMiscDeductionPolicyPayloadUnionSchema,
    })
    .required(),
})

export type QuickbooksUpdateMiscDeductionPolicyGraphqlResponse = yup.InferType<
  typeof quickbooksUpdateMiscDeductionPolicyGraphqlResponseSchema
>

// Payroll employee misc deduction GraphQL (`payrollDeleteEmployeeMiscDeduction`) -----------------
// Field names follow the documented qbexternal mutation + workflow examples.
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/mutations/payrollDeleteEmployeeMiscDeduction
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/inputobjects/Payroll_DeleteEmployeeMiscDeductionInput
// @see https://developer.intuit.com/app/developer/payroll-time/docs/workflows/payroll-deductions

/**
 * `Payroll_DeleteEmployeeMiscDeductionInput`. Workflow examples always send `employeeId` and
 * `employeeDeductionId`.
 */
export const quickbooksDeleteEmployeeMiscDeductionActionStepInputSchema = yup.object({
  employeeId: yup.string().required(),
  employeeDeductionId: yup.string().required(),
})

export type QuickbooksDeleteEmployeeMiscDeductionActionStepInput = yup.InferType<
  typeof quickbooksDeleteEmployeeMiscDeductionActionStepInputSchema
>

/**
 * `Payroll_DeleteEmployeeMiscDeductionSuccess`. Workflow GraphQL examples select `Id`; the
 * sample JSON response uses `id`. Match sibling delete-employee-pension and the sample JSON.
 */
export const quickbooksPayrollDeleteEmployeeMiscDeductionPayloadSuccessSchema = yup.object({
  __typename: yup.string().oneOf(['Payroll_DeleteEmployeeMiscDeductionSuccess']).optional(),
  id: yup.string().required(),
})

/**
 * Delete success is `{ id }`, not `{ policy }`. Do not reuse `isPayrollDeductionError`,
 * which treats any non-policy payload with message/code/type as an error.
 */
export const isPayrollDeleteEmployeeMiscDeductionPayloadError = (
  value: unknown,
): value is QuickbooksPayrollDeductionError => {
  if (typeof value !== 'object' || value === null) {
    return false
  }
  const o = value as Record<string, unknown>
  return (
    o.__typename === 'Payroll_DeductionError' ||
    (o.id == null && (o.message != null || o.code != null || o.type != null))
  )
}

export const quickbooksPayrollDeleteEmployeeMiscDeductionPayloadUnionSchema = yup.lazy(
  (value: unknown) => {
    if (isPayrollDeleteEmployeeMiscDeductionPayloadError(value)) {
      return quickbooksPayrollDeductionErrorSchema.required()
    }
    return quickbooksPayrollDeleteEmployeeMiscDeductionPayloadSuccessSchema.required()
  },
)

export const quickbooksDeleteEmployeeMiscDeductionGraphqlResponseSchema = yup.object({
  data: yup
    .object({
      payrollDeleteEmployeeMiscDeduction:
        quickbooksPayrollDeleteEmployeeMiscDeductionPayloadUnionSchema,
    })
    .required(),
})

export type QuickbooksDeleteEmployeeMiscDeductionGraphqlResponse = yup.InferType<
  typeof quickbooksDeleteEmployeeMiscDeductionGraphqlResponseSchema
>

// Business Planning (Project Budget) GraphQL ------------------------------------------------------
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/inputobjects/BusinessPlanning_BudgetInput
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/mutations/businessPlanningCreateBudget
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/mutations/businessPlanningUpdateBudget
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/mutations/businessPlanningDeleteBudgets

/** `BusinessPlanning_BudgetType` — official enum currently documents PROJECT only. */
export const QUICKBOOKS_BUSINESS_PLANNING_BUDGET_TYPE_VALUES = ['PROJECT'] as const
export type QuickbooksBusinessPlanningBudgetType =
  (typeof QUICKBOOKS_BUSINESS_PLANNING_BUDGET_TYPE_VALUES)[number]

/** `BusinessPlanning_BudgetState` */
export const QUICKBOOKS_BUSINESS_PLANNING_BUDGET_STATE_VALUES = ['DRAFT', 'LOCKED'] as const
export type QuickbooksBusinessPlanningBudgetState =
  (typeof QUICKBOOKS_BUSINESS_PLANNING_BUDGET_STATE_VALUES)[number]

/**
 * `BusinessPlanning_BudgetDetailType` enum page documents ITEM. The field description on
 * `BusinessPlanning_BudgetDetailInput` also names ACCOUNT, NET_INCOME, SUBTOTAL, and TOTAL.
 */
export const QUICKBOOKS_BUSINESS_PLANNING_BUDGET_DETAIL_TYPE_VALUES = [
  'ACCOUNT',
  'ITEM',
  'NET_INCOME',
  'SUBTOTAL',
  'TOTAL',
] as const
export type QuickbooksBusinessPlanningBudgetDetailType =
  (typeof QUICKBOOKS_BUSINESS_PLANNING_BUDGET_DETAIL_TYPE_VALUES)[number]

/** GraphQL `Decimal` on Business Planning types (JSON number in documented examples). */
const quickbooksBusinessPlanningDecimalSchema = yup.number().nullable().optional()

/** `BusinessPlanning_ExtensionInput` / `BusinessPlanning_ExtensionType` */
export const quickbooksBusinessPlanningExtensionSchema = yup.object({
  id: yup.string().nullable().optional(),
  values: yup.array().of(yup.string().required()).nullable().optional(),
})
export type QuickbooksBusinessPlanningExtension = yup.InferType<
  typeof quickbooksBusinessPlanningExtensionSchema
>

/** `BusinessPlanning_ViewSettingsInput` / `BusinessPlanning_ViewSettings` */
export const quickbooksBusinessPlanningViewSettingsSchema = yup.object({
  archived: yup.boolean().nullable().optional(),
})
export type QuickbooksBusinessPlanningViewSettings = yup.InferType<
  typeof quickbooksBusinessPlanningViewSettingsSchema
>

/** `BusinessPlanning_BudgetDetailInput` (create/update line items). */
export const quickbooksBusinessPlanningBudgetDetailInputSchema = yup.object({
  accountId: yup.string().nullable().optional(),
  amount: quickbooksBusinessPlanningDecimalSchema,
  customFields: yup
    .array()
    .of(quickbooksBusinessPlanningExtensionSchema.required())
    .nullable()
    .optional(),
  date: yup.string().nullable().optional(),
  deleted: yup.boolean().nullable().optional(),
  description: yup.string().nullable().optional(),
  dimensions: yup
    .array()
    .of(quickbooksBusinessPlanningExtensionSchema.required())
    .nullable()
    .optional(),
  itemId: yup.string().nullable().optional(),
  klassId: yup.string().nullable().optional(),
  locationId: yup.string().nullable().optional(),
  order: yup.string().nullable().optional(),
  quantity: quickbooksBusinessPlanningDecimalSchema,
  sequenceId: yup.string().nullable().optional(),
  type: yup
    .string()
    .oneOf([...QUICKBOOKS_BUSINESS_PLANNING_BUDGET_DETAIL_TYPE_VALUES])
    .nullable()
    .optional(),
  unitCost: quickbooksBusinessPlanningDecimalSchema,
})
export type QuickbooksBusinessPlanningBudgetDetailInput = yup.InferType<
  typeof quickbooksBusinessPlanningBudgetDetailInputSchema
>

/** `BusinessPlanning_BudgetDetail` object (response). `deleted` is input-only. */
export const quickbooksBusinessPlanningBudgetDetailSchema = yup.object({
  accountId: yup.string().nullable().optional(),
  amount: quickbooksBusinessPlanningDecimalSchema,
  customFields: yup
    .array()
    .of(quickbooksBusinessPlanningExtensionSchema.required())
    .nullable()
    .optional(),
  date: yup.string().nullable().optional(),
  description: yup.string().nullable().optional(),
  dimensions: yup
    .array()
    .of(quickbooksBusinessPlanningExtensionSchema.required())
    .nullable()
    .optional(),
  itemId: yup.string().nullable().optional(),
  klassId: yup.string().nullable().optional(),
  locationId: yup.string().nullable().optional(),
  order: yup.string().nullable().optional(),
  quantity: quickbooksBusinessPlanningDecimalSchema,
  sequenceId: yup.string().nullable().optional(),
  type: yup
    .string()
    .oneOf([...QUICKBOOKS_BUSINESS_PLANNING_BUDGET_DETAIL_TYPE_VALUES])
    .nullable()
    .optional(),
  unitCost: quickbooksBusinessPlanningDecimalSchema,
})
export type QuickbooksBusinessPlanningBudgetDetail = yup.InferType<
  typeof quickbooksBusinessPlanningBudgetDetailSchema
>

/** `BusinessPlanning_BudgetMetaData` */
export const quickbooksBusinessPlanningBudgetMetaDataSchema = yup.object({
  createdAt: yup.string().nullable().optional(),
  createdBy: yup.string().nullable().optional(),
  lastUpdatedBy: yup.string().nullable().optional(),
  updatedAt: yup.string().nullable().optional(),
})
export type QuickbooksBusinessPlanningBudgetMetaData = yup.InferType<
  typeof quickbooksBusinessPlanningBudgetMetaDataSchema
>

/** `BusinessPlanning_Budget` object returned on create/update payload. */
export const quickbooksBusinessPlanningBudgetSchema = yup.object({
  budgetDetails: yup
    .array()
    .of(quickbooksBusinessPlanningBudgetDetailSchema.required())
    .nullable()
    .optional(),
  budgetId: yup.string().nullable().optional(),
  budgetMetaData: quickbooksBusinessPlanningBudgetMetaDataSchema.nullable().optional(),
  budgetName: yup.string().nullable().optional(),
  budgetType: yup
    .string()
    .oneOf([...QUICKBOOKS_BUSINESS_PLANNING_BUDGET_TYPE_VALUES])
    .nullable()
    .optional(),
  deleted: yup.boolean().nullable().optional(),
  endDate: yup.string().nullable().optional(),
  linkedEntityId: yup.string().nullable().optional(),
  startDate: yup.string().nullable().optional(),
  state: yup
    .string()
    .oneOf([...QUICKBOOKS_BUSINESS_PLANNING_BUDGET_STATE_VALUES])
    .nullable()
    .optional(),
  total: quickbooksBusinessPlanningDecimalSchema,
  viewSettings: quickbooksBusinessPlanningViewSettingsSchema.nullable().optional(),
})
export type QuickbooksBusinessPlanningBudget = yup.InferType<
  typeof quickbooksBusinessPlanningBudgetSchema
>

/**
 * Shared writable fields of `BusinessPlanning_BudgetInput`. `budgetId` is documented as the ID of
 * the budget to update — required on update, omitted on create.
 */
export const quickbooksBusinessPlanningBudgetWritableFieldsSchema = yup.object({
  budgetDetails: yup
    .array()
    .of(quickbooksBusinessPlanningBudgetDetailInputSchema.required())
    .nullable()
    .optional(),
  budgetName: yup.string().nullable().optional(),
  budgetType: yup
    .string()
    .oneOf([...QUICKBOOKS_BUSINESS_PLANNING_BUDGET_TYPE_VALUES])
    .nullable()
    .optional(),
  endDate: yup.string().nullable().optional(),
  linkedEntityId: yup.string().nullable().optional(),
  startDate: yup.string().nullable().optional(),
  state: yup
    .string()
    .oneOf([...QUICKBOOKS_BUSINESS_PLANNING_BUDGET_STATE_VALUES])
    .nullable()
    .optional(),
  total: quickbooksBusinessPlanningDecimalSchema,
  viewSettings: quickbooksBusinessPlanningViewSettingsSchema.nullable().optional(),
})

/**
 * Action step `data` for `businessPlanningCreateBudget`. Required fields follow the official
 * project-budget create workflow (`budgetType: PROJECT` + project id as `linkedEntityId`).
 *
 * @see https://developer.intuit.com/app/developer/qbo/docs/workflows/manage-projects/use-cases
 */
export const quickbooksCreateBusinessPlanningBudgetActionStepInputSchema =
  quickbooksBusinessPlanningBudgetWritableFieldsSchema.shape({
    budgetName: yup.string().required(),
    budgetType: yup
      .string()
      .oneOf([...QUICKBOOKS_BUSINESS_PLANNING_BUDGET_TYPE_VALUES])
      .required(),
    endDate: yup.string().required(),
    linkedEntityId: yup.string().required(),
    startDate: yup.string().required(),
  })
export type QuickbooksCreateBusinessPlanningBudgetActionStepInput = yup.InferType<
  typeof quickbooksCreateBusinessPlanningBudgetActionStepInputSchema
>

/**
 * Action step `data` for `businessPlanningUpdateBudget`. `budgetId` is documented as the ID of
 * the budget to update.
 */
export const quickbooksUpdateBusinessPlanningBudgetActionStepInputSchema =
  quickbooksBusinessPlanningBudgetWritableFieldsSchema.shape({
    budgetId: yup.string().required(),
  })
export type QuickbooksUpdateBusinessPlanningBudgetActionStepInput = yup.InferType<
  typeof quickbooksUpdateBusinessPlanningBudgetActionStepInputSchema
>

/** `BusinessPlanning_BudgetPayload` (`budget: BusinessPlanning_Budget`). */
export const quickbooksBusinessPlanningBudgetPayloadSchema = yup.object({
  budget: quickbooksBusinessPlanningBudgetSchema.nullable().optional(),
})

export const quickbooksCreateBusinessPlanningBudgetGraphqlResponseSchema = yup.object({
  data: yup
    .object({
      businessPlanningCreateBudget: quickbooksBusinessPlanningBudgetPayloadSchema.required(),
    })
    .required(),
})
export type QuickbooksCreateBusinessPlanningBudgetGraphqlResponse = yup.InferType<
  typeof quickbooksCreateBusinessPlanningBudgetGraphqlResponseSchema
>

export const quickbooksUpdateBusinessPlanningBudgetGraphqlResponseSchema = yup.object({
  data: yup
    .object({
      businessPlanningUpdateBudget: quickbooksBusinessPlanningBudgetPayloadSchema.required(),
    })
    .required(),
})
export type QuickbooksUpdateBusinessPlanningBudgetGraphqlResponse = yup.InferType<
  typeof quickbooksUpdateBusinessPlanningBudgetGraphqlResponseSchema
>

/** `BusinessPlanning_BudgetIdsInput` */
export const quickbooksDeleteBusinessPlanningBudgetsActionStepInputSchema = yup.object({
  budgetIds: yup.array().of(yup.string().required()).min(1).required(),
})
export type QuickbooksDeleteBusinessPlanningBudgetsActionStepInput = yup.InferType<
  typeof quickbooksDeleteBusinessPlanningBudgetsActionStepInputSchema
>

/** `BusinessPlanning_BudgetResponse` — per-id delete/archive result. */
export const quickbooksBusinessPlanningBudgetResponseSchema = yup.object({
  budgetId: yup.string().nullable().optional(),
  description: yup.string().nullable().optional(),
  status: yup.boolean().nullable().optional(),
})
export type QuickbooksBusinessPlanningBudgetResponse = yup.InferType<
  typeof quickbooksBusinessPlanningBudgetResponseSchema
>

/** `BusinessPlanning_BudgetResponsePayload` */
export const quickbooksDeleteBusinessPlanningBudgetsGraphqlResponseSchema = yup.object({
  data: yup
    .object({
      businessPlanningDeleteBudgets: yup
        .object({
          budgetResponseList: yup
            .array()
            .of(quickbooksBusinessPlanningBudgetResponseSchema.required())
            .nullable()
            .optional(),
        })
        .required(),
    })
    .required(),
})
export type QuickbooksDeleteBusinessPlanningBudgetsGraphqlResponse = yup.InferType<
  typeof quickbooksDeleteBusinessPlanningBudgetsGraphqlResponseSchema
>

// BUSINESS PLANNING READ BUDGET (`businessPlanningBudget`) -----------------------------------------
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/queries/businessPlanningBudget
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/objects/BusinessPlanning_Budget
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/objects/BusinessPlanning_BudgetDetailsConnection
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/inputobjects/BusinessPlanning_FetchBudgetDetailsInput

/** Official `BusinessPlanning_SortDirection`. */
export const QUICKBOOKS_BUSINESS_PLANNING_SORT_DIRECTION_VALUES = ['ASC', 'DESC'] as const
export type QuickbooksBusinessPlanningSortDirection =
  (typeof QUICKBOOKS_BUSINESS_PLANNING_SORT_DIRECTION_VALUES)[number]

/** Official `BusinessPlanning_BudgetDetailSortByColumn`. */
export const QUICKBOOKS_BUSINESS_PLANNING_BUDGET_DETAIL_SORT_BY_COLUMN_VALUES = [
  'CREATED_AT',
  'LINE_ORDER',
  'SEQUENCE_ID',
  'UPDATED_AT',
] as const
export type QuickbooksBusinessPlanningBudgetDetailSortByColumn =
  (typeof QUICKBOOKS_BUSINESS_PLANNING_BUDGET_DETAIL_SORT_BY_COLUMN_VALUES)[number]

/**
 * `BusinessPlanning_IDFilter`. Operator names follow the official input object
 * (`equals` / `notEquals` / `matchesAny`), not the Workforce `eq` / `in` filters.
 *
 * @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/inputobjects/BusinessPlanning_IDFilter
 */
export const quickbooksBusinessPlanningIdFilterSchema = yup.object({
  equals: yup.string().nullable().optional(),
  greaterThan: yup.string().nullable().optional(),
  greaterThanOrEquals: yup.string().nullable().optional(),
  lessThan: yup.string().nullable().optional(),
  lessThanOrEquals: yup.string().nullable().optional(),
  like: yup.string().nullable().optional(),
  matchesAny: yup.array().of(yup.string().required()).nullable().optional(),
  notEquals: yup.string().nullable().optional(),
})
export type QuickbooksBusinessPlanningIdFilter = yup.InferType<
  typeof quickbooksBusinessPlanningIdFilterSchema
>

/** `BusinessPlanning_BudgetDetailFilters` — official schema currently exposes `sequenceIds` only. */
export const quickbooksBusinessPlanningBudgetDetailFiltersSchema = yup.object({
  sequenceIds: quickbooksBusinessPlanningIdFilterSchema.nullable().optional().default(undefined),
})
export type QuickbooksBusinessPlanningBudgetDetailFilters = yup.InferType<
  typeof quickbooksBusinessPlanningBudgetDetailFiltersSchema
>

/** `BusinessPlanning_BudgetDetailSortBy`. */
export const quickbooksBusinessPlanningBudgetDetailSortBySchema = yup.object({
  direction: yup
    .string()
    .oneOf([...QUICKBOOKS_BUSINESS_PLANNING_SORT_DIRECTION_VALUES])
    .nullable()
    .optional(),
  field: yup
    .string()
    .oneOf([...QUICKBOOKS_BUSINESS_PLANNING_BUDGET_DETAIL_SORT_BY_COLUMN_VALUES])
    .nullable()
    .optional(),
})
export type QuickbooksBusinessPlanningBudgetDetailSortBy = yup.InferType<
  typeof quickbooksBusinessPlanningBudgetDetailSortBySchema
>

/**
 * `BusinessPlanning_FetchBudgetDetailsInput` for `budgetDetailsPaginated(budgetDetailsInput:)`.
 * `first` / `offset` are official `PositiveInt` (min 1). No documented max.
 */
export const quickbooksFetchBudgetDetailsInputSchema = yup.object({
  after: yup.string().nullable().optional(),
  before: yup.string().nullable().optional(),
  filter: quickbooksBusinessPlanningBudgetDetailFiltersSchema
    .nullable()
    .optional()
    .default(undefined),
  first: yup.number().integer().min(1).nullable().optional(),
  offset: yup.number().integer().min(1).nullable().optional(),
  sortBy: yup
    .array()
    .of(quickbooksBusinessPlanningBudgetDetailSortBySchema.required())
    .nullable()
    .optional(),
})
export type QuickbooksFetchBudgetDetailsInput = yup.InferType<
  typeof quickbooksFetchBudgetDetailsInputSchema
>

/**
 * Action-step `data` for GraphQL `businessPlanningBudget`.
 * `budgetId` is required (query returns a single budget by ID).
 */
export const quickbooksGetBusinessPlanningBudgetActionStepInputSchema = yup.object({
  budgetId: yup.string().required(),
  secondaryId: yup.string().nullable().optional(),
  useDefaultSecondaryId: yup.boolean().nullable().optional(),
  budgetDetailsInput: quickbooksFetchBudgetDetailsInputSchema
    .nullable()
    .optional()
    .default(undefined),
})
export type QuickbooksGetBusinessPlanningBudgetActionStepInput = yup.InferType<
  typeof quickbooksGetBusinessPlanningBudgetActionStepInputSchema
>

/** `BusinessPlanning_PageInfo` — same Relay fields as `Common_PageInfo`. */
export const quickbooksBusinessPlanningPageInfoSchema = yup.object({
  endCursor: yup.string().nullable().optional(),
  hasNextPage: yup.boolean().nullable().optional(),
  hasPreviousPage: yup.boolean().nullable().optional(),
  startCursor: yup.string().nullable().optional(),
})

/** `BusinessPlanning_BudgetDetailsEdge`. */
export const quickbooksBusinessPlanningBudgetDetailsEdgeSchema = yup.object({
  cursor: yup.string().nullable().optional(),
  node: quickbooksBusinessPlanningBudgetDetailSchema.nullable().optional().default(undefined),
})

/** `BusinessPlanning_BudgetDetailsConnection`. Official fields: `edges`, `pageInfo`, `totalCount`. */
export const quickbooksBusinessPlanningBudgetDetailsConnectionSchema = yup.object({
  edges: yup
    .array()
    .of(quickbooksBusinessPlanningBudgetDetailsEdgeSchema.required())
    .nullable()
    .optional(),
  pageInfo: quickbooksBusinessPlanningPageInfoSchema.nullable().optional().default(undefined),
  totalCount: yup.number().integer().nullable().optional(),
})
export type QuickbooksBusinessPlanningBudgetDetailsConnection = yup.InferType<
  typeof quickbooksBusinessPlanningBudgetDetailsConnectionSchema
>

/**
 * `BusinessPlanning_Budget` as returned by the read query, including `budgetDetailsPaginated`.
 * Write mutations omit that field because it requires `budgetDetailsInput`.
 * Nested objects use `.default(undefined)` so omitted GraphQL fields stay omitted.
 */
export const quickbooksBusinessPlanningBudgetReadSchema =
  quickbooksBusinessPlanningBudgetSchema.shape({
    budgetMetaData: quickbooksBusinessPlanningBudgetMetaDataSchema
      .nullable()
      .optional()
      .default(undefined),
    viewSettings: quickbooksBusinessPlanningViewSettingsSchema
      .nullable()
      .optional()
      .default(undefined),
    budgetDetailsPaginated: quickbooksBusinessPlanningBudgetDetailsConnectionSchema
      .nullable()
      .optional()
      .default(undefined),
  })
export type QuickbooksBusinessPlanningBudgetRead = yup.InferType<
  typeof quickbooksBusinessPlanningBudgetReadSchema
>

export const quickbooksGetBusinessPlanningBudgetGraphqlResponseSchema = yup.object({
  data: yup
    .object({
      businessPlanningBudget: quickbooksBusinessPlanningBudgetReadSchema
        .nullable()
        .optional()
        .default(undefined),
    })
    .required(),
})
export type QuickbooksGetBusinessPlanningBudgetGraphqlResponse = yup.InferType<
  typeof quickbooksGetBusinessPlanningBudgetGraphqlResponseSchema
>

// PAYROLL UPDATE EMPLOYEE EMPLOYMENT DETAIL --------------------------------------------------------
// GraphQL `payrollUpdateEmployeeEmploymentDetail`. Workforce API is not available in sandbox.
//
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/mutations/payrollUpdateEmployeeEmploymentDetail
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/inputobjects/Payroll_UpdateEmployeeEmploymentDetailInput
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/inputobjects/Payroll_EmployeeEmploymentDetailInput
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/inputobjects/Payroll_WorkLocationInput
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/unions/Payroll_UpdateEmployeeEmploymentDetailPayload

/** `Payroll_WorkLocationInput` — work location is referenced by id. */
export const quickbooksPayrollWorkLocationInputSchema = yup.object({
  id: yup.string().required(),
})

/**
 * `Payroll_EmployeeEmploymentDetailInput` — official SDL currently exposes only `workLocation`.
 * `workLocation` is required here so we do not send a no-op `employmentDetail: {}` update.
 */
export const quickbooksPayrollEmployeeEmploymentDetailInputSchema = yup.object({
  workLocation: quickbooksPayrollWorkLocationInputSchema.required(),
})

/**
 * Action step `data` for GraphQL `Payroll_UpdateEmployeeEmploymentDetailInput`.
 * `intuitAccountId` is the connected company's realmId and is injected by the handler from
 * OAuth metadata — not accepted as user input (token + stored realm are the tenancy boundary).
 */
export const quickbooksUpdateEmployeeEmploymentDetailActionStepInputSchema = yup.object({
  employeeId: yup.string().required(),
  employmentDetail: quickbooksPayrollEmployeeEmploymentDetailInputSchema.required(),
})
export type QuickbooksUpdateEmployeeEmploymentDetailActionStepInput = yup.InferType<
  typeof quickbooksUpdateEmployeeEmploymentDetailActionStepInputSchema
>

/** `WorkerManagement_EmployeeEmploymentStatus`. */
export const quickbooksWorkerManagementEmployeeEmploymentStatusSchema = yup.object({
  active: yup.boolean().nullable().optional(),
  detailedStatus: yup.string().nullable().optional(),
})

/**
 * `WorkerManagement_EmployeeEmploymentDetail` output fields.
 *
 * @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/objects/WorkerManagement_EmployeeEmploymentDetail
 */
export const quickbooksWorkerManagementEmployeeEmploymentDetailSchema = yup.object({
  employeeIdentifier: yup.string().nullable().optional(),
  hireDate: yup.string().nullable().optional(),
  jobTitle: yup.string().nullable().optional(),
  terminationDate: yup.string().nullable().optional(),
})

/**
 * `Payroll_QbPostalAddress` scalars used on work-location `addressComponents`.
 * Selection and output use `postalCode` only (SDL also lists lowercase `postalcode`).
 *
 * @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/objects/Payroll_QbPostalAddress
 */
export const quickbooksPayrollQbPostalAddressSchema = yup.object({
  city: yup.string().nullable().optional(),
  country: yup.string().nullable().optional(),
  postalCode: yup.string().nullable().optional(),
  state: yup.string().nullable().optional(),
  streetAddressLine1: yup.string().nullable().optional(),
  streetAddressLine2: yup.string().nullable().optional(),
  streetAddressLine3: yup.string().nullable().optional(),
})

/**
 * `Payroll_WorkLocation` subset selected after update (scalars + address components).
 *
 * @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/objects/Payroll_WorkLocation
 */
export const quickbooksPayrollWorkLocationSchema = yup.object({
  id: yup.string().nullable().optional(),
  active: yup.boolean().nullable().optional(),
  primary: yup.boolean().nullable().optional(),
  unitNumber: yup.string().nullable().optional(),
  addressComponents: quickbooksPayrollQbPostalAddressSchema
    .nullable()
    .optional()
    .default(undefined),
})

/** `Payroll_EmployeeDetails` — this mutation only selects `workLocation`. */
export const quickbooksPayrollEmployeeDetailsSchema = yup.object({
  workLocation: quickbooksPayrollWorkLocationSchema.nullable().optional().default(undefined),
})

/**
 * `WorkerManagement_Employee` fields selected by the update mutation.
 *
 * @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/objects/WorkerManagement_Employee
 */
export const quickbooksWorkerManagementEmployeeSchema = yup.object({
  id: yup.string().nullable().optional(),
  firstName: yup.string().nullable().optional(),
  middleName: yup.string().nullable().optional(),
  lastName: yup.string().nullable().optional(),
  gender: yup.string().nullable().optional(),
  alternateIds: yup
    .array()
    .of(quickbooksAlternateIdSchema.required())
    .nullable()
    .optional()
    .default(undefined),
  employmentStatus: quickbooksWorkerManagementEmployeeEmploymentStatusSchema
    .nullable()
    .optional()
    .default(undefined),
  employmentDetail: quickbooksWorkerManagementEmployeeEmploymentDetailSchema
    .nullable()
    .optional()
    .default(undefined),
  payrollEmployeeDetails: quickbooksPayrollEmployeeDetailsSchema
    .nullable()
    .optional()
    .default(undefined),
})

/** `Payroll_UpdateEmployeeEmploymentDetailSuccess`. */
export const quickbooksPayrollUpdateEmployeeEmploymentDetailSuccessSchema = yup.object({
  __typename: yup.string().oneOf(['Payroll_UpdateEmployeeEmploymentDetailSuccess']).optional(),
  employee: quickbooksWorkerManagementEmployeeSchema.default(undefined).required(),
})
export type QuickbooksPayrollUpdateEmployeeEmploymentDetailSuccess = yup.InferType<
  typeof quickbooksPayrollUpdateEmployeeEmploymentDetailSuccessSchema
>

/** `Payroll_EmployeeEmploymentDetailError`. */
export const quickbooksPayrollEmployeeEmploymentDetailErrorSchema = yup.object({
  __typename: yup.string().oneOf(['Payroll_EmployeeEmploymentDetailError']).optional(),
  code: yup.string().nullable().optional(),
  message: yup.string().nullable().optional(),
  type: yup.string().nullable().optional(),
})
export type QuickbooksPayrollEmployeeEmploymentDetailError = yup.InferType<
  typeof quickbooksPayrollEmployeeEmploymentDetailErrorSchema
>

export const isPayrollEmployeeEmploymentDetailError = (
  value: unknown,
): value is QuickbooksPayrollEmployeeEmploymentDetailError => {
  if (typeof value !== 'object' || value === null) {
    return false
  }
  const record = value as Record<string, unknown>
  if (record.__typename === 'Payroll_EmployeeEmploymentDetailError') {
    return true
  }
  return (
    record.employee == null &&
    (record.code != null || record.message != null || record.type != null)
  )
}

/**
 * GraphQL union `Payroll_UpdateEmployeeEmploymentDetailPayload` →
 * `Payroll_UpdateEmployeeEmploymentDetailSuccess` | `Payroll_EmployeeEmploymentDetailError`.
 */
export const quickbooksPayrollUpdateEmployeeEmploymentDetailPayloadSchema = yup.lazy(
  (value: unknown) => {
    if (isPayrollEmployeeEmploymentDetailError(value)) {
      return quickbooksPayrollEmployeeEmploymentDetailErrorSchema.required()
    }
    return quickbooksPayrollUpdateEmployeeEmploymentDetailSuccessSchema.required()
  },
)

export const quickbooksUpdateEmployeeEmploymentDetailGraphqlResponseSchema = yup.object({
  data: yup
    .object({
      payrollUpdateEmployeeEmploymentDetail:
        quickbooksPayrollUpdateEmployeeEmploymentDetailPayloadSchema,
    })
    .required(),
})
export type QuickbooksUpdateEmployeeEmploymentDetailGraphqlResponse = yup.InferType<
  typeof quickbooksUpdateEmployeeEmploymentDetailGraphqlResponseSchema
>

// WORKFORCE LIST EMPLOYEES (`workerManagementEmployees`) ------------------------------------------
// GraphQL query. Distinct from REST `EMPLOYEES` (`select * from Employee`).
// Workforce API is not available in sandbox.
//
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/queries/workerManagementEmployees
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/objects/WorkerManagement_EmployeeConnection
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/objects/WorkerManagement_Employee
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/inputobjects/WorkerManagement_EmployeeConnectionFilter
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/enums/WorkerManagement_EmployeeConnectionOrderBy
// @see https://developer.intuit.com/app/developer/payroll-time/docs/workflows/payroll-employee

/** Official `first` / `last` page-size cap on `workerManagementEmployees`. */
export const QUICKBOOKS_WORKER_MANAGEMENT_EMPLOYEES_MAX_PAGE_SIZE = 50

/**
 * `WorkerManagement_EmployeeConnectionOrderBy`.
 *
 * @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/enums/WorkerManagement_EmployeeConnectionOrderBy
 */
export const QUICKBOOKS_WORKER_MANAGEMENT_EMPLOYEE_ORDER_BY_VALUES = [
  'ACTIVE_ASC',
  'ACTIVE_DESC',
  'CREATED_AT_ASC',
  'CREATED_AT_DESC',
  'DISPLAYNAME_ASC',
  'DISPLAYNAME_DESC',
  'EMPLOYMENTDETAIL_HIREDATE_ASC',
  'EMPLOYMENTDETAIL_HIREDATE_DESC',
  'FIRSTNAME_ASC',
  'FIRSTNAME_DESC',
  'FULLY_QUALIFIED_NAME_ASC',
  'FULLY_QUALIFIED_NAME_DESC',
  'GENDER_ASC',
  'GENDER_DESC',
  'ID_ASC',
  'ID_DESC',
  'LASTNAME_ASC',
  'LASTNAME_DESC',
  'MIDDLENAME_ASC',
  'MIDDLENAME_DESC',
  'PRINT_ON_CHECK_NAME_ASC',
  'PRINT_ON_CHECK_NAME_DESC',
  'UPDATED_AT_ASC',
  'UPDATED_AT_DESC',
] as const
export type QuickbooksWorkerManagementEmployeeOrderBy =
  (typeof QUICKBOOKS_WORKER_MANAGEMENT_EMPLOYEE_ORDER_BY_VALUES)[number]

/**
 * `WorkerManagement_StringFilter`.
 *
 * @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/inputobjects/WorkerManagement_StringFilter
 */
export const quickbooksWorkerManagementStringFilterSchema = yup.object({
  eq: yup.string().nullable().optional(),
  greaterThan: yup.string().nullable().optional(),
  greaterThanOrEquals: yup.string().nullable().optional(),
  in: yup.array().of(yup.string().required()).nullable().optional(),
  lessThan: yup.string().nullable().optional(),
  lessThanOrEquals: yup.string().nullable().optional(),
  like: yup.string().nullable().optional(),
  notEq: yup.string().nullable().optional(),
})

/**
 * `WorkerManagement_IdFilter`. JSON IDs are strings.
 *
 * @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/inputobjects/WorkerManagement_IdFilter
 */
export const quickbooksWorkerManagementIdFilterSchema = yup.object({
  eq: yup.string().nullable().optional(),
  greaterThan: yup.string().nullable().optional(),
  greaterThanOrEquals: yup.string().nullable().optional(),
  in: yup.array().of(yup.string().required()).nullable().optional(),
  lessThan: yup.string().nullable().optional(),
  lessThanOrEquals: yup.string().nullable().optional(),
  like: yup.string().nullable().optional(),
  notEq: yup.string().nullable().optional(),
})

/**
 * `WorkerManagement_BooleanFilter`.
 *
 * @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/inputobjects/WorkerManagement_BooleanFilter
 */
export const quickbooksWorkerManagementBooleanFilterSchema = yup.object({
  eq: yup.boolean().nullable().optional(),
  notEq: yup.boolean().nullable().optional(),
})

/**
 * `WorkerManagement_NameFilter`.
 *
 * @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/inputobjects/WorkerManagement_NameFilter
 */
export const quickbooksWorkerManagementNameFilterSchema = yup.object({
  nameContains: yup.string().nullable().optional(),
})

/**
 * `WorkerManagement_EmploymentStatusFilter`.
 *
 * @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/inputobjects/WorkerManagement_EmploymentStatusFilter
 */
export const quickbooksWorkerManagementEmploymentStatusFilterSchema = yup.object({
  active: quickbooksWorkerManagementBooleanFilterSchema.nullable().optional().default(undefined),
  detailedStatus: quickbooksWorkerManagementStringFilterSchema
    .nullable()
    .optional()
    .default(undefined),
})

/**
 * `WorkerManagement_EmployeeConnectionFilter`.
 * `intuitAccountId` is omitted: the handler uses the connected OAuth realm / auth context.
 *
 * @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/inputobjects/WorkerManagement_EmployeeConnectionFilter
 */
export const quickbooksWorkerManagementEmployeeConnectionFilterSchema = yup.object({
  createdAt: quickbooksWorkerManagementStringFilterSchema.nullable().optional().default(undefined),
  displayName: quickbooksWorkerManagementStringFilterSchema
    .nullable()
    .optional()
    .default(undefined),
  employeeDigitalIdentityId: yup.string().nullable().optional(),
  employeeId: quickbooksWorkerManagementIdFilterSchema.nullable().optional().default(undefined),
  employeeName: quickbooksWorkerManagementNameFilterSchema.nullable().optional().default(undefined),
  employeeUserId: yup.string().nullable().optional(),
  employmentStatus: quickbooksWorkerManagementEmploymentStatusFilterSchema
    .nullable()
    .optional()
    .default(undefined),
  firstName: quickbooksWorkerManagementStringFilterSchema.nullable().optional().default(undefined),
  fullyQualifiedName: quickbooksWorkerManagementStringFilterSchema
    .nullable()
    .optional()
    .default(undefined),
  gender: quickbooksWorkerManagementStringFilterSchema.nullable().optional().default(undefined),
  isHidden: yup.boolean().nullable().optional(),
  lastName: quickbooksWorkerManagementStringFilterSchema.nullable().optional().default(undefined),
  middleName: quickbooksWorkerManagementStringFilterSchema.nullable().optional().default(undefined),
  primaryEmail: quickbooksWorkerManagementStringFilterSchema
    .nullable()
    .optional()
    .default(undefined),
  printOnCheckName: quickbooksWorkerManagementStringFilterSchema
    .nullable()
    .optional()
    .default(undefined),
  updatedAt: quickbooksWorkerManagementStringFilterSchema.nullable().optional().default(undefined),
})

/**
 * Action-step `data` for GraphQL `workerManagementEmployees`.
 * Official args: `orderBy`, `filter`, `first` (max 50), `after`, `last`, `before`.
 */
export const quickbooksListWorkerManagementEmployeesActionStepInputSchema = yup.object({
  first: yup
    .number()
    .integer()
    .min(1)
    .max(QUICKBOOKS_WORKER_MANAGEMENT_EMPLOYEES_MAX_PAGE_SIZE)
    .optional(),
  after: yup.string().nullable().optional(),
  last: yup
    .number()
    .integer()
    .min(1)
    .max(QUICKBOOKS_WORKER_MANAGEMENT_EMPLOYEES_MAX_PAGE_SIZE)
    .optional(),
  before: yup.string().nullable().optional(),
  filter: quickbooksWorkerManagementEmployeeConnectionFilterSchema
    .nullable()
    .optional()
    .default(undefined),
  orderBy: yup
    .array()
    .of(
      yup
        .string()
        .oneOf([...QUICKBOOKS_WORKER_MANAGEMENT_EMPLOYEE_ORDER_BY_VALUES])
        .required(),
    )
    .nullable()
    .optional(),
})
export type QuickbooksListWorkerManagementEmployeesActionStepInput = yup.InferType<
  typeof quickbooksListWorkerManagementEmployeesActionStepInputSchema
>

/**
 * `Qb_PhoneNumber` scalars selected on employee contact info.
 *
 * @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/objects/Qb_PhoneNumber
 */
export const quickbooksQbPhoneNumberSchema = yup.object({
  areaCode: yup.string().nullable().optional(),
  countryCode: yup.string().nullable().optional(),
  e164Number: yup.string().nullable().optional(),
  extension: yup.string().nullable().optional(),
  kind: yup.string().nullable().optional(),
  number: yup.string().nullable().optional(),
  originalNumber: yup.string().nullable().optional(),
})

/**
 * `Qb_EmailAddress` scalars selected on employee contact info.
 *
 * @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/objects/Qb_EmailAddress
 */
export const quickbooksQbEmailAddressSchema = yup.object({
  domain: yup.string().nullable().optional(),
  email: yup.string().nullable().optional(),
  name: yup.string().nullable().optional(),
})

/**
 * `WorkerManagement_EmployeeContactInfo` (list selection; omits `primaryV3Address`).
 *
 * @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/objects/WorkerManagement_EmployeeContactInfo
 */
export const quickbooksWorkerManagementEmployeeContactInfoSchema = yup.object({
  mobilePhoneNumber: quickbooksQbPhoneNumberSchema.nullable().optional().default(undefined),
  primaryAddress: quickbooksPayrollQbPostalAddressSchema.nullable().optional().default(undefined),
  primaryEmailAddress: quickbooksQbEmailAddressSchema.nullable().optional().default(undefined),
  primaryPhoneNumber: quickbooksQbPhoneNumberSchema.nullable().optional().default(undefined),
})

/**
 * `Payroll_PayScheduleAbsoluteReferenceDates`.
 *
 * @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/objects/Payroll_PayScheduleAbsoluteReferenceDates
 */
export const quickbooksPayrollPayScheduleAbsoluteReferenceDatesSchema = yup.object({
  initialPayDate: yup.string().nullable().optional(),
})

/**
 * `Payroll_EmployerPaySchedule` subset selected by the employee list query.
 * Distinct from `quickbooksPayrollEmployerPayScheduleSchema` on `payrollEmployerInfo`.
 *
 * @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/objects/Payroll_EmployerPaySchedule
 */
export const quickbooksWorkerManagementEmployeePayScheduleSchema = yup.object({
  id: yup.string().nullable().optional(),
  name: yup.string().nullable().optional(),
  frequency: yup.string().nullable().optional(),
  referenceDates: yup
    .array()
    .of(quickbooksPayrollPayScheduleAbsoluteReferenceDatesSchema.required())
    .nullable()
    .optional()
    .default(undefined),
})

/**
 * `Payroll_EmployeeDetails` fields selected by `workerManagementEmployees`
 * (`paySchedule` + `workLocation` from the Workforce employee workflow).
 *
 * @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/objects/Payroll_EmployeeDetails
 */
export const quickbooksWorkerManagementEmployeePayrollDetailsSchema = yup.object({
  paySchedule: quickbooksWorkerManagementEmployeePayScheduleSchema
    .nullable()
    .optional()
    .default(undefined),
  workLocation: quickbooksPayrollWorkLocationSchema.nullable().optional().default(undefined),
})

/**
 * `WorkerManagement_Employee` node selected by `workerManagementEmployees`.
 * Omits `departments` (nested connection) and `limitedAccessFields` (PII / extra scopes).
 *
 * @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/objects/WorkerManagement_Employee
 */
export const quickbooksWorkerManagementEmployeeListNodeSchema = yup.object({
  id: yup.string().nullable().optional(),
  firstName: yup.string().nullable().optional(),
  middleName: yup.string().nullable().optional(),
  lastName: yup.string().nullable().optional(),
  gender: yup.string().nullable().optional(),
  alternateIds: yup
    .array()
    .of(quickbooksAlternateIdSchema.required())
    .nullable()
    .optional()
    .default(undefined),
  contactInfo: quickbooksWorkerManagementEmployeeContactInfoSchema
    .nullable()
    .optional()
    .default(undefined),
  employmentStatus: quickbooksWorkerManagementEmployeeEmploymentStatusSchema
    .nullable()
    .optional()
    .default(undefined),
  employmentDetail: quickbooksWorkerManagementEmployeeEmploymentDetailSchema
    .nullable()
    .optional()
    .default(undefined),
  payrollEmployeeDetails: quickbooksWorkerManagementEmployeePayrollDetailsSchema
    .nullable()
    .optional()
    .default(undefined),
})

/**
 * `WorkerManagement_EmployeeEdge`.
 *
 * @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/objects/WorkerManagement_EmployeeEdge
 */
export const quickbooksWorkerManagementEmployeeEdgeSchema = yup.object({
  cursor: yup.string().nullable().optional(),
  node: quickbooksWorkerManagementEmployeeListNodeSchema.nullable().optional().default(undefined),
})

/**
 * `WorkerManagement_EmployeeConnection`.
 *
 * @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/objects/WorkerManagement_EmployeeConnection
 */
export const quickbooksWorkerManagementEmployeeConnectionSchema = yup.object({
  edges: yup
    .array()
    .of(quickbooksWorkerManagementEmployeeEdgeSchema.required())
    .nullable()
    .optional()
    .default(undefined),
  pageInfo: quickbooksGraphqlPageInfoSchema.nullable().optional().default(undefined),
  totalCount: yup.number().nullable().optional(),
})

export const quickbooksListWorkerManagementEmployeesGraphqlResponseSchema = yup.object({
  data: yup
    .object({
      workerManagementEmployees: quickbooksWorkerManagementEmployeeConnectionSchema
        .nullable()
        .optional()
        .default(undefined),
    })
    .required(),
})
export type QuickbooksListWorkerManagementEmployeesGraphqlResponse = yup.InferType<
  typeof quickbooksListWorkerManagementEmployeesGraphqlResponseSchema
>

// Payroll employee benefit GraphQL (`payrollCreateEmployeeBenefit` /
// `payrollUpdateEmployeeBenefit` / `payrollDisableEmployeeBenefit`) -----------------------------
// Field names follow the official qbexternal SDL and payroll-benefits workflow docs.
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/mutations/payrollCreateEmployeeBenefit
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/inputobjects/Payroll_CreateEmployeeBenefitInput
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/objects/Payroll_EmployeeBenefit
// @see https://developer.intuit.com/app/developer/payroll-time/docs/workflows/payroll-benefits

/** `Payroll_RateType` (`FIXED` | `PERCENT`). */
export const QUICKBOOKS_PAYROLL_RATE_TYPES = ['FIXED', 'PERCENT'] as const
export type QuickbooksPayrollRateType = (typeof QUICKBOOKS_PAYROLL_RATE_TYPES)[number]

/** `Payroll_ContributionFrequency` (`PER_HOUR` | `PER_PAYSLIP`). */
export const QUICKBOOKS_PAYROLL_CONTRIBUTION_FREQUENCIES = ['PER_HOUR', 'PER_PAYSLIP'] as const
export type QuickbooksPayrollContributionFrequency =
  (typeof QUICKBOOKS_PAYROLL_CONTRIBUTION_FREQUENCIES)[number]

/** `Payroll_CappingFrequency` (`BY_CALENDAR_YEAR`). Output-only on `Payroll_Capping`. */
export const QUICKBOOKS_PAYROLL_CAPPING_FREQUENCIES = ['BY_CALENDAR_YEAR'] as const
export type QuickbooksPayrollCappingFrequency =
  (typeof QUICKBOOKS_PAYROLL_CAPPING_FREQUENCIES)[number]

/** `Payroll_Rate` / `Payroll_RateInput` (`type` + `value`). */
export const quickbooksPayrollRateSchema = yup.object({
  type: yup.string().nullable().optional(),
  value: yup.string().nullable().optional(),
})

/** `Payroll_RateInput` when a rate object is present. */
export const quickbooksPayrollRateInputSchema = yup.object({
  type: yup
    .string()
    .oneOf([...QUICKBOOKS_PAYROLL_RATE_TYPES])
    .required(),
  value: yup.string().required(),
})

/** `Payroll_CappingInput`. SDL: `amount` only; `frequency` is returned on `Payroll_Capping`. */
export const quickbooksPayrollCappingInputSchema = yup.object({
  amount: quickbooksPayrollRateInputSchema.default(undefined).required(),
})

/** `Payroll_Capping` (`amount` + `frequency`). */
export const quickbooksPayrollCappingSchema = yup.object({
  amount: quickbooksPayrollRateSchema.nullable().optional().default(undefined),
  frequency: yup.string().nullable().optional(),
})

/**
 * `Payroll_CreateEmployeeContributionInput` / `Payroll_CreateEmployerContributionInput` /
 * `Payroll_UpdateEmployeeContributionInput` / `Payroll_UpdateEmployerContributionInput`.
 */
export const quickbooksPayrollContributionInputSchema = yup.object({
  amount: quickbooksPayrollRateInputSchema.default(undefined).required(),
  frequency: yup
    .string()
    .oneOf([...QUICKBOOKS_PAYROLL_CONTRIBUTION_FREQUENCIES])
    .required(),
  capping: quickbooksPayrollCappingInputSchema.nullable().optional().default(undefined),
})

/** `Payroll_EmployeeContribution` / `Payroll_EmployerContribution`. */
export const quickbooksPayrollContributionSchema = yup.object({
  amount: quickbooksPayrollRateSchema.nullable().optional().default(undefined),
  frequency: yup.string().nullable().optional(),
  capping: quickbooksPayrollCappingSchema.nullable().optional().default(undefined),
})

/**
 * `Payroll_CreateEmployeeExternalContributionInput` /
 * `Payroll_UpdateEmployeeExternalContributionInput` (`amount: Common_MoneyAmountInput`).
 */
export const quickbooksPayrollExternalContributionInputSchema = yup.object({
  amount: quickbooksIndirectTaxMoneyInputSchema.default(undefined).required(),
})

/**
 * `Payroll_ContributiveDeductionInput`. At least one of employee / employer / external
 * contribution must be present.
 */
export const quickbooksPayrollContributiveDeductionInputSchema = yup
  .object({
    employeeContributionInput: quickbooksPayrollContributionInputSchema
      .nullable()
      .optional()
      .default(undefined),
    employerContributionInput: quickbooksPayrollContributionInputSchema
      .nullable()
      .optional()
      .default(undefined),
    employeeExternalContributionInput: quickbooksPayrollExternalContributionInputSchema
      .nullable()
      .optional()
      .default(undefined),
  })
  .test(
    'at-least-one-contribution',
    'At least one of employeeContributionInput, employerContributionInput, or employeeExternalContributionInput is required',
    (value) => {
      if (value == null) {
        return true
      }
      return (
        value.employeeContributionInput != null ||
        value.employerContributionInput != null ||
        value.employeeExternalContributionInput != null
      )
    },
  )

/** Employer deduction policy nested on `Payroll_EmployeeBenefit.deductionPolicy`. */
export const quickbooksPayrollDeductionPolicySchema = yup.object({
  id: yup.string().required(),
  name: yup.string().nullable().optional(),
  category: quickbooksPayrollVariableStringFieldSchema.nullable().optional().default(undefined),
  subCategory: quickbooksPayrollVariableStringFieldSchema.nullable().optional().default(undefined),
  statutoryType: quickbooksPayrollVariableStringFieldSchema
    .nullable()
    .optional()
    .default(undefined),
})

/** `Payroll_EmployeeBenefit` returned on create/update success (`deduction`). */
export const quickbooksPayrollEmployeeBenefitSchema = yup.object({
  id: yup.string().required(),
  active: yup.boolean().nullable().optional(),
  alternateIds: yup.array().of(quickbooksAlternateIdSchema.required()).nullable().optional(),
  deductionPolicy: quickbooksPayrollDeductionPolicySchema.nullable().optional().default(undefined),
  employeeContribution: quickbooksPayrollContributionSchema
    .nullable()
    .optional()
    .default(undefined),
  employerContribution: quickbooksPayrollContributionSchema
    .nullable()
    .optional()
    .default(undefined),
})

export type QuickbooksPayrollEmployeeBenefit = yup.InferType<
  typeof quickbooksPayrollEmployeeBenefitSchema
>

/** `Payroll_CreateEmployeeDeductionDetailsInput`. */
export const quickbooksCreateEmployeeDeductionDetailsInputSchema = yup.object({
  existingDeductionPolicyId: yup.string().required(),
  contribution: quickbooksPayrollContributiveDeductionInputSchema.default(undefined).required(),
})

/**
 * `Payroll_CreateEmployeeBenefitInput`. Workflow examples always send `employeeId` and
 * `employeeDeductionDetails`.
 */
export const quickbooksCreateEmployeeBenefitActionStepInputSchema = yup.object({
  employeeId: yup.string().required(),
  employeeDeductionDetails: quickbooksCreateEmployeeDeductionDetailsInputSchema
    .default(undefined)
    .required(),
})

export type QuickbooksCreateEmployeeBenefitActionStepInput = yup.InferType<
  typeof quickbooksCreateEmployeeBenefitActionStepInputSchema
>

const payrollEmployeeBenefitPayloadErrorTypenames = [
  'Payroll_CreateEmployeeBenefitPayloadError',
  'Payroll_UpdateEmployeeBenefitPayloadError',
  'Payroll_DisableEmployeeBenefitPayloadError',
] as const

/** Shared employee-benefit mutation error object. */
export const quickbooksPayrollEmployeeBenefitPayloadErrorSchema = yup.object({
  __typename: yup
    .string()
    .oneOf([...payrollEmployeeBenefitPayloadErrorTypenames])
    .optional(),
  code: yup.string().nullable().optional(),
  employeeDeductionId: yup.string().nullable().optional(),
  employeeId: yup.string().nullable().optional(),
  employerDeductionId: yup.string().nullable().optional(),
  message: yup.string().nullable().optional(),
  type: yup.string().nullable().optional(),
})

export type QuickbooksPayrollEmployeeBenefitPayloadError = yup.InferType<
  typeof quickbooksPayrollEmployeeBenefitPayloadErrorSchema
>

const payrollEmployeeBenefitPayloadSuccessTypenames = [
  'Payroll_CreateEmployeeBenefitPayloadSuccess',
  'Payroll_UpdateEmployeeBenefitPayloadSuccess',
] as const

export const quickbooksPayrollEmployeeBenefitPayloadSuccessSchema = yup.object({
  __typename: yup
    .string()
    .oneOf([...payrollEmployeeBenefitPayloadSuccessTypenames])
    .optional(),
  deduction: quickbooksPayrollEmployeeBenefitSchema.default(undefined).required(),
})

export const isPayrollEmployeeBenefitPayloadError = (
  value: unknown,
): value is QuickbooksPayrollEmployeeBenefitPayloadError => {
  if (typeof value !== 'object' || value === null) {
    return false
  }
  const o = value as Record<string, unknown>
  return (
    o.__typename === 'Payroll_CreateEmployeeBenefitPayloadError' ||
    o.__typename === 'Payroll_UpdateEmployeeBenefitPayloadError' ||
    o.__typename === 'Payroll_DisableEmployeeBenefitPayloadError' ||
    (o.deduction == null && o.id == null && (o.message != null || o.code != null || o.type != null))
  )
}

export const formatPayrollEmployeeBenefitPayloadError = (
  error: QuickbooksPayrollEmployeeBenefitPayloadError,
): string => {
  const parts = [error.type, error.message, error.code].filter((part): part is string => {
    return part != null && part !== ''
  })
  if (parts.length > 0) {
    return parts.join(': ')
  }
  return JSON.stringify(error)
}

export const quickbooksPayrollEmployeeBenefitPayloadUnionSchema = yup.lazy((value: unknown) => {
  if (isPayrollEmployeeBenefitPayloadError(value)) {
    return quickbooksPayrollEmployeeBenefitPayloadErrorSchema.required()
  }
  return quickbooksPayrollEmployeeBenefitPayloadSuccessSchema.required()
})

export const quickbooksCreateEmployeeBenefitGraphqlResponseSchema = yup.object({
  data: yup
    .object({
      payrollCreateEmployeeBenefit: quickbooksPayrollEmployeeBenefitPayloadUnionSchema,
    })
    .required(),
})

export type QuickbooksCreateEmployeeBenefitGraphqlResponse = yup.InferType<
  typeof quickbooksCreateEmployeeBenefitGraphqlResponseSchema
>

/**
 * `Payroll_UpdateEmployeeDeductionDetailsInput`. Contributions are flat (not nested under
 * `contribution`). `employeeDeductionId` identifies the employee benefit to update.
 *
 * @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/inputobjects/Payroll_UpdateEmployeeDeductionDetailsInput
 */
export const quickbooksUpdateEmployeeDeductionDetailsInputSchema = yup.object({
  employeeDeductionId: yup.string().required(),
  employeeContributionInput: quickbooksPayrollContributionInputSchema
    .nullable()
    .optional()
    .default(undefined),
  employerContributionInput: quickbooksPayrollContributionInputSchema
    .nullable()
    .optional()
    .default(undefined),
  employeeExternalContributionInput: quickbooksPayrollExternalContributionInputSchema
    .nullable()
    .optional()
    .default(undefined),
})

/**
 * `Payroll_UpdateEmployeeBenefitInput`. Workflow examples always send `employeeId` and
 * `employeeDeductionDetails`.
 *
 * @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/inputobjects/Payroll_UpdateEmployeeBenefitInput
 */
export const quickbooksUpdateEmployeeBenefitActionStepInputSchema = yup.object({
  employeeId: yup.string().required(),
  employeeDeductionDetails: quickbooksUpdateEmployeeDeductionDetailsInputSchema
    .default(undefined)
    .required(),
})

export type QuickbooksUpdateEmployeeBenefitActionStepInput = yup.InferType<
  typeof quickbooksUpdateEmployeeBenefitActionStepInputSchema
>

export const quickbooksUpdateEmployeeBenefitGraphqlResponseSchema = yup.object({
  data: yup
    .object({
      payrollUpdateEmployeeBenefit: quickbooksPayrollEmployeeBenefitPayloadUnionSchema,
    })
    .required(),
})

export type QuickbooksUpdateEmployeeBenefitGraphqlResponse = yup.InferType<
  typeof quickbooksUpdateEmployeeBenefitGraphqlResponseSchema
>

/**
 * `Payroll_DisableEmployeeBenefitInput`. Workflow "Delete Employee Benefits" always sends
 * `employeeId` and `employeeDeductionId`.
 *
 * @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/inputobjects/Payroll_DisableEmployeeBenefitInput
 */
export const quickbooksDisableEmployeeBenefitActionStepInputSchema = yup.object({
  employeeId: yup.string().required(),
  employeeDeductionId: yup.string().required(),
})

export type QuickbooksDisableEmployeeBenefitActionStepInput = yup.InferType<
  typeof quickbooksDisableEmployeeBenefitActionStepInputSchema
>

/** `Payroll_DisableEmployeeBenefitPayloadSuccess` (`id` only). */
export const quickbooksPayrollDisableEmployeeBenefitPayloadSuccessSchema = yup.object({
  __typename: yup.string().oneOf(['Payroll_DisableEmployeeBenefitPayloadSuccess']).optional(),
  id: yup.string().required(),
})

export const quickbooksPayrollDisableEmployeeBenefitPayloadUnionSchema = yup.lazy(
  (value: unknown) => {
    if (isPayrollEmployeeBenefitPayloadError(value)) {
      return quickbooksPayrollEmployeeBenefitPayloadErrorSchema.required()
    }
    return quickbooksPayrollDisableEmployeeBenefitPayloadSuccessSchema.required()
  },
)

export const quickbooksDisableEmployeeBenefitGraphqlResponseSchema = yup.object({
  data: yup
    .object({
      payrollDisableEmployeeBenefit: quickbooksPayrollDisableEmployeeBenefitPayloadUnionSchema,
    })
    .required(),
})

export type QuickbooksDisableEmployeeBenefitGraphqlResponse = yup.InferType<
  typeof quickbooksDisableEmployeeBenefitGraphqlResponseSchema
>

// Payroll employee pension GraphQL (`payrollCreateEmployeePension` /
// `payrollUpdateEmployeePension` / `payrollDeleteEmployeePension`) -----------------------------
// Field names follow the official qbexternal SDL.
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/mutations/payrollCreateEmployeePension
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/inputobjects/Payroll_CreateEmployeePensionInput
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/objects/Payroll_EmployeePension
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/unions/Payroll_CreateEmployeePensionPayload

/** `Payroll_EmployeeExternalContribution` (`amount: Common_MoneyAmount`). */
export const quickbooksPayrollExternalContributionSchema = yup.object({
  amount: quickbooksIndirectTaxMoneySchema.nullable().optional().default(undefined),
})

/** `Payroll_EmployeePension` returned on create/update success (`deduction`). */
export const quickbooksPayrollEmployeePensionSchema = yup.object({
  id: yup.string().required(),
  active: yup.boolean().nullable().optional(),
  alternateIds: yup.array().of(quickbooksAlternateIdSchema.required()).nullable().optional(),
  deductionPolicy: quickbooksPayrollDeductionPolicySchema.nullable().optional().default(undefined),
  employeeContribution: quickbooksPayrollContributionSchema
    .nullable()
    .optional()
    .default(undefined),
  employeeExternalContribution: quickbooksPayrollExternalContributionSchema
    .nullable()
    .optional()
    .default(undefined),
  employerContribution: quickbooksPayrollContributionSchema
    .nullable()
    .optional()
    .default(undefined),
})

export type QuickbooksPayrollEmployeePension = yup.InferType<
  typeof quickbooksPayrollEmployeePensionSchema
>

/**
 * `Payroll_CreateEmployeePensionInput`. SDL does not mark fields required; examples and
 * descriptions always send `employeeId` and `employeeDeductionDetails`.
 *
 * @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/inputobjects/Payroll_CreateEmployeePensionInput
 */
export const quickbooksCreateEmployeePensionActionStepInputSchema = yup.object({
  employeeId: yup.string().required(),
  employeeDeductionDetails: quickbooksCreateEmployeeDeductionDetailsInputSchema
    .default(undefined)
    .required(),
})

export type QuickbooksCreateEmployeePensionActionStepInput = yup.InferType<
  typeof quickbooksCreateEmployeePensionActionStepInputSchema
>

const payrollEmployeePensionPayloadSuccessTypenames = [
  'Payroll_CreateEmployeePensionSuccess',
  'Payroll_UpdateEmployeePensionSuccess',
] as const

export const quickbooksPayrollEmployeePensionPayloadSuccessSchema = yup.object({
  __typename: yup
    .string()
    .oneOf([...payrollEmployeePensionPayloadSuccessTypenames])
    .optional(),
  deduction: quickbooksPayrollEmployeePensionSchema.default(undefined).required(),
})

export const isPayrollEmployeePensionPayloadError = (
  value: unknown,
): value is QuickbooksPayrollDeductionError => {
  if (typeof value !== 'object' || value === null) {
    return false
  }
  const o = value as Record<string, unknown>
  return (
    o.__typename === 'Payroll_DeductionError' ||
    (o.deduction == null && o.id == null && (o.message != null || o.code != null || o.type != null))
  )
}

/**
 * GraphQL union `Payroll_CreateEmployeePensionPayload` /
 * `Payroll_UpdateEmployeePensionPayload`.
 */
export const quickbooksPayrollEmployeePensionPayloadUnionSchema = yup.lazy((value: unknown) => {
  if (isPayrollEmployeePensionPayloadError(value)) {
    return quickbooksPayrollDeductionErrorSchema.required()
  }
  return quickbooksPayrollEmployeePensionPayloadSuccessSchema.required()
})

export const quickbooksCreateEmployeePensionGraphqlResponseSchema = yup.object({
  data: yup
    .object({
      payrollCreateEmployeePension: quickbooksPayrollEmployeePensionPayloadUnionSchema,
    })
    .required(),
})

export type QuickbooksCreateEmployeePensionGraphqlResponse = yup.InferType<
  typeof quickbooksCreateEmployeePensionGraphqlResponseSchema
>

/**
 * `Payroll_UpdateEmployeePensionInput`. SDL does not mark fields required; descriptions always
 * send `employeeId` and `employeeDeductionDetails`.
 *
 * @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/inputobjects/Payroll_UpdateEmployeePensionInput
 */
export const quickbooksUpdateEmployeePensionActionStepInputSchema = yup.object({
  employeeId: yup.string().required(),
  employeeDeductionDetails: quickbooksUpdateEmployeeDeductionDetailsInputSchema
    .default(undefined)
    .required(),
})

export type QuickbooksUpdateEmployeePensionActionStepInput = yup.InferType<
  typeof quickbooksUpdateEmployeePensionActionStepInputSchema
>

export const quickbooksUpdateEmployeePensionGraphqlResponseSchema = yup.object({
  data: yup
    .object({
      payrollUpdateEmployeePension: quickbooksPayrollEmployeePensionPayloadUnionSchema,
    })
    .required(),
})

export type QuickbooksUpdateEmployeePensionGraphqlResponse = yup.InferType<
  typeof quickbooksUpdateEmployeePensionGraphqlResponseSchema
>

/**
 * `Payroll_DeleteEmployeePensionInput`. Descriptions always send `employeeId` and
 * `employeeDeductionId`.
 *
 * @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/inputobjects/Payroll_DeleteEmployeePensionInput
 */
export const quickbooksDeleteEmployeePensionActionStepInputSchema = yup.object({
  employeeId: yup.string().required(),
  employeeDeductionId: yup.string().required(),
})

export type QuickbooksDeleteEmployeePensionActionStepInput = yup.InferType<
  typeof quickbooksDeleteEmployeePensionActionStepInputSchema
>

/** `Payroll_DeleteEmployeePensionSuccess` (`id` only). */
export const quickbooksPayrollDeleteEmployeePensionPayloadSuccessSchema = yup.object({
  __typename: yup.string().oneOf(['Payroll_DeleteEmployeePensionSuccess']).optional(),
  id: yup.string().required(),
})

export const quickbooksPayrollDeleteEmployeePensionPayloadUnionSchema = yup.lazy(
  (value: unknown) => {
    if (isPayrollEmployeePensionPayloadError(value)) {
      return quickbooksPayrollDeductionErrorSchema.required()
    }
    return quickbooksPayrollDeleteEmployeePensionPayloadSuccessSchema.required()
  },
)

export const quickbooksDeleteEmployeePensionGraphqlResponseSchema = yup.object({
  data: yup
    .object({
      payrollDeleteEmployeePension: quickbooksPayrollDeleteEmployeePensionPayloadUnionSchema,
    })
    .required(),
})

export type QuickbooksDeleteEmployeePensionGraphqlResponse = yup.InferType<
  typeof quickbooksDeleteEmployeePensionGraphqlResponseSchema
>

// Payroll employee misc deduction GraphQL (`payrollCreateEmployeeMiscDeduction` /
// `payrollUpdateEmployeeMiscDeduction`) --------------------------------------------------------
// Field names follow the official qbexternal SDL.
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/mutations/payrollCreateEmployeeMiscDeduction
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/inputobjects/Payroll_CreateEmployeeMiscDeductionInput
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/objects/Payroll_EmployeeMiscDeduction
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/unions/Payroll_CreateEmployeeMiscDeductionPayload
// @see https://developer.intuit.com/app/developer/payroll-time/docs/workflows/payroll-deductions

/**
 * `Payroll_EmployeeMiscDeduction` returned on create/update success (`deduction`).
 * SDL fields: `id`, `active`, `alternateIds`, `deductionPolicy`, `employeeContribution`,
 * `employerContribution`, `policyPlan`. There is no `employeeExternalContribution`.
 *
 * @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/objects/Payroll_EmployeeMiscDeduction
 */
export const quickbooksPayrollEmployeeMiscDeductionSchema = yup.object({
  id: yup.string().required(),
  active: yup.boolean().nullable().optional(),
  alternateIds: yup.array().of(quickbooksAlternateIdSchema.required()).nullable().optional(),
  deductionPolicy: quickbooksPayrollDeductionPolicySchema.nullable().optional().default(undefined),
  employeeContribution: quickbooksPayrollContributionSchema
    .nullable()
    .optional()
    .default(undefined),
  employerContribution: quickbooksPayrollContributionSchema
    .nullable()
    .optional()
    .default(undefined),
  policyPlan: quickbooksPayrollVariableStringFieldSchema.nullable().optional().default(undefined),
})

export type QuickbooksPayrollEmployeeMiscDeduction = yup.InferType<
  typeof quickbooksPayrollEmployeeMiscDeductionSchema
>

/**
 * `Payroll_CreateEmployeeMiscDeductionInput`. SDL does not mark fields required; examples and
 * descriptions always send `employeeId` and `employeeDeductionDetails`. `policyPlan` is an
 * optional string on the mutation input (distinct from output `Payroll_VariableStringField`).
 *
 * @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/inputobjects/Payroll_CreateEmployeeMiscDeductionInput
 * @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/inputobjects/Payroll_CreateEmployeeDeductionDetailsInput
 */
export const quickbooksCreateEmployeeMiscDeductionActionStepInputSchema = yup.object({
  employeeId: yup.string().required(),
  employeeDeductionDetails: quickbooksCreateEmployeeDeductionDetailsInputSchema
    .default(undefined)
    .required(),
  policyPlan: yup.string().nullable().optional(),
})

export type QuickbooksCreateEmployeeMiscDeductionActionStepInput = yup.InferType<
  typeof quickbooksCreateEmployeeMiscDeductionActionStepInputSchema
>

const payrollEmployeeMiscDeductionPayloadSuccessTypenames = [
  'Payroll_CreateEmployeeMiscDeductionSuccess',
  'Payroll_UpdateEmployeeMiscDeductionSuccess',
] as const

export const quickbooksPayrollEmployeeMiscDeductionPayloadSuccessSchema = yup.object({
  __typename: yup
    .string()
    .oneOf([...payrollEmployeeMiscDeductionPayloadSuccessTypenames])
    .optional(),
  deduction: quickbooksPayrollEmployeeMiscDeductionSchema.default(undefined).required(),
})

export const isPayrollEmployeeMiscDeductionPayloadError = (
  value: unknown,
): value is QuickbooksPayrollDeductionError => {
  if (typeof value !== 'object' || value === null) {
    return false
  }
  const o = value as Record<string, unknown>
  return (
    o.__typename === 'Payroll_DeductionError' ||
    (o.deduction == null && o.id == null && (o.message != null || o.code != null || o.type != null))
  )
}

/**
 * GraphQL union `Payroll_CreateEmployeeMiscDeductionPayload` /
 * `Payroll_UpdateEmployeeMiscDeductionPayload`.
 */
export const quickbooksPayrollEmployeeMiscDeductionPayloadUnionSchema = yup.lazy(
  (value: unknown) => {
    if (isPayrollEmployeeMiscDeductionPayloadError(value)) {
      return quickbooksPayrollDeductionErrorSchema.required()
    }
    return quickbooksPayrollEmployeeMiscDeductionPayloadSuccessSchema.required()
  },
)

export const quickbooksCreateEmployeeMiscDeductionGraphqlResponseSchema = yup.object({
  data: yup
    .object({
      payrollCreateEmployeeMiscDeduction: quickbooksPayrollEmployeeMiscDeductionPayloadUnionSchema,
    })
    .required(),
})

export type QuickbooksCreateEmployeeMiscDeductionGraphqlResponse = yup.InferType<
  typeof quickbooksCreateEmployeeMiscDeductionGraphqlResponseSchema
>

/**
 * `Payroll_UpdateEmployeeMiscDeductionInput`. SDL does not mark fields required; descriptions
 * and workflow examples always send `employeeId` and `employeeDeductionDetails`. `policyPlan`
 * is an optional string on the mutation input.
 *
 * @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/inputobjects/Payroll_UpdateEmployeeMiscDeductionInput
 * @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/inputobjects/Payroll_UpdateEmployeeDeductionDetailsInput
 * @see https://developer.intuit.com/app/developer/payroll-time/docs/workflows/payroll-deductions
 */
export const quickbooksUpdateEmployeeMiscDeductionActionStepInputSchema = yup.object({
  employeeId: yup.string().required(),
  employeeDeductionDetails: quickbooksUpdateEmployeeDeductionDetailsInputSchema
    .default(undefined)
    .required(),
  policyPlan: yup.string().nullable().optional(),
})

export type QuickbooksUpdateEmployeeMiscDeductionActionStepInput = yup.InferType<
  typeof quickbooksUpdateEmployeeMiscDeductionActionStepInputSchema
>

export const quickbooksUpdateEmployeeMiscDeductionGraphqlResponseSchema = yup.object({
  data: yup
    .object({
      payrollUpdateEmployeeMiscDeduction: quickbooksPayrollEmployeeMiscDeductionPayloadUnionSchema,
    })
    .required(),
})

export type QuickbooksUpdateEmployeeMiscDeductionGraphqlResponse = yup.InferType<
  typeof quickbooksUpdateEmployeeMiscDeductionGraphqlResponseSchema
>

// WORKFORCE READ COMPENSATION (`payrollEmployeeCompensations`) ------------------------------------
//
// Query args: required `filter` (`Payroll_EmployeeCompensationsFilter`), optional `first`
// (non-negative Int, default 50), optional `after` (cursor).
// Returns `Payroll_EmployeeCompensationConnection` (`edges` / `nodes` / `pageInfo`).
//
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/queries/payrollEmployeeCompensations
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/inputobjects/Payroll_EmployeeCompensationsFilter
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/objects/Payroll_EmployeeCompensationConnection
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/objects/Payroll_EmployeeCompensationEdge
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/objects/Payroll_EmployeeCompensation
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/objects/Payroll_EmployeeCompensationDslInfo
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/objects/Payroll_EmployerCompensation
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/objects/Payroll_PayRate
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/objects/Common_PageInfo
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/objects/Common_MoneyAmount
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/enums/Payroll_PayUnit
// @see https://developer.intuit.com/app/developer/payroll-time/docs/develop/develop-payroll

/** Official `Payroll_PayUnit` values on `Payroll_PayRate.payUnit`. */
export const QUICKBOOKS_PAYROLL_PAY_UNITS = [
  'HOURLY',
  'MONTHLY',
  'PER_PAYSLIP',
  'PER_PIECE',
  'UNKNOWN',
  'WEEKLY',
  'YEARLY',
] as const
export type QuickbooksPayrollPayUnit = (typeof QUICKBOOKS_PAYROLL_PAY_UNITS)[number]

/**
 * `Payroll_EmployeeCompensationsFilter`. Every field is optional; omit `active` to return both
 * active and inactive compensations.
 *
 * @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/inputobjects/Payroll_EmployeeCompensationsFilter
 */
export const quickbooksPayrollEmployeeCompensationsFilterSchema = yup.object({
  active: yup.boolean().nullable().optional(),
  employeeId: yup.string().nullable().optional(),
  intuitAccountId: yup.string().nullable().optional(),
})

export type QuickbooksPayrollEmployeeCompensationsFilter = yup.InferType<
  typeof quickbooksPayrollEmployeeCompensationsFilterSchema
>

/**
 * Action-step `data` for GraphQL `payrollEmployeeCompensations`.
 * `filter` is required by the query; an empty object is a valid filter.
 */
export const quickbooksGetEmployeeCompensationsActionStepInputSchema = yup.object({
  filter: quickbooksPayrollEmployeeCompensationsFilterSchema.default({}),
  first: yup.number().integer().min(0).nullable().optional(),
  after: yup.string().nullable().optional(),
})

export type QuickbooksGetEmployeeCompensationsActionStepInput = yup.InferType<
  typeof quickbooksGetEmployeeCompensationsActionStepInputSchema
>

/** `Payroll_EmployeeCompensationDslInfo`. Null when the compensation is legacy (non-DSL). */
export const quickbooksPayrollEmployeeCompensationDslInfoSchema = yup.object({
  definitionId: yup.string().nullable().optional(),
  isDslCompensation: yup.boolean().nullable().optional(),
})

/** `Payroll_PayRate` (`amount: Common_MoneyAmount` + `payUnit`). */
export const quickbooksPayrollPayRateSchema = yup.object({
  amount: quickbooksIndirectTaxMoneySchema.nullable().optional().default(undefined),
  payUnit: yup
    .string()
    .oneOf([...QUICKBOOKS_PAYROLL_PAY_UNITS])
    .nullable()
    .optional(),
})

/** `Payroll_EmployerCompensation` nested on each employee compensation. */
export const quickbooksPayrollEmployerCompensationSchema = yup.object({
  id: yup.string().required(),
  name: yup.string().nullable().optional(),
  active: yup.boolean().nullable().optional(),
  alternateIds: yup.array().of(quickbooksAlternateIdSchema.required()).nullable().optional(),
  type: quickbooksPayrollVariableStringFieldSchema.nullable().optional().default(undefined),
})

/**
 * `Payroll_EmployeeCompensation` node selected by `payrollEmployeeCompensations`.
 *
 * @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/objects/Payroll_EmployeeCompensation
 */
export const quickbooksPayrollEmployeeCompensationSchema = yup.object({
  id: yup.string().required(),
  employeeId: yup.string().nullable().optional(),
  active: yup.boolean().nullable().optional(),
  alternateIds: yup.array().of(quickbooksAlternateIdSchema.required()).nullable().optional(),
  dsl: quickbooksPayrollEmployeeCompensationDslInfoSchema.nullable().optional().default(undefined),
  employerCompensation: quickbooksPayrollEmployerCompensationSchema
    .nullable()
    .optional()
    .default(undefined),
  rate: quickbooksPayrollPayRateSchema.nullable().optional().default(undefined),
})

export type QuickbooksPayrollEmployeeCompensation = yup.InferType<
  typeof quickbooksPayrollEmployeeCompensationSchema
>

/** `Payroll_EmployeeCompensationEdge`. */
export const quickbooksPayrollEmployeeCompensationEdgeSchema = yup.object({
  cursor: yup.string().required(),
  node: quickbooksPayrollEmployeeCompensationSchema.nullable().optional().default(undefined),
})

/**
 * `Payroll_EmployeeCompensationConnection`.
 *
 * @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/objects/Payroll_EmployeeCompensationConnection
 */
export const quickbooksPayrollEmployeeCompensationConnectionSchema = yup.object({
  edges: yup
    .array()
    .of(quickbooksPayrollEmployeeCompensationEdgeSchema.required())
    .nullable()
    .optional(),
  nodes: yup
    .array()
    .of(quickbooksPayrollEmployeeCompensationSchema.required())
    .nullable()
    .optional(),
  pageInfo: quickbooksGraphqlPageInfoSchema.nullable().optional().default(undefined),
})

export const quickbooksGetEmployeeCompensationsGraphqlResponseSchema = yup.object({
  data: yup
    .object({
      payrollEmployeeCompensations: quickbooksPayrollEmployeeCompensationConnectionSchema
        .nullable()
        .optional()
        .default(undefined),
    })
    .required(),
})

export type QuickbooksGetEmployeeCompensationsGraphqlResponse = yup.InferType<
  typeof quickbooksGetEmployeeCompensationsGraphqlResponseSchema
>

// Payroll employer info GraphQL (`payrollEmployerInfo`) -------------------------------------------
// Field names follow the official qbexternal SDL for `Payroll_EmployerInfo` and nested types.
// `earnedWageAccessProfile` is on the live SDL but not on the published object docs; omit it.
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/queries/payrollEmployerInfo
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/objects/Payroll_EmployerInfo
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/objects/Payroll_BusinessInfo
// @see https://developer.intuit.com/app/developer/payroll-time/docs/develop/develop-payroll
// @see https://developer.intuit.com/app/developer/payroll-time/docs/workflows/payroll-benefits

/** `Payroll_PayScheduleFrequency` JSON enum values. */
export const QUICKBOOKS_PAYROLL_PAY_SCHEDULE_FREQUENCY_VALUES = [
  'EVERY_WEEK',
  'EVERY_OTHER_WEEK',
  'TWICE_A_MONTH',
  'EVERY_MONTH',
  'UNSPECIFIED',
] as const

/** `Payroll_IdFilter` (`in: [ID!]`). */
export const quickbooksPayrollIdFilterSchema = yup.object({
  in: yup.array().of(yup.string().required()).nullable().optional(),
})

/** `Payroll_BooleanFilter` (`eq` / `ne`). */
export const quickbooksPayrollBooleanFilterSchema = yup.object({
  eq: yup.boolean().nullable().optional(),
  ne: yup.boolean().nullable().optional(),
})

/** `Payroll_IntFilter` (`gte` / `lte`). */
export const quickbooksPayrollIntFilterSchema = yup.object({
  gte: yup.number().integer().nullable().optional(),
  lte: yup.number().integer().nullable().optional(),
})

/**
 * `Payroll_DeductionPolicyFilter`. SDL marks `id` required when the filter object is sent.
 *
 * @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/inputobjects/Payroll_DeductionPolicyFilter
 */
export const quickbooksPayrollDeductionPolicyFilterSchema = yup.object({
  id: quickbooksPayrollIdFilterSchema.default(undefined).required(),
  active: quickbooksPayrollBooleanFilterSchema.nullable().optional().default(undefined),
})

const quickbooksGraphqlConnectionPaginationSchema = yup.object({
  first: yup.number().integer().min(0).nullable().optional(),
  after: yup.string().nullable().optional(),
  last: yup.number().integer().min(0).nullable().optional(),
  before: yup.string().nullable().optional(),
})

/** Pagination + `Payroll_DeductionPolicyFilter` for benefits / deductions / pensions. */
export const quickbooksPayrollDeductionPolicyConnectionArgsSchema =
  quickbooksGraphqlConnectionPaginationSchema.concat(
    yup.object({
      filter: quickbooksPayrollDeductionPolicyFilterSchema.nullable().optional().default(undefined),
    }),
  )

/**
 * `Payroll_EmployerCompensationsFilter` plus relay pagination.
 *
 * @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/inputobjects/Payroll_EmployerCompensationsFilter
 */
export const quickbooksPayrollEmployerCompensationsConnectionArgsSchema =
  quickbooksGraphqlConnectionPaginationSchema.concat(
    yup.object({
      filter: yup
        .object({
          id: quickbooksPayrollIdFilterSchema.nullable().optional().default(undefined),
          type: yup.array().of(yup.string().required()).nullable().optional(),
          employeeId: yup.string().nullable().optional(),
        })
        .nullable()
        .optional()
        .default(undefined),
    }),
  )

/**
 * `employerPaySchedules` args: `first` (default 50), `after`, `input`, `filter`.
 *
 * @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/inputobjects/Payroll_EmployerPaySchedulesQueryInput
 * @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/inputobjects/Payroll_PayScheduleFilter
 */
export const quickbooksPayrollEmployerPaySchedulesConnectionArgsSchema = yup.object({
  first: yup.number().integer().min(0).nullable().optional(),
  after: yup.string().nullable().optional(),
  input: yup
    .object({
      maxNumberOfPreviousPeriods: yup.number().integer().min(0).nullable().optional(),
      maxNumberOfUpcomingPeriods: yup.number().integer().min(0).nullable().optional(),
    })
    .nullable()
    .optional()
    .default(undefined),
  filter: yup
    .object({
      id: quickbooksPayrollIdFilterSchema.nullable().optional().default(undefined),
      activeEmployeesCount: quickbooksPayrollIntFilterSchema
        .nullable()
        .optional()
        .default(undefined),
    })
    .nullable()
    .optional()
    .default(undefined),
})

/** `workLocations(first: Int = 10, after: String)`. */
export const quickbooksPayrollWorkLocationsConnectionArgsSchema = yup.object({
  first: yup.number().integer().min(0).nullable().optional(),
  after: yup.string().nullable().optional(),
})

/**
 * Action-step `data` for GraphQL `payrollEmployerInfo`.
 * `intuitAccountId` is optional; when omitted Intuit reads the company from the auth context.
 */
export const quickbooksGetEmployerInfoActionStepInputSchema = yup.object({
  intuitAccountId: yup.string().nullable().optional(),
  benefits: quickbooksPayrollDeductionPolicyConnectionArgsSchema
    .nullable()
    .optional()
    .default(undefined),
  deductions: quickbooksPayrollDeductionPolicyConnectionArgsSchema
    .nullable()
    .optional()
    .default(undefined),
  pensions: quickbooksPayrollDeductionPolicyConnectionArgsSchema
    .nullable()
    .optional()
    .default(undefined),
  employerCompensations: quickbooksPayrollEmployerCompensationsConnectionArgsSchema
    .nullable()
    .optional()
    .default(undefined),
  employerPaySchedules: quickbooksPayrollEmployerPaySchedulesConnectionArgsSchema
    .nullable()
    .optional()
    .default(undefined),
  workLocations: quickbooksPayrollWorkLocationsConnectionArgsSchema
    .nullable()
    .optional()
    .default(undefined),
})

export type QuickbooksGetEmployerInfoActionStepInput = yup.InferType<
  typeof quickbooksGetEmployerInfoActionStepInputSchema
>

/** `Payroll_BusinessName`. */
export const quickbooksPayrollBusinessNameSchema = yup.object({
  legalName: yup.string().nullable().optional(),
})

/** `Identity_Validation`. */
export const quickbooksIdentityValidationSchema = yup.object({
  time: yup.string().nullable().optional(),
  version: yup.string().nullable().optional(),
})

/** `Identity_DataQuality`. */
export const quickbooksIdentityDataQualitySchema = yup.object({
  validation: quickbooksIdentityValidationSchema.nullable().optional().default(undefined),
  verification: quickbooksCommonVerificationSchema.nullable().optional().default(undefined),
})

/** `Identity_GovernmentIdentity`. */
export const quickbooksIdentityGovernmentIdentitySchema = yup.object({
  countryOfIssuingAuthority: yup.string().nullable().optional(),
  dataQuality: quickbooksIdentityDataQualitySchema.nullable().optional().default(undefined),
  expirationDate: yup.string().nullable().optional(),
  governmentId: yup.string().nullable().optional(),
  id: yup.string().nullable().optional(),
  idType: yup.string().nullable().optional(),
  issuingAuthority: yup.string().nullable().optional(),
  issuingDate: yup.string().nullable().optional(),
  regionOfIssuingAuthority: yup.string().nullable().optional(),
})

/** `Identity_IndustryClassification`. */
export const quickbooksIdentityIndustryClassificationSchema = yup.object({
  naicsId: yup.string().nullable().optional(),
  description: yup.string().nullable().optional(),
})

/** `Payroll_ContactInfo`. */
export const quickbooksPayrollContactInfoSchema = yup.object({
  addresses: yup
    .array()
    .of(quickbooksProjectManagementPostalAddressSchema.required())
    .nullable()
    .optional(),
})

/** `Payroll_BusinessInfo`. */
export const quickbooksPayrollBusinessInfoSchema = yup.object({
  name: quickbooksPayrollBusinessNameSchema.nullable().optional().default(undefined),
  displayName: yup.string().nullable().optional(),
  contactInfo: quickbooksPayrollContactInfoSchema.nullable().optional().default(undefined),
  governmentIds: yup
    .array()
    .of(quickbooksIdentityGovernmentIdentitySchema.required())
    .nullable()
    .optional(),
  industryCode: quickbooksIdentityIndustryClassificationSchema
    .nullable()
    .optional()
    .default(undefined),
})

/** `Payroll_PayPeriod`. */
export const quickbooksPayrollPayPeriodSchema = yup.object({
  beginDate: yup.string().nullable().optional(),
  endDate: yup.string().nullable().optional(),
})

/** `Payroll_PaySchedulePeriod`. */
export const quickbooksPayrollPaySchedulePeriodSchema = yup.object({
  payDate: yup.string().nullable().optional(),
  period: quickbooksPayrollPayPeriodSchema.nullable().optional().default(undefined),
})

/**
 * `Payroll_PayScheduleReferenceDates` union
 * (`Payroll_PayScheduleAbsoluteReferenceDates` | `Payroll_PayScheduleRelativeReferenceDates`).
 */
export const quickbooksPayrollPayScheduleReferenceDatesSchema = yup.object({
  __typename: yup.string().nullable().optional(),
  initialPayDate: yup.string().nullable().optional(),
  payDay: yup.string().nullable().optional(),
})

/** `Payroll_EmployerPaySchedule`. */
export const quickbooksPayrollEmployerPayScheduleSchema = yup.object({
  id: yup.string().required(),
  name: yup.string().nullable().optional(),
  frequency: yup.string().nullable().optional(),
  referenceDates: yup
    .array()
    .of(quickbooksPayrollPayScheduleReferenceDatesSchema.required())
    .nullable()
    .optional(),
  upcomingPayPeriods: yup
    .array()
    .of(quickbooksPayrollPaySchedulePeriodSchema.required())
    .nullable()
    .optional(),
  previousPayPeriods: yup
    .array()
    .of(quickbooksPayrollPaySchedulePeriodSchema.required())
    .nullable()
    .optional(),
  activeEmployeesCount: yup.number().integer().nullable().optional(),
})

/**
 * `Payroll_WorkLocation` as selected on `payrollEmployerInfo.workLocations`.
 * Distinct from `quickbooksPayrollWorkLocationSchema` (employee-update subset).
 */
export const quickbooksPayrollEmployerWorkLocationSchema = yup.object({
  id: yup.string().required(),
  addressComponents: quickbooksProjectManagementPostalAddressSchema
    .nullable()
    .optional()
    .default(undefined),
  alternateIds: yup.array().of(quickbooksAlternateIdSchema.required()).nullable().optional(),
  active: yup.boolean().nullable().optional(),
  primary: yup.boolean().nullable().optional(),
  unitNumber: yup.string().nullable().optional(),
  politicalSubdivisionCode: quickbooksPayrollVariableStringFieldSchema
    .nullable()
    .optional()
    .default(undefined),
})

const quickbooksPayrollGraphqlPageInfoField = quickbooksGraphqlPageInfoSchema
  .nullable()
  .optional()
  .default(undefined)

/** `Payroll_BenefitPolicyConnection`. */
export const quickbooksPayrollBenefitPolicyConnectionSchema = yup.object({
  edges: yup
    .array()
    .of(
      yup
        .object({
          node: quickbooksPayrollBenefitPolicySchema.nullable().optional().default(undefined),
          cursor: yup.string().nullable().optional(),
        })
        .required(),
    )
    .nullable()
    .optional(),
  pageInfo: quickbooksPayrollGraphqlPageInfoField,
})

/** `Payroll_MiscDeductionPolicyConnection`. */
export const quickbooksPayrollMiscDeductionPolicyConnectionSchema = yup.object({
  edges: yup
    .array()
    .of(
      yup
        .object({
          node: quickbooksPayrollMiscDeductionPolicySchema.nullable().optional().default(undefined),
          cursor: yup.string().nullable().optional(),
        })
        .required(),
    )
    .nullable()
    .optional(),
  pageInfo: quickbooksPayrollGraphqlPageInfoField,
})

/** `Payroll_PensionPolicyConnection`. */
export const quickbooksPayrollPensionPolicyConnectionSchema = yup.object({
  edges: yup
    .array()
    .of(
      yup
        .object({
          node: quickbooksPayrollPensionPolicySchema.nullable().optional().default(undefined),
          cursor: yup.string().nullable().optional(),
        })
        .required(),
    )
    .nullable()
    .optional(),
  pageInfo: quickbooksPayrollGraphqlPageInfoField,
})

/** `Payroll_EmployerCompensationsConnection`. */
export const quickbooksPayrollEmployerCompensationsConnectionSchema = yup.object({
  edges: yup
    .array()
    .of(
      yup
        .object({
          node: quickbooksPayrollEmployerCompensationSchema
            .nullable()
            .optional()
            .default(undefined),
          cursor: yup.string().nullable().optional(),
        })
        .required(),
    )
    .nullable()
    .optional(),
  nodes: yup
    .array()
    .of(quickbooksPayrollEmployerCompensationSchema.required())
    .nullable()
    .optional(),
  pageInfo: quickbooksPayrollGraphqlPageInfoField,
})

/** `Payroll_EmployerPayScheduleConnection`. */
export const quickbooksPayrollEmployerPayScheduleConnectionSchema = yup.object({
  edges: yup
    .array()
    .of(
      yup
        .object({
          node: quickbooksPayrollEmployerPayScheduleSchema.nullable().optional().default(undefined),
          cursor: yup.string().nullable().optional(),
        })
        .required(),
    )
    .nullable()
    .optional(),
  nodes: yup
    .array()
    .of(quickbooksPayrollEmployerPayScheduleSchema.required())
    .nullable()
    .optional(),
  pageInfo: quickbooksPayrollGraphqlPageInfoField,
})

/** `Payroll_WorkLocationConnection`. */
export const quickbooksPayrollWorkLocationConnectionSchema = yup.object({
  edges: yup
    .array()
    .of(
      yup
        .object({
          node: quickbooksPayrollEmployerWorkLocationSchema
            .nullable()
            .optional()
            .default(undefined),
          cursor: yup.string().nullable().optional(),
        })
        .required(),
    )
    .nullable()
    .optional(),
  nodes: yup
    .array()
    .of(quickbooksPayrollEmployerWorkLocationSchema.required())
    .nullable()
    .optional(),
  pageInfo: quickbooksPayrollGraphqlPageInfoField,
})

/** `Payroll_EmployerInfo` (published object fields). */
export const quickbooksPayrollEmployerInfoSchema = yup.object({
  intuitAccountId: yup.string().required(),
  businessInfo: quickbooksPayrollBusinessInfoSchema.nullable().optional().default(undefined),
  benefits: quickbooksPayrollBenefitPolicyConnectionSchema.nullable().optional().default(undefined),
  deductions: quickbooksPayrollMiscDeductionPolicyConnectionSchema
    .nullable()
    .optional()
    .default(undefined),
  pensions: quickbooksPayrollPensionPolicyConnectionSchema.nullable().optional().default(undefined),
  employerCompensations: quickbooksPayrollEmployerCompensationsConnectionSchema
    .nullable()
    .optional()
    .default(undefined),
  employerPaySchedules: quickbooksPayrollEmployerPayScheduleConnectionSchema
    .nullable()
    .optional()
    .default(undefined),
  workLocations: quickbooksPayrollWorkLocationConnectionSchema
    .nullable()
    .optional()
    .default(undefined),
})

export type QuickbooksPayrollEmployerInfo = yup.InferType<
  typeof quickbooksPayrollEmployerInfoSchema
>

export const quickbooksGetEmployerInfoGraphqlResponseSchema = yup.object({
  data: yup
    .object({
      payrollEmployerInfo: quickbooksPayrollEmployerInfoSchema
        .nullable()
        .optional()
        .default(undefined),
    })
    .required(),
})

export type QuickbooksGetEmployerInfoGraphqlResponse = yup.InferType<
  typeof quickbooksGetEmployerInfoGraphqlResponseSchema
>

// WORKFORCE READ PAYSLIPS (`payrollPayslips`) -----------------------------------------------------
//
// Query args: optional `filter` (`Payroll_PayslipsFilter`; omit → active employees only),
// optional `orderBy` (`[Payroll_EmployerPayslipsOrderBy]`; omit → pay date descending),
// optional cursor pagination `first` / `after` / `last` / `before` (non-negative Int + String).
// Returns `Payroll_EmployerPayslipConnection` (`edges` / `pageInfo` / `totalCount`).
//
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/queries/payrollPayslips
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/inputobjects/Payroll_PayslipsFilter
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/objects/Payroll_EmployerPayslipConnection
// @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/objects/Payroll_Payslip
// @see https://developer.intuit.com/app/developer/payroll-time/docs/workflows/payroll-payslips

/** Official `Payroll_PayslipType` values used by `Payroll_PayslipTypeFilter`. */
export const QUICKBOOKS_PAYROLL_PAYSLIP_TYPES = [
  'ADJUSTMENT',
  'BONUS',
  'COMMISSION',
  'FRINGE',
  'REGULAR',
] as const
export type QuickbooksPayrollPayslipType = (typeof QUICKBOOKS_PAYROLL_PAYSLIP_TYPES)[number]

/** Official `Payroll_PayslipPayDateFilterPreset` values. */
export const QUICKBOOKS_PAYROLL_PAYSLIP_PAY_DATE_FILTER_PRESETS = [
  'LATEST_EMPLOYEE_PAY_DATE',
  'LATEST_PAY_DATE',
] as const
export type QuickbooksPayrollPayslipPayDateFilterPreset =
  (typeof QUICKBOOKS_PAYROLL_PAYSLIP_PAY_DATE_FILTER_PRESETS)[number]

/**
 * Official `Payroll_EmployerPayslipsOrderBy` values.
 * Default when omitted: pay date descending.
 */
export const QUICKBOOKS_PAYROLL_EMPLOYER_PAYSLIPS_ORDER_BY = [
  'EMPLOYEE_FIRSTNAME_ASC',
  'EMPLOYEE_FIRSTNAME_DESC',
  'EMPLOYEE_LASTNAME_ASC',
  'EMPLOYEE_LASTNAME_DESC',
  'GROSSPAY_CURRENTAMOUNT_ASC',
  'GROSSPAY_CURRENTAMOUNT_DESC',
  'NETPAY_CURRENTAMOUNT_ASC',
  'NETPAY_CURRENTAMOUNT_DESC',
  'NETPAY_DISTRIBUTIONS_CHECKNUMBER_ASC',
  'NETPAY_DISTRIBUTIONS_CHECKNUMBER_DESC',
  'PAYDATE_ASC',
  'PAYDATE_DESC',
] as const
export type QuickbooksPayrollEmployerPayslipsOrderBy =
  (typeof QUICKBOOKS_PAYROLL_EMPLOYER_PAYSLIPS_ORDER_BY)[number]

/** Official `Payroll_ToDateAmountType` values on `Payroll_ToDateAmount`. */
export const QUICKBOOKS_PAYROLL_TO_DATE_AMOUNT_TYPES = ['MONTH', 'QUARTER', 'YEAR'] as const
export type QuickbooksPayrollToDateAmountType =
  (typeof QUICKBOOKS_PAYROLL_TO_DATE_AMOUNT_TYPES)[number]

/** `Payroll_StringFilter` (`eq` / `in`). */
export const quickbooksPayrollStringFilterSchema = yup.object({
  eq: yup.string().nullable().optional(),
  in: yup.array().of(yup.string().required()).nullable().optional(),
})

/** `Payroll_DateFilter` (`gte` / `lte`). */
export const quickbooksPayrollDateFilterSchema = yup.object({
  gte: yup.string().nullable().optional(),
  lte: yup.string().nullable().optional(),
})

/**
 * `Payroll_PayslipEmployeeFilter`.
 *
 * @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/inputobjects/Payroll_PayslipEmployeeFilter
 */
export const quickbooksPayrollPayslipEmployeeFilterSchema = yup.object({
  employmentStatus: quickbooksPayrollStringFilterSchema.nullable().optional().default(undefined),
  id: quickbooksPayrollIdFilterSchema.nullable().optional().default(undefined),
})

/**
 * `Payroll_PayslipPayDateFilter`. Docs require one and only one of `dateRange` / `preset`.
 *
 * @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/inputobjects/Payroll_PayslipPayDateFilter
 */
export const quickbooksPayrollPayslipPayDateFilterSchema = yup.object({
  dateRange: quickbooksPayrollDateFilterSchema.nullable().optional().default(undefined),
  preset: yup
    .string()
    .oneOf([...QUICKBOOKS_PAYROLL_PAYSLIP_PAY_DATE_FILTER_PRESETS])
    .nullable()
    .optional(),
})

/**
 * `Payroll_PayslipTypeFilter`.
 *
 * @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/inputobjects/Payroll_PayslipTypeFilter
 */
export const quickbooksPayrollPayslipTypeFilterSchema = yup.object({
  eq: yup
    .string()
    .oneOf([...QUICKBOOKS_PAYROLL_PAYSLIP_TYPES])
    .nullable()
    .optional(),
  in: yup
    .array()
    .of(
      yup
        .string()
        .oneOf([...QUICKBOOKS_PAYROLL_PAYSLIP_TYPES])
        .required(),
    )
    .nullable()
    .optional(),
  ne: yup
    .string()
    .oneOf([...QUICKBOOKS_PAYROLL_PAYSLIP_TYPES])
    .nullable()
    .optional(),
  nin: yup
    .array()
    .of(
      yup
        .string()
        .oneOf([...QUICKBOOKS_PAYROLL_PAYSLIP_TYPES])
        .required(),
    )
    .nullable()
    .optional(),
})

/**
 * `Payroll_PayslipsFilter`. Every field is optional; omit the filter to return payslips for
 * active employees only.
 *
 * @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/inputobjects/Payroll_PayslipsFilter
 */
export const quickbooksPayrollPayslipsFilterSchema = yup.object({
  employee: quickbooksPayrollPayslipEmployeeFilterSchema.nullable().optional().default(undefined),
  exportedToAccounting: quickbooksPayrollBooleanFilterSchema
    .nullable()
    .optional()
    .default(undefined),
  ids: yup.array().of(yup.string().required()).nullable().optional(),
  intuitAccountId: yup.string().nullable().optional(),
  modifiedAfter: yup.string().nullable().optional(),
  payDate: quickbooksPayrollPayslipPayDateFilterSchema.nullable().optional().default(undefined),
  sourceIds: yup.array().of(yup.string().required()).nullable().optional(),
  type: quickbooksPayrollPayslipTypeFilterSchema.nullable().optional().default(undefined),
})

export type QuickbooksPayrollPayslipsFilter = yup.InferType<
  typeof quickbooksPayrollPayslipsFilterSchema
>

/**
 * Action-step `data` for GraphQL `payrollPayslips`.
 * All args are optional; an empty object is a valid request.
 */
export const quickbooksGetPayrollPayslipsActionStepInputSchema = yup.object({
  filter: quickbooksPayrollPayslipsFilterSchema.nullable().optional().default(undefined),
  orderBy: yup
    .array()
    .of(
      yup
        .string()
        .oneOf([...QUICKBOOKS_PAYROLL_EMPLOYER_PAYSLIPS_ORDER_BY])
        .required(),
    )
    .nullable()
    .optional(),
  first: yup.number().integer().min(0).nullable().optional(),
  after: yup.string().nullable().optional(),
  last: yup.number().integer().min(0).nullable().optional(),
  before: yup.string().nullable().optional(),
})

export type QuickbooksGetPayrollPayslipsActionStepInput = yup.InferType<
  typeof quickbooksGetPayrollPayslipsActionStepInputSchema
>

/** `Qb_Metadata` timestamps / actors selected on `Payroll_Payslip.metadata`. */
export const quickbooksPayrollPayslipMetadataSchema = yup.object({
  createdAt: yup.string().nullable().optional(),
  createdBy: yup.string().nullable().optional(),
  createdByApp: yup.string().nullable().optional(),
  createdByUser: yup.string().nullable().optional(),
  updatedAt: yup.string().nullable().optional(),
  updatedByApp: yup.string().nullable().optional(),
  updatedByUser: yup.string().nullable().optional(),
})

/**
 * `Payroll_ToDateAmount` as returned by the official payslips workflow sample
 * (`amount` + `toDateAmountType`).
 *
 * @see https://developer.intuit.com/app/developer/payroll-time/docs/workflows/payroll-payslips
 */
export const quickbooksPayrollToDateAmountSchema = yup.object({
  amount: yup.number().nullable().optional(),
  toDateAmountType: yup
    .string()
    .oneOf([...QUICKBOOKS_PAYROLL_TO_DATE_AMOUNT_TYPES])
    .nullable()
    .optional(),
})

/** `Payroll_PayslipAccumulationAmount` (`currentAmount` + `yearToDateAmount` Decimals). */
export const quickbooksPayrollPayslipAccumulationAmountSchema = yup.object({
  currentAmount: yup.number().nullable().optional(),
  yearToDateAmount: yup.number().nullable().optional(),
})

/** Shared accumulation shape: `currentAmount: Common_MoneyAmount` + `toDateAmounts`. */
export const quickbooksPayrollMoneyAccumulationAmountSchema = yup.object({
  currentAmount: quickbooksIndirectTaxMoneySchema.nullable().optional().default(undefined),
  toDateAmounts: yup
    .array()
    .of(quickbooksPayrollToDateAmountSchema.required())
    .nullable()
    .optional(),
})

/** `Payroll_TaxAccumulationAmount` adds `currentTaxableIncome`. */
export const quickbooksPayrollTaxAccumulationAmountSchema = yup.object({
  currentAmount: quickbooksIndirectTaxMoneySchema.nullable().optional().default(undefined),
  currentTaxableIncome: quickbooksIndirectTaxMoneySchema.nullable().optional().default(undefined),
  toDateAmounts: yup
    .array()
    .of(quickbooksPayrollToDateAmountSchema.required())
    .nullable()
    .optional(),
})

/** Nested `Payroll_PayRate` on payslip compensation detail (`amount` + `payUnit`). */
export const quickbooksPayrollPayslipPayRateSchema = yup.object({
  amount: quickbooksIndirectTaxMoneySchema.nullable().optional().default(undefined),
  payUnit: yup
    .string()
    .oneOf([...QUICKBOOKS_PAYROLL_PAY_UNITS])
    .nullable()
    .optional(),
})

/** Compact `Payroll_EmployeeCompensation` nested on a calculated payslip compensation. */
export const quickbooksPayrollPayslipEmployeeCompensationSchema = yup.object({
  id: yup.string().nullable().optional(),
  employeeId: yup.string().nullable().optional(),
  active: yup.boolean().nullable().optional(),
  rate: quickbooksPayrollPayslipPayRateSchema.nullable().optional().default(undefined),
})

/** `Payroll_CalculatedCompensationDetail`. */
export const quickbooksPayrollCalculatedCompensationDetailSchema = yup.object({
  hours: yup.number().nullable().optional(),
  rate: quickbooksPayrollPayslipPayRateSchema.nullable().optional().default(undefined),
  source: yup.string().nullable().optional(),
})

/**
 * `Payroll_EmployeeCompensationSplit` without recursive `splitDetails`.
 *
 * @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/objects/Payroll_EmployeeCompensationSplit
 */
export const quickbooksPayrollEmployeeCompensationSplitSchema = yup.object({
  id: yup.string().nullable().optional(),
  hours: yup.number().nullable().optional(),
  splitPercentage: yup.number().nullable().optional(),
  amount: quickbooksIndirectTaxMoneySchema.nullable().optional().default(undefined),
})

/**
 * `Payroll_PayslipCalculatedCompensation`.
 *
 * @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/objects/Payroll_PayslipCalculatedCompensation
 */
export const quickbooksPayrollPayslipCalculatedCompensationSchema = yup.object({
  calculatedCompensationAccumulationAmount: quickbooksPayrollMoneyAccumulationAmountSchema
    .nullable()
    .optional()
    .default(undefined),
  calculatedCompensationDetail: quickbooksPayrollCalculatedCompensationDetailSchema
    .nullable()
    .optional()
    .default(undefined),
  compensationSplits: yup
    .array()
    .of(quickbooksPayrollEmployeeCompensationSplitSchema.required())
    .nullable()
    .optional(),
  employeeCompensation: quickbooksPayrollPayslipEmployeeCompensationSchema
    .nullable()
    .optional()
    .default(undefined),
})

/**
 * `Payroll_EmployeeDeduction`.
 *
 * @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/objects/Payroll_EmployeeDeduction
 */
export const quickbooksPayrollEmployeeDeductionSchema = yup.object({
  active: yup.boolean().nullable().optional(),
  alternateIds: yup.array().of(quickbooksAlternateIdSchema.required()).nullable().optional(),
  deductionPolicy: quickbooksPayrollDeductionPolicySchema.nullable().optional().default(undefined),
})

/**
 * `Payroll_PayslipCalculatedDeduction`.
 *
 * @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/objects/Payroll_PayslipCalculatedDeduction
 */
export const quickbooksPayrollPayslipCalculatedDeductionSchema = yup.object({
  employeeContributionAccumulationAmount: quickbooksPayrollMoneyAccumulationAmountSchema
    .nullable()
    .optional()
    .default(undefined),
  employeeDeduction: quickbooksPayrollEmployeeDeductionSchema
    .nullable()
    .optional()
    .default(undefined),
  employerContributionAccumulationAmount: quickbooksPayrollMoneyAccumulationAmountSchema
    .nullable()
    .optional()
    .default(undefined),
})

/**
 * `Payroll_PayslipCalculatedTax`.
 *
 * @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/objects/Payroll_PayslipCalculatedTax
 */
export const quickbooksPayrollPayslipCalculatedTaxSchema = yup.object({
  accumulationAmount: quickbooksPayrollTaxAccumulationAmountSchema
    .nullable()
    .optional()
    .default(undefined),
  statutoryType: quickbooksPayrollVariableStringFieldSchema
    .nullable()
    .optional()
    .default(undefined),
})

/**
 * `Payroll_PayslipNetPayDistribution`.
 *
 * @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/objects/Payroll_PayslipNetPayDistribution
 */
export const quickbooksPayrollPayslipNetPayDistributionSchema = yup.object({
  amount: quickbooksIndirectTaxMoneySchema.nullable().optional().default(undefined),
  method: yup.string().nullable().optional(),
})

/** `Payroll_PayslipPeriod` (`beginDate` + `endDate`). */
export const quickbooksPayrollPayslipPeriodSchema = yup.object({
  beginDate: yup.string().nullable().optional(),
  endDate: yup.string().nullable().optional(),
})

/**
 * Compact `WorkerManagement_Employee` identity fields nested on a payslip.
 * Full employee records belong on `workerManagementEmployees`.
 *
 * @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/objects/WorkerManagement_Employee
 */
export const quickbooksPayrollPayslipEmployeeSchema = yup.object({
  id: yup.string().nullable().optional(),
  firstName: yup.string().nullable().optional(),
  middleName: yup.string().nullable().optional(),
  lastName: yup.string().nullable().optional(),
  gender: yup.string().nullable().optional(),
  alternateIds: yup.array().of(quickbooksAlternateIdSchema.required()).nullable().optional(),
  employmentStatus: yup
    .object({
      active: yup.boolean().nullable().optional(),
      detailedStatus: yup.string().nullable().optional(),
    })
    .nullable()
    .optional()
    .default(undefined),
})

/**
 * `Payroll_Payslip` node selected by `payrollPayslips`.
 *
 * @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/objects/Payroll_Payslip
 */
export const quickbooksPayrollPayslipSchema = yup.object({
  id: yup.string().required(),
  sourceId: yup.string().nullable().optional(),
  type: yup.string().nullable().optional(),
  payDate: yup.string().nullable().optional(),
  approvedDateTime: yup.string().nullable().optional(),
  alternateIds: yup.array().of(quickbooksAlternateIdSchema.required()).nullable().optional(),
  metadata: quickbooksPayrollPayslipMetadataSchema.nullable().optional().default(undefined),
  grossPay: quickbooksPayrollPayslipAccumulationAmountSchema
    .nullable()
    .optional()
    .default(undefined),
  netPay: quickbooksIndirectTaxMoneySchema.nullable().optional().default(undefined),
  payPeriod: quickbooksPayrollPayslipPeriodSchema.nullable().optional().default(undefined),
  employee: quickbooksPayrollPayslipEmployeeSchema.nullable().optional().default(undefined),
  compensations: yup
    .array()
    .of(quickbooksPayrollPayslipCalculatedCompensationSchema.required())
    .nullable()
    .optional(),
  deductions: yup
    .array()
    .of(quickbooksPayrollPayslipCalculatedDeductionSchema.required())
    .nullable()
    .optional(),
  employeeTaxes: yup
    .array()
    .of(quickbooksPayrollPayslipCalculatedTaxSchema.required())
    .nullable()
    .optional(),
  employerTaxes: yup
    .array()
    .of(quickbooksPayrollPayslipCalculatedTaxSchema.required())
    .nullable()
    .optional(),
  netPayDistributions: yup
    .array()
    .of(quickbooksPayrollPayslipNetPayDistributionSchema.required())
    .nullable()
    .optional(),
})

export type QuickbooksPayrollPayslip = yup.InferType<typeof quickbooksPayrollPayslipSchema>

/** `Payroll_EmployerPayslipEdge`. */
export const quickbooksPayrollEmployerPayslipEdgeSchema = yup.object({
  cursor: yup.string().required(),
  node: quickbooksPayrollPayslipSchema.nullable().optional().default(undefined),
})

/**
 * `Payroll_EmployerPayslipConnection`.
 *
 * @see https://developer.intuit.com/app/developer/gql/docs/api/qbexternal/objects/Payroll_EmployerPayslipConnection
 */
export const quickbooksPayrollEmployerPayslipConnectionSchema = yup.object({
  edges: yup
    .array()
    .of(quickbooksPayrollEmployerPayslipEdgeSchema.required())
    .nullable()
    .optional(),
  pageInfo: quickbooksGraphqlPageInfoSchema.nullable().optional().default(undefined),
  totalCount: yup.number().nullable().optional(),
})

export const quickbooksGetPayrollPayslipsGraphqlResponseSchema = yup.object({
  data: yup
    .object({
      payrollPayslips: quickbooksPayrollEmployerPayslipConnectionSchema
        .nullable()
        .optional()
        .default(undefined),
    })
    .required(),
})

export type QuickbooksGetPayrollPayslipsGraphqlResponse = yup.InferType<
  typeof quickbooksGetPayrollPayslipsGraphqlResponseSchema
>
